"use client";

import { ArrowDown, ArrowUp, Check, ChevronLeft, CircleHelp, Copy, Download, EyeOff, Image as ImageIcon, LoaderCircle, Pencil, Plus, Repeat2, RotateCcw, Scissors, Search, SkipBack, SkipForward, Trash2, X, Zap } from "lucide-react";
import { type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isActiveBrowseEncodingJobStatus } from "@/lib/encodingResplitPolicy";
import { originalSourceVideoUrl } from "@/lib/mediaUrls";
import { SELECTED_SHOW_COOKIE_MAX_AGE_SECONDS, SELECTED_SHOW_STORAGE_KEY, parseStoredShowId } from "@/lib/shotPreferences";
import { resolveHorizontalShotSelection, type HorizontalShotSelectionDirection } from "@/lib/shotKeyboardSelection";
import { BROWSE_SHOT_HISTORY_LIMIT, BROWSE_SHOT_HISTORY_STORAGE_KEY, LAST_BROWSE_SHOT_CHANNEL, LAST_BROWSE_SHOT_STORAGE_KEY, normalizeBrowseShotHistory, type LastBrowseShot } from "@/lib/shotSelectionBridge";
import { SerializedAsyncQueue } from "@/lib/serializedAsyncQueue";
import { subtitlePlainText } from "@/lib/subtitleText";
import { countShotActionableHelpItems, countShotHelpIssues, helpRequestMatchesFilter, normalizeUserAuthoredSplitIssueMask, shotMatchesActionableHelpFilter, shotSplitHelpIssueMask, shotSplitHelpState, shotSplitHelpStateLabel, summarizeShotHelp, SPLIT_ISSUE_MASK_ALL, SPLIT_ISSUE_MERGE_LEFT, SPLIT_ISSUE_MERGE_MASK, SPLIT_ISSUE_MERGE_RIGHT, SPLIT_ISSUE_UNDER, type ShotHelpFilterMode, type SplitHelpReviewOverride } from "@/lib/shotHelpSummary";
import { type BrowseCharacter, type BrowseData, type BrowseEpisode, type BrowseMotionProgress, type BrowseMotionState, type BrowseShot, type BrowseShotCompactRow, type BrowseShotEditJob, type BrowseShotHelpCompactRow, type BrowseShotHelpRequest, type BrowseShotHelpRequestStatus, type BrowseShotHelpState, type BrowseShow, type BrowseShowMotionStatus, type BrowseVideo } from "@/lib/story37Data";

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function formatTime(ms: number) {
  const total = ms / 1000;
  const minutes = Math.floor(total / 60);
  const seconds = total - minutes * 60;
  return `${minutes}:${seconds.toFixed(1).padStart(4, "0")}`;
}

function episodeLabel(seasonNo: number | null, episodeNo: number | null, showSeasonLabel = true) {
  if (showSeasonLabel && seasonNo !== null && episodeNo !== null) {
    return `S${seasonNo} E${episodeNo}`;
  }
  if (episodeNo !== null) {
    return `E${episodeNo}`;
  }
  if (showSeasonLabel && seasonNo !== null) {
    return `S${seasonNo}`;
  }
  return "Video";
}

function browseEpisodeLabel(episode: BrowseEpisode, showSeasonLabel = true) {
  return episodeLabel(episode.seasonNo, episode.episodeNo, showSeasonLabel);
}

function fileNameFromPath(path: string) {
  return path.split(/[\\/]/).pop() || path;
}

function dirNameFromPath(path: string) {
  const normalized = path.replace(/[\\/]+$/, "");
  const index = Math.max(normalized.lastIndexOf("/"), normalized.lastIndexOf("\\"));
  return index > 0 ? normalized.slice(0, index) : normalized;
}

async function copyTextToClipboard(text: string) {
  try {
    if (!navigator.clipboard?.writeText) {
      throw new Error("async clipboard unavailable");
    }
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
  }
}

const BROWSE_LAYOUT_STORAGE_KEY = "story37.browseLayout";
const EPISODE_STORAGE_KEY = "story37.selectedEpisode";
const SHOW_DELETED_STORAGE_KEY = "story37.browse.showDeleted";
const PREVIEW_PLAYBACK_RATE_STORAGE_KEY = "story37.shots.previewPlaybackRate";
const PREVIEW_SOURCE_STORAGE_KEY = "story37.shots.previewSource";
const NAMU_WIKI_ICON_URL =
  "https://i.namu.wiki/i/dFaZEEOb4sVuaQ3A6RdrqyCswTm2GWU4tEKKFRnnnnrOBVg_aVqwt8kRxcVzEX9ua-99WCj5-_ubKCgRtsyll_Ef3UlqbJLpWqfnS2ztp0sNgZ30eZIldVKnHAtA8iPi2Zrbkvkn4lKSStXvVBmntA.svg";
const WIKIPEDIA_ICON_URL =
  "https://upload.wikimedia.org/wikipedia/commons/5/5a/Wikipedia%27s_W.svg?utm_source=commons.wikimedia.org&utm_campaign=index&utm_content=original";
const SHOW_TYPE_OPTIONS = [
  { value: 1, label: "movie" },
  { value: 2, label: "drama" },
  { value: 3, label: "vertical" }
];
const SHOW_LANG_OPTIONS = ["en", "ko", "ja", "zhs", "zht", "es", "fr", "de", "ar"];
const SHOT_TILE_SIZE = 160;
const SHOT_GRID_GAP = 2;
const SCENE_BREAK_HEIGHT = 34;

type ShotGridMetrics = {
  columns: number;
  tileSize: number;
};

type H2vState = {
  state: "inactive" | "starting" | "processing" | "stopping";
  run: { id:number; mode:"gpuk0"|"gpuk1"|"both"; enabled:{gpuk0:boolean;gpuk1:boolean}; total:number; done:number; failed:number; todo:number; encoding:number; current:Array<{serverName:string;showTitle:string;episodeNo:number|null}>; recent:{showTitle:string;episodeNo:number|null;episodeProgressPercent:number|null}|null; history:Array<{showId:number;showTitle:string;firstTs:number;episodes:number[];currentEpisodeNo:number|null;progressPercent:number|null}> } | null;
};

function h2vHistoryStartedAt(firstTs: number): string {
  const date = new Date(firstTs * 1000);
  return `${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function h2vEpisodeRanges(episodes: number[], longRange: boolean): string[] {
  const segments: string[] = [];
  let start = 0;
  for (let index = 0; index < episodes.length; index += 1) {
    if (index === episodes.length - 1 || episodes[index + 1] !== episodes[index] + 1) {
      if (start === index) {
        segments.push(`ep${episodes[index]}`);
      } else if (index - start === 1) {
        // 연속이어도 두 개면 범위 표기가 오히려 장황하다 — 그냥 나열한다.
        segments.push(`ep${episodes[start]}`, `ep${episodes[index]}`);
      } else {
        segments.push(`ep${episodes[start]} ~ ${longRange ? `ep${episodes[index]}` : episodes[index]}`);
      }
      start = index + 1;
    }
  }
  return segments;
}

function h2vHistoryEps(entry: { episodes: number[]; currentEpisodeNo: number | null; progressPercent: number | null }): string {
  if (entry.currentEpisodeNo == null) return h2vEpisodeRanges(entry.episodes, false).join(", ");
  const rest = entry.episodes.filter((no) => no !== entry.currentEpisodeNo);
  const segments = h2vEpisodeRanges(rest, true);
  segments.push(`ep${entry.currentEpisodeNo}`);
  return segments.join(", ") + (entry.progressPercent != null ? ` (${entry.progressPercent}%)` : "");
}

type NpuRecentBlock = {
  showId: number; showTitle: string; startedTs: number; lastTs: number;
  eps: number[]; currentNo: number | null; hasUnknownEps: boolean; progressPercent: number | null;
};

type NpuState = {
  state: "inactive" | "starting" | "processing" | "stopping";
  run: {
    id: number; xMode: "vllm" | "activity" | "done"; yMode: "horizontal_outpaint" | "native_vertical" | "done";
    zMode: "vertical" | "horizontal-vert" | "done" | "paused"; loopYEnabled: boolean; loopZEnabled: boolean;
    xDone: number; xFailed: number; yDone: number; yFailed: number; zDone: number; zFailed: number; freeGb: number;
    x_queued: number; x_running: number; y_queued: number; y_running: number;
    v_queued: number; v_running: number; v_failed: number;
    z_queued: number; z_running: number; z_failed: number;
  } | null;
  // run이 끝난 뒤에도 직전 run의 처리 기록을 보여준다(활성 run이 있으면 빈 배열).
  recentVl?: NpuRecentBlock[];
  recentDoodle?: NpuRecentBlock[];
  recentVisual?: NpuRecentBlock[];
};

function npuTaskProgress(done: number, running: number, queued: number, failed: number) {
  const parts = [`${done} done`];
  if (running) parts.push(`${running} running`);
  if (queued) parts.push(`${queued} queued`);
  if (failed) parts.push(`${failed} failed`);
  return parts.join(" · ");
}

function npuRecentStartedLabel(ts: number) {
  const date = new Date(ts * 1000);
  if (!Number.isFinite(ts) || Number.isNaN(date.getTime())) return "";
  return `${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function npuEpisodeRanges(episodes: number[], longRange: boolean) {
  // 연속 구간은 '~'로 잇되 두 개 연속은 그냥 개별 표기한다(H2V 표기 규칙).
  const segments: string[] = [];
  let start = 0;
  for (let index = 0; index < episodes.length; index += 1) {
    if (index === episodes.length - 1 || episodes[index + 1] !== episodes[index] + 1) {
      if (index - start + 1 >= 3) {
        segments.push(`ep${episodes[start]} ~ ${longRange ? `ep${episodes[index]}` : episodes[index]}`);
      } else {
        for (let cursor = start; cursor <= index; cursor += 1) segments.push(`ep${episodes[cursor]}`);
      }
      start = index + 1;
    }
  }
  return segments;
}

function npuRecentEps(block: NpuRecentBlock) {
  const known = [...block.eps].sort((left, right) => left - right);
  // 현재 처리 중인 ep는 목록에서 빼고, 맨 뒤에 진행율과 함께 특별 표기한다.
  const rest = block.currentNo == null ? known : known.filter((no) => no !== block.currentNo);
  const parts = known.length ? npuEpisodeRanges(rest, block.currentNo != null) : [];
  if (block.currentNo != null && block.currentNo > 0) {
    parts.push(`ep${block.currentNo}${block.progressPercent != null ? ` (${block.progressPercent}%)` : ""}`);
  }
  return parts.join(", ");
}

function mobileShotGridColumns(width: number) {
  if (width <= 0) {
    return 1;
  }
  if (width < 360) {
    return 2;
  }
  if (width < 540) {
    return 3;
  }
  if (width < 720) {
    return 4;
  }
  return Math.max(5, Math.floor((width + SHOT_GRID_GAP) / (SHOT_TILE_SIZE + SHOT_GRID_GAP)));
}

function shotGridMetrics(width: number, isMobile: boolean): ShotGridMetrics {
  if (isMobile) {
    const contentWidth = Math.max(0, width);
    if (contentWidth <= 0) {
      return { columns: 1, tileSize: SHOT_TILE_SIZE };
    }
    const columns = Math.max(1, mobileShotGridColumns(contentWidth));
    const tileSize = Math.max(1, Math.floor((contentWidth - SHOT_GRID_GAP * (columns - 1)) / columns));
    return { columns, tileSize };
  }

  const contentWidth = Math.max(0, width - SHOT_GRID_GAP * 2);
  const columns = Math.max(1, Math.floor((contentWidth + SHOT_GRID_GAP) / (SHOT_TILE_SIZE + SHOT_GRID_GAP)));
  return { columns, tileSize: SHOT_TILE_SIZE };
}

function persistSelectedShowId(showId: number) {
  try {
    localStorage.setItem(SELECTED_SHOW_STORAGE_KEY, String(showId));
  } catch {
    // Show persistence is optional.
  }
  try {
    document.cookie = `${SELECTED_SHOW_STORAGE_KEY}=${showId}; path=/; max-age=${SELECTED_SHOW_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
  } catch {
    // Cookie persistence is optional.
  }
}

function publishLastBrowseShot(shot: BrowseShot) {
  const payload: LastBrowseShot = {
    shotId: shot.shotId,
    videoId: shot.videoId,
    shotNo: shot.shotNo,
    durMs: shot.durMs,
    width: shot.width,
    height: shot.height,
    thumbUrl: shot.thumbUrl,
    videoUrl: shot.videoUrl,
    ts: Date.now()
  };
  try {
    localStorage.setItem(LAST_BROWSE_SHOT_STORAGE_KEY, JSON.stringify(payload));
    const history = normalizeBrowseShotHistory(JSON.parse(localStorage.getItem(BROWSE_SHOT_HISTORY_STORAGE_KEY) || "[]"));
    localStorage.setItem(BROWSE_SHOT_HISTORY_STORAGE_KEY, JSON.stringify([
      payload,
      ...history.filter((item) => item.shotId !== payload.shotId)
    ].slice(0, BROWSE_SHOT_HISTORY_LIMIT)));
  } catch {
    // Cross-tab handoff is optional; shot browsing must keep working without it.
  }
  try {
    const channel = new BroadcastChannel(LAST_BROWSE_SHOT_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // BroadcastChannel is not guaranteed in every embedded browser.
  }
}

type BrowseShotFetchPayload = {
  video?: { id: number; subManifestPath?: string | null } | null;
  shots?: BrowseShot[];
  compactShots?: BrowseShotCompactRow[];
  helpRows?: BrowseShotHelpCompactRow[];
  mediaBaseUrl?: string;
  requestedShowFound?: boolean;
};

function expandBrowseShotHelpRows(rows: BrowseShotHelpCompactRow[] = []) {
  return new Map<number, BrowseShotHelpRequest[]>(rows.map(([shotId, requests]) => [
    shotId,
    requests.map((request) => {
      const [id, type, severity, status, helpState, issueMask, systemIssueMask, userIssueMask, summary, detectorVersion] = request;
      return {
        id,
        type,
        severity,
        status,
        helpState,
        issueMask,
        systemIssueMask,
        userIssueMask,
        summary,
        details: null,
        detectorVersion
      };
    })
  ]));
}

function expandBrowseShotPayload(payload: BrowseShotFetchPayload) {
  if (!payload.compactShots || payload.compactShots.length === 0) {
    return payload.shots || [];
  }
  const videoId = Number(payload.video?.id || 0);
  const mediaBaseUrl = String(payload.mediaBaseUrl || "").replace(/\/+$/, "");
  if (!videoId || !mediaBaseUrl) {
    return [];
  }
  const helpRequestsByShotId = expandBrowseShotHelpRows(payload.helpRows);
  return payload.compactShots.map((row): BrowseShot => {
    const [shotId, shotNo, sceneStart, flags, width, height, startMs, durMs, frameCount, hasVert, seasonNo, episodeNo, doodleVideoUrl] = row;
    const file = `${String(shotId).padStart(6, "0")}`;
    const version = `${shotId}-${startMs}-${durMs}`;
    const seconds = durMs / 1000;
    return {
      key: String(shotId),
      videoId,
      shotId,
      shotNo,
      sceneStart,
      flags,
      softDeleted: (flags & 1) !== 0,
      width,
      height,
      startMs,
      durMs,
      frameCount,
      durationLabel: seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`,
      thumbUrl: `${mediaBaseUrl}/${videoId}/frames/thumbs/${file}.webp?v=${version}`,
      videoUrl: `${mediaBaseUrl}/${videoId}/shots/${file}.mp4?v=${version}`,
      verticalVideoUrl: hasVert ? `${mediaBaseUrl}/${videoId}/shots_v/${file}.mp4?v=${version}` : null,
      hasVert: Boolean(hasVert),
      poseVideoUrl: null,
      poseKeypointsJsonUrl: null,
      depthVideoUrl: null,
      edgeVideoUrl: null,
      doodleVideoUrl: doodleVideoUrl || null,
      seasonNo,
      episodeNo,
      motionState: "none",
      motionCaptionEn: null,
      motionSearchTextEn: null,
      motionKeywordsEn: null,
      motionPacketPreview: null,
      motionEmbeddingModel: null,
      motionEmbeddingDim: 0,
      helpRequests: helpRequestsByShotId.get(shotId) || [],
      subtitles: [],
      subtitleTracks: [],
      subtitleOriginalLanguage: null,
      subtitleFamiliarLanguage: null,
      subtitlePreviewLines: []
    };
  });
}

function browseDataShots(data: BrowseData) {
  if (data.shots.length > 0) {
    return data.shots;
  }
  return expandBrowseShotPayload({
    video: data.compactShotVideoId ? { id: data.compactShotVideoId } : null,
    shots: data.shots,
    compactShots: data.compactShots,
    helpRows: data.compactHelpRows,
    mediaBaseUrl: data.mediaBaseUrl
  });
}
const SHOT_GRID_OVERSCAN_PX = 640;
const SHOT_EDIT_POLL_MS = 2000;
const SHORT_ACTION_TIMEOUT_MS = 60000;
const DEPTH_RANGE_MIN_GAP = 1;
const DEPTH_RANGE_SOFTNESS = 0.035;

type BrowseToast = {
  id: number;
  kind: "success" | "error" | "info";
  text: string;
};

type SplitCorrectionJobStatus = {
  id?: number;
  learning_job_id?: number;
  status?: number;
  progress_msg?: string;
  last_error?: string | null;
  started_ts?: number;
  updated_ts?: number;
};

type SplitCorrectionStatus = {
  correctionActive: boolean;
  correctionJob?: SplitCorrectionJobStatus | null;
  learningActive?: boolean;
  learningJob?: SplitCorrectionJobStatus | null;
  state2Count?: number;
  splitLearning?: SplitLearningState;
};

type SplitLearningState = {
  labelCount?: number;
  dirtyCount?: number;
  affectedVideoCount?: number;
  labelUpdatedTs?: number;
  modelTs?: number;
  countMismatch?: boolean;
  isCurrent?: boolean;
};

type SplitCorrectionPreview = {
  ok?: boolean;
  state2?: {
    itemCount?: number;
    underCount?: number;
    overCount?: number;
  };
  blockers?: Array<{ type?: string; count?: number }>;
  sourceVideo?: { sourcePathSafe?: boolean };
};

type MobileShotStage = "shows" | "shots" | "preview";
type MobileStageHistoryState = { showId?: number; story37MobileStage?: unknown };
type MobileSwipeState = {
  pointerId: number;
  startX: number;
  startY: number;
  startTime: number;
};
type ProgressScrubState = {
  startClientX: number;
  startTime: number;
  duration: number;
  railWidth: number;
  hasMoved: boolean;
};
type PreviewSource = "original" | "lite" | "vertical-lite" | "doodle" | "pose" | "depth" | "edge";
type PreviewSubtitleMode = "original" | "familiar";
type DepthRangeHandle = "low" | "high";
type StoredEpisodeSelection = {
  episodeKey: string | null;
  videoId: number | null;
};

const MOBILE_SHOTS_QUERY = "(max-width: 760px), (hover: none) and (pointer: coarse)";
const MOBILE_STAGE_EDGE_SWIPE_START_X = 36;
const MOBILE_STAGE_SWIPE_MIN_DX = 70;
const MOBILE_STAGE_SWIPE_MIN_MS = 90;
const MOBILE_STAGE_SWIPE_RATIO = 1.4;

function storedPreviewPlaybackRate() {
  if (typeof window === "undefined") {
    return 1;
  }
  try {
    const stored = Number(localStorage.getItem(PREVIEW_PLAYBACK_RATE_STORAGE_KEY));
    return stored >= 1 && stored <= 5 ? stored : 1;
  } catch {
    return 1;
  }
}

function playbackRateFromKeyboardEvent(event: KeyboardEvent) {
  if (event.shiftKey) {
    return null;
  }
  if (event.key === "1" || event.code === "Digit1" || event.code === "Numpad1") {
    return 1;
  }
  if (event.key === "2" || event.code === "Digit2" || event.code === "Numpad2") {
    return 2;
  }
  if (event.key === "3" || event.code === "Digit3" || event.code === "Numpad3") {
    return 3;
  }
  if (event.key === "4" || event.code === "Digit4" || event.code === "Numpad4") {
    return 4;
  }
  if (event.key === "5" || event.code === "Digit5" || event.code === "Numpad5") {
    return 5;
  }
  return null;
}

function parseStoredEpisodeSelection(raw: string | null): StoredEpisodeSelection {
  if (!raw) {
    return { episodeKey: null, videoId: null };
  }
  const legacyVideoId = Number(raw);
  if (Number.isInteger(legacyVideoId) && legacyVideoId > 0) {
    return { episodeKey: null, videoId: legacyVideoId };
  }
  try {
    const parsed = JSON.parse(raw) as { episodeKey?: unknown; videoId?: unknown };
    const episodeKey = typeof parsed.episodeKey === "string" && parsed.episodeKey ? parsed.episodeKey : null;
    const videoId = Number(parsed.videoId || 0);
    return {
      episodeKey,
      videoId: Number.isInteger(videoId) && videoId > 0 ? videoId : null
    };
  } catch {
    return { episodeKey: null, videoId: null };
  }
}

function storedEpisodeSelection(showId: number): StoredEpisodeSelection {
  try {
    return parseStoredEpisodeSelection(localStorage.getItem(`${EPISODE_STORAGE_KEY}.${showId}`));
  } catch {
    return { episodeKey: null, videoId: null };
  }
}


function formatIssueCount(count: number) {
  return `${count} issue${count === 1 ? "" : "s"}`;
}

function formatReviewCount(count: number) {
  return `${count} review item${count === 1 ? "" : "s"}`;
}

type ShotEditJobDetail = Omit<BrowseShotEditJob, "status"> & {
  status: "pending" | "running" | "done" | "failed";
  resultJson: Record<string, unknown> | null;
  lastError: string | null;
};

class HttpResponseError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "HttpResponseError";
  }
}

async function fetchJsonWithTimeout<T>(url: string, init: RequestInit = {}, timeoutMs = SHORT_ACTION_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    const text = await response.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = { error: text };
      }
    }
    if (!response.ok) {
      const message =
        payload && typeof payload === "object" && "error" in payload
          ? String((payload as { error?: unknown }).error || response.statusText)
          : response.statusText;
      throw new HttpResponseError(message || `request failed: ${response.status}`, response.status);
    }
    return (payload || {}) as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(`server did not respond within ${Math.round(timeoutMs / 1000)}s`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function fetchShotSplitHelpJson<T>(init: RequestInit): Promise<T> {
  const maxAttempts = 3;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fetchJsonWithTimeout<T>("/api/shot-split-help", init);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const retryableStatus = error instanceof HttpResponseError && [502, 503, 504].includes(error.status);
      const retryableNetworkFailure = error instanceof TypeError || /^(Failed to fetch|Load failed)$/i.test(message);
      const retryable = retryableStatus || retryableNetworkFailure;
      if (!retryable || attempt >= maxAttempts) {
        throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, attempt * 150));
    }
  }
  throw new Error("split help update retry exhausted");
}

type MotionSearchResult = {
  shotId: number;
  videoId: number;
  showId: number;
  shotNo: number;
  startMs: number;
  durMs: number;
  seasonNo: number | null;
  episodeNo: number | null;
  score: number;
  captionEn: string | null;
  searchTextEn: string | null;
  suitability: number;
  badFlags: number;
  thumbUrl: string;
  videoUrl: string;
};

type ShotGridVirtualRow =
  | {
      type: "scene";
      key: string;
      top: number;
      height: number;
      shot: BrowseShot;
    }
  | {
      type: "shots";
      key: string;
      top: number;
      height: number;
      shots: BrowseShot[];
    };

function buildShotGridRows(shots: BrowseShot[], columns: number, tileSize: number) {
  const rows: ShotGridVirtualRow[] = [];
  let pendingShots: BrowseShot[] = [];
  let top = 0;

  const flushShotRow = () => {
    if (pendingShots.length === 0) {
      return;
    }
    rows.push({
      type: "shots",
      key: `shots-${pendingShots[0].key}`,
      top,
      height: tileSize,
      shots: pendingShots
    });
    top += tileSize + SHOT_GRID_GAP;
    pendingShots = [];
  };

  for (const shot of shots) {
    if (shot.sceneStart > 0) {
      flushShotRow();
      rows.push({
        type: "scene",
        key: `scene-${shot.key}-${shot.sceneStart}`,
        top,
        height: SCENE_BREAK_HEIGHT,
        shot
      });
      top += SCENE_BREAK_HEIGHT + SHOT_GRID_GAP;
    }
    pendingShots.push(shot);
    if (pendingShots.length >= columns) {
      flushShotRow();
    }
  }

  flushShotRow();
  return { rows, totalHeight: Math.max(0, top - SHOT_GRID_GAP) };
}

function videoHasShots(videoId: number, shots: BrowseShot[]) {
  return shots.some((shot) => shot.videoId === videoId);
}

function firstVideoWithShots(videos: BrowseVideo[], shots: BrowseShot[]) {
  return videos.find((video) => videoHasShots(video.id, shots)) || videos[0] || null;
}

function episodeContainsShot(episode: BrowseEpisode, shot: BrowseShot) {
  return (
    shot.videoId === episode.videoId &&
    shot.startMs >= episode.startMs &&
    (episode.endMs === null || shot.startMs < episode.endMs)
  );
}

function episodeHasShots(episode: BrowseEpisode, shots: BrowseShot[]) {
  return shots.some((shot) => episodeContainsShot(episode, shot));
}

function firstEpisodeWithShots(episodes: BrowseEpisode[], shots: BrowseShot[]) {
  return episodes.find((episode) => episodeHasShots(episode, shots)) || episodes[0] || null;
}

function episodeForVideo(episodes: BrowseEpisode[], videoId: number | null) {
  return videoId === null ? null : episodes.find((episode) => episode.videoId === videoId) || null;
}

function episodeForShot(episodes: BrowseEpisode[], shot: BrowseShot) {
  return episodes.find((episode) => episodeContainsShot(episode, shot)) || null;
}

function VirtualShotGrid({
  emptyContent,
  filterBar,
  footer,
  gridRef,
  isMobile,
  onClearSelection,
  onFocusShot,
  onSelectShot,
  selectedShotKeys,
  shotEditJobs,
  splitGbdtRejudgeActive,
  shots
}: {
  emptyContent: ReactNode;
  filterBar?: ReactNode;
  footer?: ReactNode;
  gridRef: React.RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  onClearSelection: (event: React.MouseEvent<HTMLDivElement>) => void;
  onFocusShot: (shot: BrowseShot) => void;
  onSelectShot: (shot: BrowseShot, event: React.MouseEvent<HTMLButtonElement>) => void;
  selectedShotKeys: string[];
  shotEditJobs: BrowseShotEditJob[];
  splitGbdtRejudgeActive: boolean;
  shots: BrowseShot[];
}) {
  const [viewport, setViewport] = useState({ width: 0, height: 0, scrollTop: 0 });
  const keyboardFocusRef = useRef(false);
  const selectedShotKeySet = useMemo(() => new Set(selectedShotKeys), [selectedShotKeys]);
  const shotEditJobByShotId = useMemo(() => {
    const jobs = new Map<number, BrowseShotEditJob>();
    for (const job of shotEditJobs) {
      for (const shotId of job.shotIds.length > 0 ? job.shotIds : [job.shotId]) {
        const existing = jobs.get(shotId);
        if (!existing || job.status === "running") {
          jobs.set(shotId, job);
        }
      }
    }
    return jobs;
  }, [shotEditJobs]);

  useEffect(() => {
    const element = gridRef.current;
    if (!element) {
      return;
    }

    let frame = 0;
    const updateViewport = () => {
      frame = 0;
      if (element.scrollLeft !== 0) {
        element.scrollLeft = 0;
      }
      setViewport({
        width: element.clientWidth,
        height: element.clientHeight,
        scrollTop: element.scrollTop
      });
    };
    const scheduleUpdate = () => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(updateViewport);
    };

    updateViewport();
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(element);
    element.addEventListener("scroll", scheduleUpdate, { passive: true });
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      resizeObserver.disconnect();
      element.removeEventListener("scroll", scheduleUpdate);
    };
  }, [gridRef]);

  const metrics = useMemo(() => shotGridMetrics(viewport.width, isMobile), [isMobile, viewport.width]);
  const { rows, totalHeight } = useMemo(
    () => buildShotGridRows(shots, metrics.columns, metrics.tileSize),
    [metrics.columns, metrics.tileSize, shots]
  );
  const innerStyle = useMemo(
    () => ({
      "--shot-tile-size": `${metrics.tileSize}px`,
      height: totalHeight
    }) as CSSProperties & Record<"--shot-tile-size", string>,
    [metrics.tileSize, totalHeight]
  );
  const visibleRows = useMemo(() => {
    const start = viewport.scrollTop - SHOT_GRID_OVERSCAN_PX;
    const end = viewport.scrollTop + viewport.height + SHOT_GRID_OVERSCAN_PX;
    return rows.filter((row) => row.top + row.height >= start && row.top <= end);
  }, [rows, viewport.height, viewport.scrollTop]);

  return (
    <div
      className="browseShotGrid browseShotGridVirtualized"
      onClick={onClearSelection}
      onKeyDownCapture={(event) => {
        if (event.key === "Tab") {
          keyboardFocusRef.current = true;
        }
      }}
      onPointerDownCapture={() => {
        keyboardFocusRef.current = false;
      }}
      ref={gridRef}
      aria-label="Shot thumbnails"
    >
      {filterBar ? <div className="shotGridFilterBar">{filterBar}</div> : null}
      {shots.length > 0 ? (
        <div className="browseShotGridVirtualInner" style={innerStyle}>
          {visibleRows.map((row) => {
            if (row.type === "scene") {
              return (
                <div
                  className="sceneStartBreak browseShotVirtualScene"
                  key={row.key}
                  style={{ top: row.top, height: row.height }}
                  aria-label={`Scene ${row.shot.sceneStart}`}
                >
                  <span>Scene {row.shot.sceneStart}</span>
                </div>
              );
            }

            return (
              <div
                className="browseShotVirtualRow"
                key={row.key}
                style={{ top: row.top, height: row.height }}
              >
                {row.shots.map((shot, index) => {
                  const editJob = shotEditJobByShotId.get(shot.shotId);
                  const helpState = shotSplitHelpState(shot);
                  const systemHelpFixing = helpState === "system_need_to_adjust" && (Boolean(editJob) || splitGbdtRejudgeActive);
                  return (
                    <button
                      className={`browseShotTile browseShotVirtualTile ${shot.durMs >= 10000 ? "durationLong" : ""} ${shot.durMs < 300 ? "durationShort" : ""} ${selectedShotKeySet.has(shot.key) ? "active" : ""} ${shot.softDeleted ? "softDeleted" : ""} ${editJob ? "editActive" : ""}`}
                      data-shot-key={shot.key}
                      key={shot.key}
                      onClick={(event) => onSelectShot(shot, event)}
                      onFocus={() => {
                        if (keyboardFocusRef.current) {
                          onFocusShot(shot);
                        }
                      }}
                      style={{
                        left: index * (metrics.tileSize + SHOT_GRID_GAP),
                        position: "absolute",
                        top: 0
                      }}
                      type="button"
                      aria-label={`Open shot ${shot.shotNo}`}
                    >
                      <img alt="" decoding="async" loading="lazy" src={shot.thumbUrl} />
                      <span className="shotDuration">{shot.durationLabel}</span>
                      {editJob ? (
                        <span
                          aria-label={`${editJob.action} ${editJob.status}`}
                          className={`shotEditActivity ${editJob.action}`}
                          title={`${editJob.action} ${editJob.status}`}
                        >
                          <LoaderCircle size={18} />
                        </span>
                      ) : null}
                      {helpState === "user_need_to_help" ? (
                        <span
                          aria-label="User help needed"
                          className="shotHelpBadge user"
                          title={shot.helpRequests.map((helpRequest) => helpRequest.summary).join("\n")}
                        >
                          <CircleHelp size={16} />
                        </span>
                      ) : helpState === "system_need_to_adjust" ? (
                        <span
                          aria-label="System adjustment needed"
                          className={`shotHelpBadge system${systemHelpFixing ? " fixing" : ""}`}
                          title={shot.helpRequests.map((helpRequest) => helpRequest.summary).join("\n")}
                        >
                          <LoaderCircle size={16} />
                        </span>
                      ) : helpState === "resolved" ? (
                        <span
                          aria-label="Split confirmed good"
                          className="shotHelpBadge resolved"
                          title={shot.helpRequests.map((helpRequest) => helpRequest.summary).join("\n")}
                        />
                      ) : helpState === "ignored" ? (
                        <span
                          aria-label="Split review ignored"
                          className="shotHelpBadge ignored"
                          title={shot.helpRequests.map((helpRequest) => helpRequest.summary).join("\n")}
                        />
                      ) : null}
                      {shot.subtitlePreviewLines.length > 0 ? (
                        <span className="shotSubtitleOverlay">
                          {shot.subtitlePreviewLines.map((line, subtitleIndex) => (
                            <span className="shotSubtitleLine" key={`${shot.key}-${subtitleIndex}`}>
                              {line}
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {shot.softDeleted ? <span aria-hidden="true" className="shotDeletedCross" /> : null}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        emptyContent
      )}
      {footer ? <div className="shotGridFooter" onClick={(event) => event.stopPropagation()}>{footer}</div> : null}
    </div>
  );
}

function wikiKind(url: string | null) {
  if (!url) {
    return null;
  }
  let host = "";
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return "wiki";
  }
  if (host === "namu.wiki" || host.endsWith(".namu.wiki")) {
    return "namu";
  }
  if (host === "wikipedia.org" || host.endsWith(".wikipedia.org")) {
    return "wikipedia";
  }
  return "wiki";
}

function formatTotalDuration(ms: number) {
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatCompactDuration(ms: number) {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatPreviewSeconds(ms: number) {
  const seconds = Math.max(0, ms) / 1000;
  if (seconds < 1) {
    return `${seconds.toFixed(3)}s`;
  }
  if (seconds < 10) {
    return `${seconds.toFixed(2)}s`;
  }
  return `${seconds.toFixed(1)}s`;
}

function videoProgressLabel(video: BrowseVideo) {
  const worker = video.encodingWorkerName ? ` ${video.encodingWorkerName}` : "";
  const message = video.progressMsg ? `: ${video.progressMsg}` : "";
  if (video.encodingJobStatus === "queued" && video.encodingJobPriority === 0) {
    return "held";
  }
  const preparedMessages = new Set(["prepared", "source local", "source ready", "prefetched, waiting", "paused, source ready"]);
  if (video.encodingJobStatus === "downloading" && video.progressMsg && preparedMessages.has(video.progressMsg)) {
    return `prepared${worker}${video.progressMsg === "prepared" ? "" : message}`;
  }
  if (video.progressTotal > 0 && video.progressStage > 0 && video.progressStage < 5) {
    return `${video.progressStageLabel}${worker} ${video.progressCurrent}/${video.progressTotal} (${video.progressPercent}%)`;
  }
  if (video.encodingJobStatus && video.encodingJobStatus !== "queued") {
    return `${video.encodingJobStatus}${worker}${message}`;
  }
  if (video.progressMsg && video.progressStageLabel !== video.status) {
    return `${video.progressStageLabel}: ${video.progressMsg}`;
  }
  if (video.encodingJobStatus) {
    return `${video.encodingJobStatus}${worker}`;
  }
  return video.status;
}

function videoShotCountLabel(video: BrowseVideo) {
  if (video.shotCount > 0) {
    return String(video.shotCount);
  }
  return "0";
}

function videoResolutionLabel(video: BrowseVideo) {
  return video.width > 0 && video.height > 0 ? `${video.width}x${video.height}` : "-";
}

// MB 단위로 변환, thousand separator 포함. 예: 4567000000 -> "4,567.00"
function videoSizeLabel(bytes: number): string {
  const mb = Math.round(bytes / (1024 * 1024));
  return mb.toLocaleString("en-US");
}

function videoTsvLine(video: BrowseVideo, showSeasonLabel: boolean) {
  return [
    String(video.id),
    episodeLabel(video.seasonNo, video.episodeNo, showSeasonLabel),
    videoProgressLabel(video),
    `${videoShotCountLabel(video)} shots`,
    videoResolutionLabel(video),
    video.fpsDen > 0 ? `${(video.fpsNum / video.fpsDen).toFixed(3)} fps` : "",
    video.srcSizeBytes > 0 ? videoSizeLabel(video.srcSizeBytes) : ""
    ,video.splitGbdtRevision === 0
      ? "GBDT legacy"
      : `GBDT r${video.splitGbdtRevision} (-${video.splitGbdtRevisionGap})`
  ].join("\t");
}

function motionStateLabel(state: BrowseMotionState) {
  if (state === "done") {
    return "Embedded";
  }
  if (state === "embedding") {
    return "Embedding";
  }
  if (state === "vlm") {
    return "Analyzing";
  }
  if (state === "failed") {
    return "Failed";
  }
  if (state === "partial") {
    return "Partial";
  }
  return "Not indexed";
}

function motionProgressTitle(progress: BrowseMotionProgress) {
  return [
    motionStateLabel(progress.state),
    `Processed ${progress.processedDone}/${progress.totalShots}`,
    `VLM ${progress.motionDone}/${progress.totalShots}`,
    `Embed ${progress.embeddingDone}/${progress.totalShots}`,
    `Pose ${progress.poseDone}/${progress.totalShots}`,
    `Depth ${progress.depthDone}/${progress.totalShots}`,
    `Edge ${progress.edgeDone}/${progress.totalShots}`,
    progress.dialogueDone || progress.dialogueActive || progress.dialogueFailed
      ? `Dialogue ${progress.dialogueDone}`
      : "",
    progress.grammarDone || progress.grammarActive || progress.grammarFailed
      ? `Grammar ${progress.grammarDone}/${progress.totalShots}`
      : "",
    progress.motionActive || progress.embeddingActive || progress.analysisActive
      ? `active ${progress.motionActive + progress.embeddingActive + progress.analysisActive}`
      : "",
    progress.motionFailed || progress.embeddingFailed || progress.analysisFailed
      ? `failed ${progress.motionFailed + progress.embeddingFailed + progress.analysisFailed}`
      : ""
  ].filter(Boolean).join(" · ");
}

function motionProcessedPercent(progress: Pick<BrowseMotionProgress, "totalShots" | "processedDone">) {
  return progress.totalShots > 0
    ? Math.max(0, Math.min(100, Math.round((progress.processedDone / progress.totalShots) * 100)))
    : 0;
}

function motionProgressPercentText(progress: BrowseMotionProgress) {
  if (progress.totalShots <= 0 || progress.state === "none") {
    return "";
  }
  const percent = motionProcessedPercent(progress);
  if (percent >= 100 || progress.state === "done") {
    return "";
  }
  return `${percent}%`;
}

function motionProgressTextFromStatus(status: BrowseShowMotionStatus | null) {
  if (!status || status.totalShots <= 0) {
    return "";
  }
  const hasMotionIndex =
    Boolean(status.job) ||
    status.motionDone > 0 ||
    status.motionActive > 0 ||
    status.motionFailed > 0 ||
    status.embeddingDone > 0 ||
    status.embeddingActive > 0 ||
    status.embeddingFailed > 0 ||
    status.processedDone > 0 ||
    status.analysisActive > 0 ||
    status.analysisFailed > 0 ||
    status.dialogueDone > 0 ||
    status.grammarDone > 0;
  if (!hasMotionIndex) {
    return "";
  }
  return `${motionProcessedPercent(status)}%`;
}

function motionProgressActive(progress: {
  motionActive: number;
  embeddingActive: number;
  analysisActive?: number;
  dialogueActive?: number;
  grammarActive?: number;
} | null | undefined) {
  return Boolean(
    progress &&
      (
        progress.motionActive > 0 ||
        progress.embeddingActive > 0 ||
        Number(progress.analysisActive || 0) > 0 ||
        Number(progress.dialogueActive || 0) > 0 ||
        Number(progress.grammarActive || 0) > 0
      )
  );
}

function motionProgressShouldPoll(progress: BrowseShowMotionStatus | null | undefined) {
  if (!progress || progress.totalShots <= 0) {
    return false;
  }
  if (motionProgressActive(progress)) {
    return true;
  }
  const complete = progress.processedDone >= progress.totalShots && progress.analysisActive === 0;
  if (complete) {
    return false;
  }
  return Boolean(
    progress.job ||
      progress.motionDone > 0 ||
      progress.motionFailed > 0 ||
      progress.embeddingDone > 0 ||
      progress.embeddingFailed > 0 ||
      progress.dialogueDone > 0 ||
      progress.grammarDone > 0
  );
}

/// 5개 프리뷰 탭(lite/v-lite/pose/depth/edge)의 표시 영상 규칙.
/// 탭 자신의 영상이 준비되면 그것을 보여주고, 아니면 v-lite, 그래도 없으면 lite로 폴백한다.
/// 탭 식별 자체는 바뀌지 않고 URL만 폴백한다 — 탭이 무엇을 "보여주는가"는 own 플래그로 안다.
function previewTabVideo(shot: BrowseShot, source: PreviewSource, originalUrl = ""): { url: string; own: boolean } {
  if (source === "original") {
    return originalUrl ? { url: originalUrl, own: true } : { url: shot.videoUrl, own: false };
  }
  if (source === "lite") {
    return { url: shot.videoUrl, own: true };
  }
  const ownUrl = source === "pose" ? shot.poseVideoUrl
    : source === "depth" ? shot.depthVideoUrl
    : source === "edge" ? shot.edgeVideoUrl
    : shot.verticalVideoUrl;
  if (ownUrl) {
    return { url: ownUrl, own: true };
  }
  if (shot.verticalVideoUrl) {
    return { url: shot.verticalVideoUrl, own: false };
  }
  return { url: shot.videoUrl, own: false };
}

/// doodle 탭은 래스터(선이 구워진 doodle mp4)를 절대 재생하지 않는다 — 벡터 데이터가
/// 로딩 중이거나 없으면 v-lite/lite 폴백 화면을 보여줄 뿐이다. 굵은 선 회귀 방지 정책.
/// 벡터 경로(순수 depth + canvas 오버레이)는 activePreviewMedia에서 별도로 선택한다.

/// 벡터 doodle 오버레이 — depth 영상 위에 프리미티브(눈꺼풀/입/눈썹 폴리라인, 홍채 원)를
/// 표시 해상도에서 즉석 렌더한다. 래스터 composite와 달리 어느 해상도에서도 선명하다.
function DoodleVectorOverlay({ canvasRef, primitives, videoEl }: {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  primitives: { fps: number | string; frameCount: number; persons: Array<{ frames: Array<Record<string, unknown> & { f: number }> }> };
  videoEl: HTMLVideoElement | null;
}) {
  useEffect(() => {
    if (!videoEl) return;
    let raf = 0;
    const fps = Number(primitives.fps) || 24;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const box = canvas.parentElement?.getBoundingClientRect();
      if (!box || box.width <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      // 그리기 캔버스는 디스플레이보다 2배 여유 해상도로 잡는다 — depth 영상과 달리 선은
      // 1px 급이라 디스플레이 해상도에 딱 맞추면 계단·번짐이 그대로 보인다. CSS 표시
      // 크기는 그대로 두고 백팅만 키운다.
      const supersample = 2;
      const width = Math.round(box.width * dpr * supersample), height = Math.round(box.height * dpr * supersample);
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      // 프리미티브 좌표는 '비디오 프레임'에 정규화돼 있다. 비디오는 object-fit: contain으로
      // 캔버스 안에 띠박스되므로(가로 doodle을 9:16 프레임에 띄울 때), 정규화 좌표를 캔버스
      // 전체가 아니라 비디오의 실제 표시 사각형에 매핑해야 한다. 2026-08-31 좌표 이탈의 원인.
      const videoW = videoEl.videoWidth, videoH = videoEl.videoHeight;
      if (!videoW || !videoH) return;
      const boxAspect = width / height, videoAspect = videoW / videoH;
      let contentW: number, contentH: number;
      if (videoAspect > boxAspect) { contentW = width; contentH = width / videoAspect; }
      else { contentH = height; contentW = height * videoAspect; }
      // landscapeInVertical(25% 구도)일 때는 CSS와 같은 기준으로 세로 중심을 맞춘다.
      const wrapLandscape = videoEl.closest(".previewVideoWrap")?.classList.contains("landscapeInVertical") === true;
      const contentX = (width - contentW) / 2;
      const contentY = wrapLandscape ? height * 0.25 - contentH / 2 : (height - contentH) / 2;
      const mapX = (nx: number) => contentX + nx * contentW;
      const mapY = (ny: number) => contentY + ny * contentH;
      const frame = Math.max(0, Math.min(primitives.frameCount - 1, Math.round(videoEl.currentTime * fps)));
      // 두께는 캔버스의 짧은 변 기준이다(세로 축으로 나누면 16/9배 두꺼워진다). 래스터
      // 흉내(/540)를 버리고 절반(/1080)으로 — 작은 얼굴에서도 위·아래 눈꺼풀 선이
      // 검게 붙어 보이지 않게 한다(2026-08-31 제품 지시).
      const lineWidth = Math.max(1, Math.min(width, height) / 1080);
      ctx.lineWidth = lineWidth;
      // 사람 depth는 밝은(흰 계열) 실루엣이므로 선·홍채는 검은색으로 그린다.
      ctx.strokeStyle = "#000";
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      for (const person of primitives.persons) {
        const entry = person.frames.find((item) => item.f === frame);
        if (!entry) continue;
        const trace = (xs: unknown, ys: unknown, close: boolean) => {
          const xsArr = xs as number[] | undefined, ysArr = ys as number[] | undefined;
          if (!Array.isArray(xsArr) || !Array.isArray(ysArr) || xsArr.length < 2) return;
          ctx.beginPath();
          for (let i = 0; i < xsArr.length; i += 1) {
            const x = mapX(xsArr[i]), y = mapY(ysArr[i]);
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          if (close) ctx.closePath();
          ctx.stroke();
        };
        const half = (value: unknown): [number[], number[]] => {
          const arr = Array.isArray(value) ? (value as number[]) : [];
          const mid = Math.floor(arr.length / 2);
          return [arr.slice(0, mid), arr.slice(mid)];
        };
        const [eRx, eRy] = half(entry.eR); const [eLx, eLy] = half(entry.eL);
        const [lipx, lipy] = half(entry.lip);
        const [bRx, bRy] = half(entry.bR); const [bLx, bLy] = half(entry.bL);
        trace(eRx, eRy, true); trace(eLx, eLy, true); trace(lipx, lipy, true);
        trace(bRx, bRy, false); trace(bLx, bLy, false);
        // 눈알(홍채)은 눈꺼풀 spline 내부에만 그린다 — 눈을 감거나 반쯤 감은 프레임에서
        // 홍채 원이 눈꺼풀 밖으로 삐져나오지 않게 컨투어로 clip한다.
        const irisInEye = (iris: unknown, exs: unknown, eys: unknown) => {
          const c = iris as number[] | undefined;
          if (!Array.isArray(c) || c.length < 3) return;
          const xsArr = exs as number[] | undefined, ysArr = eys as number[] | undefined;
          const hasContour = Array.isArray(xsArr) && Array.isArray(ysArr) && xsArr.length >= 2;
          if (hasContour) {
            ctx.save();
            ctx.beginPath();
            for (let i = 0; i < xsArr!.length; i += 1) {
              const x = mapX(xsArr![i]), y = mapY(ysArr![i]);
              if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.clip();
          }
          ctx.beginPath();
          ctx.arc(mapX(c[0]), mapY(c[1]), Math.max(1, c[2] * contentH), 0, Math.PI * 2);
          ctx.fillStyle = "#000";
          ctx.fill();
          if (hasContour) ctx.restore();
        };
        irisInEye(entry.cR, eRx, eRy);
        irisInEye(entry.cL, eLx, eLy);
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef, primitives, videoEl]);
  return <canvas aria-hidden="true" className="previewDoodleVector" ref={canvasRef} />;
}

function storedPreviewSource(): PreviewSource {
  if (typeof window === "undefined") return "lite";
  const stored=localStorage.getItem(PREVIEW_SOURCE_STORAGE_KEY);
  return stored==="original"||stored==="lite"||stored==="vertical-lite"||stored==="doodle"||stored==="pose"||stored==="depth"||stored==="edge"?stored:"lite";
}

function createDepthShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create depth mask shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || "depth mask shader compile failed";
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function createDepthProgram(gl: WebGLRenderingContext) {
  const vertexShader = createDepthShader(
    gl,
    gl.VERTEX_SHADER,
    `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
      }
    `
  );
  const fragmentShader = createDepthShader(
    gl,
    gl.FRAGMENT_SHADER,
    `
      precision mediump float;
      uniform sampler2D u_video;
      uniform float u_low;
      uniform float u_high;
      uniform float u_softness;
      varying vec2 v_texCoord;
      void main() {
        vec4 color = texture2D(u_video, v_texCoord);
        float depth = dot(color.rgb, vec3(0.299, 0.587, 0.114));
        float lower = u_low <= 0.0001 ? 1.0 : smoothstep(u_low, min(u_low + u_softness, u_high), depth);
        float upper = u_high >= 0.9999 ? 1.0 : 1.0 - smoothstep(max(u_high - u_softness, u_low), u_high, depth);
        float keep = lower * upper;
        gl_FragColor = vec4(color.rgb * keep, 1.0);
      }
    `
  );
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create depth mask program");
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || "depth mask program link failed";
    gl.deleteProgram(program);
    throw new Error(message);
  }
  return program;
}

/** 상태를 네 가지로만 줄인다. 진행 중인 단계 이름(vlm/embedding)은 사용자에게 의미가 없다. */
function motionBadgeTone(state: BrowseMotionProgress["state"]) {
  if (state === "done") return "done";
  if (state === "failed") return "failed";
  if (state === "none") return "";
  return "working";
}

function MotionProgressBadge({
  hideWhenDone = false,
  progress
}: {
  hideWhenDone?: boolean;
  progress: BrowseMotionProgress;
}) {
  const tone = motionBadgeTone(progress.state);
  if (progress.totalShots <= 0 || !tone) {
    return null;
  }
  if (hideWhenDone && tone === "done") {
    return null;
  }
  return (
    <span
      aria-label={motionProgressTitle(progress)}
      className={`motionBadge ${tone}`}
      title={motionProgressTitle(progress)}
    >
      ★
    </span>
  );
}

function isTextEditingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

function isMobileShotStage(value: unknown): value is MobileShotStage {
  return value === "shows" || value === "shots" || value === "preview";
}

function defaultMobileStageForPath(pathname: string): MobileShotStage {
  return /^\/shots\/\d+$/.test(pathname) ? "shots" : "shows";
}

function shouldIgnoreMobileStageSwipe(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (isTextEditingTarget(target)) {
    return true;
  }
  if (
    target.closest(
      [
        "video",
        "label",
        ".browseEpisodeRail",
        ".continuousModeButton",
        ".mobileGlobalNav",
        ".previewProgressBar",
        ".previewLanguageToggle",
        ".previewSpeedToggle",
        ".previewScrollButtons",
        ".previewSourceSwitch",
        ".shotScrollButtons",
        ".shotHelpBulkFooter",
        ".shotHelpFilter",
        ".showSourceFooter",
        ".sourcePathForm",
        ".uiSwitchControl"
      ].join(", ")
    )
  ) {
    return true;
  }
  const buttonOrLink = target.closest("button, a, [role='button']");
  if (!buttonOrLink) {
    return false;
  }
  return !buttonOrLink.classList.contains("browseShotTile");
}

export function ShotWorkspace({
  data,
  initialIsMobile = false,
  initialMobileStage = "shows"
}: {
  data: BrowseData;
  initialIsMobile?: boolean;
  initialMobileStage?: MobileShotStage;
}) {
  const initialDataShots = browseDataShots(data);
  const initialEpisode = firstEpisodeWithShots(data.episodes, initialDataShots);
  const initialVideoId = initialEpisode?.videoId ?? firstVideoWithShots(data.videos, initialDataShots)?.id ?? null;
  const workspaceRef = useRef<HTMLDivElement>(null);
  const showTableRef = useRef<HTMLDivElement>(null);
  const shotGridRef = useRef<HTMLDivElement>(null);
  const episodeRailRef = useRef<HTMLDivElement>(null);
  const previewPaneRef = useRef<HTMLDivElement>(null);
  const showRequestIdRef = useRef(0);
  const initialShotShowIdRef = useRef(0);
  const previousShotEditJobsRef = useRef<BrowseShotEditJob[]>(data.shotEditJobs);
  const toastIdRef = useRef(0);
  const currentVideoIdsRef = useRef<number[]>(data.videos.map((video) => video.id));
  const shotDetailsRequestIdRef = useRef(new Map<number, number>());
  const hydratedShowDetailsRef = useRef(new Set<number>());
  const selectedShowIdRef = useRef<number>(data.selectedShow?.id || 0);
  const selectedVideoIdRef = useRef<number | null>(initialVideoId);
  const selectedEpisodeKeyRef = useRef<string | null>(initialEpisode?.key ?? null);
  const loadedShotVideoIdsRef = useRef<number[]>(Array.from(new Set(initialDataShots.map((shot) => shot.videoId))));
  const isMobileShotsRef = useRef(initialIsMobile);
  const mobileStageRef = useRef<MobileShotStage>(initialMobileStage);
  const mobileSwipeRef = useRef<MobileSwipeState | null>(null);
  const [shows, setShows] = useState(data.shows);
  const [videos, setVideos] = useState(data.videos);
  const [episodes, setEpisodes] = useState(data.episodes);
  const [shotEditJobs, setShotEditJobs] = useState(data.shotEditJobs);
  const [toasts, setToasts] = useState<BrowseToast[]>([]);
  const [motionStatus, setMotionStatus] = useState(data.motionStatus);
  const [selectedShow, setSelectedShow] = useState<BrowseShow | null>(data.selectedShow);
  const [h2vState, setH2vState] = useState<H2vState>({ state:"inactive", run:null });
  const [npuState, setNpuState] = useState<NpuState>({ state:"inactive", run:null });
  const [shots, setShots] = useState(initialDataShots);
  const [loadedShotVideoIds, setLoadedShotVideoIds] = useState<number[]>(() =>
    Array.from(new Set(initialDataShots.map((shot) => shot.videoId)))
  );
  const [loadingShotVideoIds, setLoadingShotVideoIds] = useState<number[]>([]);
  const [initialShotLoadShowId, setInitialShotLoadShowId] = useState<number | null>(null);
  const [showLoadingId, setShowLoadingId] = useState<number | null>(null);
  const [showLoadError, setShowLoadError] = useState("");
  const [leftPct, setLeftPct] = useState(20);
  const [midPct, setMidPct] = useState(40);
  const [loadedLayoutKey, setLoadedLayoutKey] = useState<string | null>(null);
  const [showTypeFilter, setShowTypeFilter] = useState<"all" | 1 | 2 | 3>("all");
  const [selectedVideoId, setSelectedVideoId] = useState<number | null>(initialVideoId);
  const [selectedEpisodeKey, setSelectedEpisodeKey] = useState<string | null>(initialEpisode?.key ?? null);
  const [loadedEpisodeKey, setLoadedEpisodeKey] = useState<string | null>(null);
  const [selectedShotKeys, setSelectedShotKeys] = useState<string[]>([]);
  const [lastSelectedShotKey, setLastSelectedShotKey] = useState<string | null>(null);
  const lightboxImageRef = useRef<string | null>(null);
  const [pendingSceneShotKey, setPendingSceneShotKey] = useState<string | null>(null);
  const [pendingEpisodeShotKey, setPendingEpisodeShotKey] = useState<string | null>(null);
  const [pendingRangeMark, setPendingRangeMark] = useState<{ shotKey: string; mode: "intro_end" | "outro_start" } | null>(null);
  const [pendingSplitShotKeys, setPendingSplitShotKeys] = useState<string[]>([]);
  const [pendingDeleteShotKeys, setPendingDeleteShotKeys] = useState<string[]>([]);
  const [pendingMergeShotKeys, setPendingMergeShotKeys] = useState<string[]>([]);
  const [pendingMotionReprocessShotIds, setPendingMotionReprocessShotIds] = useState<number[]>([]);
  const [pendingSplitHelpShotIds, setPendingSplitHelpShotIds] = useState<number[]>([]);
  const pendingSplitHelpShotIdSetRef = useRef<Set<number>>(new Set());
  const splitHelpSaveQueueRef = useRef(new SerializedAsyncQueue());
  const splitLearningRefreshTimerRef = useRef<number | null>(null);
  const [splitHelpReviewOverrides, setSplitHelpReviewOverrides] = useState<Record<number, SplitHelpReviewOverride>>({});
  const [deletingVideoIds, setDeletingVideoIds] = useState<number[]>([]);
  const [retryingVideoIds, setRetryingVideoIds] = useState<number[]>([]);
  const [resplittingVideoIds, setResplittingVideoIds] = useState<number[]>([]);
  const [resplittingShowId, setResplittingShowId] = useState<number | null>(null);
  const [shotHelpFilter, setShotHelpFilter] = useState<ShotHelpFilterMode>("all");
  const [pendingHelpRequestIds, setPendingHelpRequestIds] = useState<number[]>([]);
  const [applyingSplitCorrection, setApplyingSplitCorrection] = useState(false);
  const [splitCorrectionStatuses, setSplitCorrectionStatuses] = useState<Record<number, SplitCorrectionStatus>>({});
  const [splitCorrectionStatusLoadingVideoIds, setSplitCorrectionStatusLoadingVideoIds] = useState<number[]>(
    () => initialVideoId === null ? [] : [initialVideoId]
  );
  const [splitCorrectionWatchJobIds, setSplitCorrectionWatchJobIds] = useState<Record<number, number>>({});
  const [splitCorrectionPreviews, setSplitCorrectionPreviews] = useState<Record<number, SplitCorrectionPreview>>({});
  const [splitLearningStatus, setSplitLearningStatus] = useState<SplitCorrectionStatus | null>(null);
  const [trainingGbdt, setTrainingGbdt] = useState(false);
  const [showDeleted, setShowDeleted] = useState(() => {
    try {
      return localStorage.getItem(SHOW_DELETED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [playRequestId, setPlayRequestId] = useState(0);
  const [sourceAdderOpen, setSourceAdderOpen] = useState(false);
  const [sourcePath, setSourcePath] = useState("");
  const [sourceRegisterBusy, setSourceRegisterBusy] = useState(false);
  const [sourceRegisterMessage, setSourceRegisterMessage] = useState("");
  const [loadedStoredShow, setLoadedStoredShow] = useState(false);
  const [isMobileShots, setIsMobileShots] = useState(initialIsMobile);
  const [mobileStage, setMobileStage] = useState<MobileShotStage>(initialMobileStage);
  const layoutProfile = selectedShow?.type === 3 ? "vertical" : "standard";
  const layoutStorageKey = `${BROWSE_LAYOUT_STORAGE_KEY}.${layoutProfile}`;
  const currentData = useMemo<BrowseData>(() => ({
    shows,
    videos,
    episodes,
    shots,
    splitHelpSummary: summarizeShotHelp(shots),
    shotEditJobs,
    motionStatus,
    selectedShow,
    requestedShowFound: true
  }), [episodes, motionStatus, shotEditJobs, selectedShow, shots, videos, shows]);

  const visibleShows = useMemo(() => {
    if (showTypeFilter === "all") {
      return shows;
    }
    return shows.filter((show) => show.type === showTypeFilter);
  }, [shows, showTypeFilter]);

  const visibleVideos = useMemo(
    () => (showDeleted ? videos : videos.filter((video) => !video.deleted)),
    [showDeleted, videos]
  );
  const videoById = useMemo(() => new Map(videos.map((video) => [video.id, video])), [videos]);
  const visibleVideoIds = useMemo(() => new Set(visibleVideos.map((video) => video.id)), [visibleVideos]);
  const visibleEpisodes = useMemo(
    () => episodes.filter((episode) => showDeleted || visibleVideoIds.has(episode.videoId)),
    [episodes, showDeleted, visibleVideoIds]
  );
  const selectedEpisode = selectedEpisodeKey === null
    ? null
    : visibleEpisodes.find((episode) => episode.key === selectedEpisodeKey) || null;
  const selectedVideo = selectedVideoId === null
    ? null
    : visibleVideos.find((video) => video.id === selectedVideoId) || null;
  const selectedEpisodeBaseShots = useMemo(() => {
    if (!selectedEpisode) {
      return [];
    }
    return shots.filter((shot) => episodeContainsShot(selectedEpisode, shot) && (showDeleted || !shot.softDeleted));
  }, [selectedEpisode, shots, showDeleted]);
  const selectedVideoHelpCounts = useMemo(
    () => ({
      unidentified: selectedEpisodeBaseShots.filter((shot) => shotSplitHelpState(shot) === "unidentified").length,
      review: countShotHelpIssues(selectedEpisodeBaseShots, "review"),
      ignored: countShotHelpIssues(selectedEpisodeBaseShots, "ignored"),
      over: countShotActionableHelpItems(selectedEpisodeBaseShots, "over_split"),
      under: countShotActionableHelpItems(selectedEpisodeBaseShots, "under_split_candidate")
    }),
    [selectedEpisodeBaseShots]
  );
  const selectedVideoHelpStateCounts = useMemo(() => {
    let user = 0;
    let system = 0;
    for (const shot of selectedEpisodeBaseShots) {
      const state = shotSplitHelpState(shot);
      if (state === "user_need_to_help") {
        user += 1;
      } else if (state === "system_need_to_adjust") {
        system += 1;
      }
    }
    return { user, system };
  }, [selectedEpisodeBaseShots]);
  const selectedVideoCorrectionCount = selectedVideoHelpStateCounts.system;
  const selectedSplitCorrectionStatus = selectedVideoId === null ? null : splitCorrectionStatuses[selectedVideoId] || null;
  const selectedSplitCorrectionPreview = selectedVideoId === null ? null : splitCorrectionPreviews[selectedVideoId] || null;
  const selectedVideoCorrectionActive = Boolean(selectedSplitCorrectionStatus?.correctionActive);
  const selectedVideoCorrectionStatusPending = selectedVideoId !== null && (
    splitCorrectionStatusLoadingVideoIds.includes(selectedVideoId) || !(selectedVideoId in splitCorrectionStatuses)
  );
  const selectedVideoCorrectionWatchJobId = selectedVideoId === null ? 0 : Number(splitCorrectionWatchJobIds[selectedVideoId] || 0);
  const selectedVideoCorrectionRunning = selectedVideoCorrectionActive || selectedVideoCorrectionWatchJobId > 0;
  const selectedVideoCorrectionButtonBusy = applyingSplitCorrection
    || selectedVideoCorrectionRunning
    || selectedVideoCorrectionStatusPending;
  const splitLearning = splitLearningStatus?.splitLearning || null;
  const splitLearningActive = Boolean(splitLearningStatus?.learningActive);
  const splitLearningDirtyCount = Number(splitLearning?.dirtyCount || 0);
  const splitLearningAffectedVideoCount = Number(splitLearning?.affectedVideoCount || 0);
  const selectedVideoPlannedSplitCount = selectedSplitCorrectionPreview
    ? Number(selectedSplitCorrectionPreview.state2?.underCount || 0)
    : selectedVideoHelpCounts.under;
  const selectedVideoPlannedMergeCount = selectedSplitCorrectionPreview
    ? Number(selectedSplitCorrectionPreview.state2?.overCount || 0)
    : selectedVideoHelpCounts.over;
  const selectedVideoCorrectionButtonLabel = selectedVideoPlannedMergeCount > 0
    ? selectedVideoPlannedSplitCount > 0
      ? `Apply Merge ${selectedVideoPlannedMergeCount} / Split ${selectedVideoPlannedSplitCount}`
      : `Apply Merge ${selectedVideoPlannedMergeCount}`
    : selectedVideoPlannedSplitCount > 0
      ? `Apply Split ${selectedVideoPlannedSplitCount}`
      : "Apply Merge/Split";
  const selectedVideoCorrectionBlockerCount = selectedSplitCorrectionPreview?.blockers?.length || 0;
  const filteredShots = useMemo(() => {
    if (shotHelpFilter !== "all") {
      return selectedEpisodeBaseShots.filter((shot) => {
        if (shotHelpFilter === "unidentified") {
          return shotSplitHelpState(shot) === "unidentified";
        }
        return shotMatchesActionableHelpFilter(shot, shotHelpFilter);
      });
    }
    return selectedEpisodeBaseShots;
  }, [selectedEpisodeBaseShots, shotHelpFilter]);

  useEffect(() => {
    let cancelled=false;
    let inFlight=false;
    const load=()=>{
      if(inFlight)return;
      inFlight=true;
      void fetch("/api/shot-h2v",{cache:"no-store"}).then((r)=>r.json()).then((p:H2vState)=>{
        if(cancelled||!p?.state)return;
        // start snapshot을 만드는 동안의 일시적인 inactive만 무시한다.
        // stop 완료 후 inactive까지 막으면 stopping spinner가 영원히 남는다.
        setH2vState((current)=>{
          const next=p.state==="inactive"&&current.state==="starting"?current:p;
          return JSON.stringify(current)===JSON.stringify(next)?current:next;
        });
      }).catch(()=>undefined).finally(()=>{inFlight=false;});
    };
    load(); const timer=window.setInterval(load,2000);
    return()=>{cancelled=true;window.clearInterval(timer);};
  },[]);

  async function setH2vGpu(gpu:"gpuk0"|"gpuk1",enabled:boolean) {
    const wasInactive=h2vState.state==="inactive";const activeCount=Number(h2vState.run?.enabled.gpuk0||false)+Number(h2vState.run?.enabled.gpuk1||false);const stopping=!enabled&&activeCount===1;
    setH2vState((current)=>({...current,state:stopping?"stopping":wasInactive?"starting":current.state}));
    const response=await fetch("/api/shot-h2v",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({action:"set-gpu",gpu,enabled})});
    const payload=await response.json() as H2vState&{error?:string};
    if(!response.ok){const refreshed=await fetch("/api/shot-h2v",{cache:"no-store"}).then((item)=>item.json()).catch(()=>null) as H2vState|null;if(refreshed?.state)setH2vState(refreshed);window.alert(payload.error||"H2V request failed");return;}
    setH2vState(payload);
  }

  // '지금 처리' — 이 shot의 세로 outpaint를 claim 대기열 앞쪽으로 새치기시킨다.

  const h2vStatusLabel={inactive:"Inactive",starting:"Starting",processing:"Processing",stopping:"Stopping"}[h2vState.state];
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    const load = () => {
      if (inFlight) return;
      inFlight = true;
      void fetch("/api/shot-npu", { cache: "no-store" }).then((response) => response.json()).then((payload: NpuState) => {
        if (!cancelled && payload?.state) setNpuState((current) => {
          const next = payload.state === "inactive" && current.state === "starting" ? current : payload;
          return JSON.stringify(current) === JSON.stringify(next) ? current : next;
        });
      }).catch(() => undefined).finally(() => { inFlight = false; });
    };
    load();
    const timer = window.setInterval(load, 2000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);

  async function requestNpu(action: "start" | "stop") {
    setNpuState((current) => ({ ...current, state: action === "stop" ? "stopping" : "starting" }));
    const response = await fetch("/api/shot-npu", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ action })
    });
    const payload = await response.json() as NpuState & { error?: string };
    if (!response.ok) {
      const refreshed = await fetch("/api/shot-npu", { cache: "no-store" }).then((item) => item.json()).catch(() => null) as NpuState | null;
      setNpuState(refreshed?.state ? refreshed : { state: "inactive", run: null });
      window.alert(payload.error || "NPU request failed");
      return;
    }
    setNpuState(payload);
  }
  const npuStatusLabel = { inactive: "Inactive", starting: "Starting", processing: "Processing", stopping: "Stopping" }[npuState.state];
  const npuActionBusy = npuState.state === "starting" || npuState.state === "stopping";
  const npuXLine = npuState.run ? npuState.run.xMode === "done"
    ? `VL descr complete — ${npuState.run.xDone} processed`
    : `${npuState.run.xMode === "vllm" ? "VL descr" : "activity labels"} — ${npuTaskProgress(npuState.run.xDone, npuState.run.x_running, npuState.run.x_queued, npuState.run.xFailed)}`
    : null;
  const npuYLine = npuState.run ? !npuState.run.loopYEnabled
    ? "Loop Y · dwpose paused"
    : npuState.run.yMode === "done"
    ? `dwpose complete — ${npuState.run.yDone} processed`
    : `${npuState.run.yMode === "horizontal_outpaint" ? "dwpose" : "dwpose vertical"} — ${npuTaskProgress(npuState.run.yDone, npuState.run.y_running, npuState.run.y_queued, npuState.run.yFailed)}`
    : null;
  const npuZLine = npuState.run && npuState.run.loopZEnabled
    ? `Loop Z · doodle ${npuState.run.zMode === "horizontal-vert" ? "outpaint" : npuState.run.zMode === "done" ? "done" : "vertical"} — ${npuTaskProgress(npuState.run.zDone,npuState.run.z_running,npuState.run.z_queued,npuState.run.zFailed)}`
    : null;
  const npuVLine = npuState.run && (npuState.run.v_running > 0 || npuState.run.v_queued > 0 || npuState.run.v_failed > 0)
    ? `visual — ${npuState.run.v_running} running · ${npuState.run.v_queued} queued${npuState.run.v_failed ? ` · ${npuState.run.v_failed} failed` : ""}`
    : null;
  // 활성 run이 끝나도 직전 run의 기록을 팝업에 유지한다(끝났다고 사라지면 방금 뭘 돌렸는지 안 보인다).
  // 오래된 순서로 두어 가장 최신(현재 처리 중) 블록이 맨 아래에 온다.
  const npuRecentVl = [...(npuState.recentVl ?? [])].reverse();
  const npuRecentDoodle = [...(npuState.recentDoodle ?? [])].reverse();
  const npuRecentVisual = [...(npuState.recentVisual ?? [])].reverse();
  // 디스크 경계는 lib/shotNpu.ts의 SOFT_FREE_GB(180, 프리페치 중단)와 HARD_FREE_GB(120, 신규 작업 중단)를 따른다.
  const npuDiskLine = npuState.run && npuState.run.freeGb > 0
    ? npuState.run.freeGb < 120
      ? { className: "showNpuDisk crit", text: `NPU disk ${npuState.run.freeGb}GB free · new work paused` }
      : npuState.run.freeGb < 180
        ? { className: "showNpuDisk low", text: `NPU disk ${npuState.run.freeGb}GB free · prefetch paused` }
        : { className: "showNpuDisk", text: `NPU disk ${npuState.run.freeGb}GB free` }
    : null;
  const visibleFilteredHelpRequests = useMemo(() => {
    if (shotHelpFilter === "all" || shotHelpFilter === "unidentified") {
      return [];
    }
    const seen = new Set<number>();
    const helpRequests: BrowseShotHelpRequest[] = [];
    for (const shot of filteredShots) {
      for (const helpRequest of shot.helpRequests) {
        const matchesFilter = helpRequestMatchesFilter(helpRequest, shotHelpFilter);
        if (!matchesFilter || seen.has(helpRequest.id)) {
          continue;
        }
        seen.add(helpRequest.id);
        helpRequests.push(helpRequest);
      }
    }
    return helpRequests;
  }, [filteredShots, shotHelpFilter]);
  const visibleFilteredHelpIssueCount = useMemo(
    () => shotHelpFilter === "all"
      ? 0
      : shotHelpFilter === "unidentified"
        ? filteredShots.length
        : countShotHelpIssues(filteredShots, shotHelpFilter),
    [filteredShots, shotHelpFilter]
  );
  const filteredShotsRef = useRef<BrowseShot[]>(filteredShots);
  const visibleEpisodesRef = useRef<BrowseEpisode[]>(visibleEpisodes);
  const shotsRef = useRef<BrowseShot[]>(shots);
  const showDeletedRef = useRef(showDeleted);
  const shotHelpFilterRef = useRef<ShotHelpFilterMode>(shotHelpFilter);
  const pendingFilterCenterRef = useRef<{
    selectedKeys: string[];
    preferredKey: string | null;
  } | null>(null);
  const selectedShotRef = useRef<BrowseShot | null>(null);
  const selectedShotsRef = useRef<BrowseShot[]>([]);
  const lastSelectedShotKeyRef = useRef<string | null>(lastSelectedShotKey);
  const shotAssetRefreshTsRef = useRef(new Map<number, number>());
  const shotAssetRefreshInFlightRef = useRef(new Set<number>());
  const pendingSceneShotKeyRef = useRef<string | null>(null);
  const runMultiActionRef = useRef<((action: "delete" | "merge", targetShots?: BrowseShot[]) => Promise<void>) | null>(null);
  const markSelectedShotsGoodRef = useRef<((targetShots?: BrowseShot[]) => Promise<boolean>) | null>(null);
  // 탭 이동 모드: Tab(help shot 순회)으로 진입한 뒤 리뷰 키(G/I)의 자동 이동도
  // 인접 샷이 아니라 다음 help shot으로 보낸다. 화살표/마우스/ESC/작품 전환으로 해제.
  const tabNavModeRef = useRef(false);
  const tabJumpNextRef = useRef<(() => boolean) | null>(null);

  useEffect(() => {
    setSelectedShotKeys((current) => {
      const next = current.filter((key) => filteredShots.some((shot) => shot.key === key));
      return next.length === current.length ? current : next;
    });
    if (lastSelectedShotKey !== null && !filteredShots.some((shot) => shot.key === lastSelectedShotKey)) {
      setLastSelectedShotKey(null);
    }
  }, [filteredShots, lastSelectedShotKey]);

  useEffect(() => {
    filteredShotsRef.current = filteredShots;
  }, [filteredShots]);

  useEffect(() => {
    visibleEpisodesRef.current = visibleEpisodes;
  }, [visibleEpisodes]);

  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);

  useEffect(() => {
    showDeletedRef.current = showDeleted;
  }, [showDeleted]);

  useEffect(() => {
    shotHelpFilterRef.current = shotHelpFilter;
  }, [shotHelpFilter]);

  const selectedShots = filteredShots.filter((shot) => selectedShotKeys.includes(shot.key));
  const selectedShot = selectedShotKeys.length === 1
    ? filteredShots.find((shot) => shot.key === selectedShotKeys[0]) || null
    : null;

  useEffect(() => {
    selectedShotRef.current = selectedShot;
  }, [selectedShot]);

  useEffect(() => {
    selectedShotsRef.current = selectedShots;
  }, [selectedShots]);

  useEffect(() => {
    lastSelectedShotKeyRef.current = lastSelectedShotKey;
  }, [lastSelectedShotKey]);

  // ShowInfoPanel lightbox 열림 상태를 ESC capture 핸들러에서 읽을 수 있게 유지한다.
  const handleLightboxImageChange = useCallback((image: string | null) => {
    lightboxImageRef.current = image;
  }, []);

  useEffect(() => {
    pendingSceneShotKeyRef.current = pendingSceneShotKey;
  }, [pendingSceneShotKey]);

  function shotGridRowOffset(grid: HTMLDivElement) {
    return grid.querySelector<HTMLDivElement>(".browseShotGridVirtualInner")?.offsetTop || 0;
  }

  function revealShotTileSoon(shotKey: string) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const grid = shotGridRef.current;
        if (!grid) {
          return;
        }
        const rowOffset = shotGridRowOffset(grid);
        const metrics = shotGridMetrics(grid.clientWidth, isMobileShotsRef.current);
        const { rows } = buildShotGridRows(filteredShotsRef.current, metrics.columns, metrics.tileSize);
        const row = rows.find((item): item is Extract<ShotGridVirtualRow, { type: "shots" }> =>
          item.type === "shots" && item.shots.some((shot) => shot.key === shotKey)
        );
        if (row) {
          const rowTop = rowOffset + row.top;
          const rowBottom = rowTop + row.height;
          const viewportTop = grid.scrollTop;
          const viewportBottom = viewportTop + grid.clientHeight;
          const comfortPadding = Math.min(96, Math.max(32, Math.floor(grid.clientHeight * 0.16)));
          const maxTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
          if (rowTop < viewportTop + comfortPadding) {
            grid.scrollTop = Math.max(0, rowTop - comfortPadding);
          } else if (rowBottom > viewportBottom - comfortPadding) {
            grid.scrollTop = Math.min(maxTop, Math.max(0, rowBottom - grid.clientHeight + comfortPadding));
          }
        }

        const target = Array.from(grid.querySelectorAll<HTMLButtonElement>("[data-shot-key]"))
          .find((button) => button.dataset.shotKey === shotKey);
        target?.focus({ preventScroll: true });
      });
    });
  }

  function centerShotTileSoon(shotKey: string) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const grid = shotGridRef.current;
        if (!grid) {
          return;
        }
        const rowOffset = shotGridRowOffset(grid);
        const metrics = shotGridMetrics(grid.clientWidth, isMobileShotsRef.current);
        const { rows } = buildShotGridRows(filteredShotsRef.current, metrics.columns, metrics.tileSize);
        const row = rows.find((item): item is Extract<ShotGridVirtualRow, { type: "shots" }> =>
          item.type === "shots" && item.shots.some((shot) => shot.key === shotKey)
        );
        if (!row) {
          return;
        }
        const maxTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
        const centeredTop = rowOffset + row.top + row.height / 2 - grid.clientHeight / 2;
        grid.scrollTop = Math.min(maxTop, Math.max(0, centeredTop));
      });
    });
  }

  function changeShotHelpFilter(nextFilter: ShotHelpFilterMode) {
    if (nextFilter === shotHelpFilter) {
      return;
    }
    pendingFilterCenterRef.current = selectedShotKeys.length > 0
      ? {
          selectedKeys: [...selectedShotKeys],
          preferredKey: lastSelectedShotKey
        }
      : null;
    setShotHelpFilter(nextFilter);
  }

  useEffect(() => {
    const pending = pendingFilterCenterRef.current;
    if (!pending) {
      return;
    }
    pendingFilterCenterRef.current = null;
    const selectedKeySet = new Set(pending.selectedKeys);
    const targetKey = pending.preferredKey && selectedKeySet.has(pending.preferredKey) &&
      filteredShots.some((shot) => shot.key === pending.preferredKey)
      ? pending.preferredKey
      : filteredShots.find((shot) => selectedKeySet.has(shot.key))?.key || null;
    if (targetKey) {
      centerShotTileSoon(targetKey);
    }
  }, [filteredShots, shotHelpFilter]);

  const selectedShowId = selectedShow?.id || 0;

  function mobileStagePath(showId: number) {
    return showId > 0 ? `/shots/${showId}` : "/shots";
  }

  function mobileHistoryState(stage: MobileShotStage, showId: number): MobileStageHistoryState {
    const existing = window.history.state;
    const base = existing && typeof existing === "object" ? existing : {};
    return {
      ...base,
      showId: showId > 0 ? showId : undefined,
      story37MobileStage: stage
    };
  }

  function goToMobileStage(stage: MobileShotStage, pushHistory = true, showId = selectedShowIdRef.current) {
    mobileStageRef.current = stage;
    setMobileStage(stage);
    const path = mobileStagePath(showId);
    const state = mobileHistoryState(stage, showId);
    if (pushHistory) {
      window.history.pushState(state, "", path);
    } else {
      window.history.replaceState(state, "", path);
    }
  }

  function previousMobileStage(stage = mobileStageRef.current): MobileShotStage | null {
    if (stage === "preview") {
      return "shots";
    }
    if (stage === "shots") {
      return "shows";
    }
    return null;
  }

  function navigateMobileStageBack() {
    const previous = previousMobileStage();
    if (!previous) {
      return;
    }
    goToMobileStage(previous, false);
  }

  function handleMobileStagePointerDown(event: ReactPointerEvent<HTMLElement>) {
    if (
      !isMobileShotsRef.current ||
      mobileStageRef.current === "shows" ||
      (event.pointerType === "mouse" && event.button !== 0) ||
      event.clientX > MOBILE_STAGE_EDGE_SWIPE_START_X ||
      shouldIgnoreMobileStageSwipe(event.target)
    ) {
      mobileSwipeRef.current = null;
      return;
    }
    mobileSwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: performance.now()
    };
  }

  function handleMobileStagePointerMove(event: ReactPointerEvent<HTMLElement>) {
    const swipe = mobileSwipeRef.current;
    if (!swipe || swipe.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - swipe.startX;
    const dy = event.clientY - swipe.startY;
    if (Math.abs(dy) > 48 && Math.abs(dy) > Math.abs(dx) * 1.2) {
      mobileSwipeRef.current = null;
    }
  }

  function handleMobileStagePointerEnd(event: ReactPointerEvent<HTMLElement>) {
    const swipe = mobileSwipeRef.current;
    mobileSwipeRef.current = null;
    if (!swipe || swipe.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - swipe.startX;
    const dy = event.clientY - swipe.startY;
    const elapsed = performance.now() - swipe.startTime;
    if (
      dx >= MOBILE_STAGE_SWIPE_MIN_DX &&
      dx >= Math.abs(dy) * MOBILE_STAGE_SWIPE_RATIO &&
      elapsed >= MOBILE_STAGE_SWIPE_MIN_MS
    ) {
      event.preventDefault();
      event.stopPropagation();
      navigateMobileStageBack();
    }
  }

  const shotHelpFilterBar = (
    <div className="shotHelpFilter" aria-label="Shot help filter">
      <button
        className={shotHelpFilter === "all" ? "active" : ""}
        onClick={() => changeShotHelpFilter("all")}
        type="button"
      >
        All
      </button>
      <button
        className={shotHelpFilter === "review" ? "active" : ""}
        disabled={selectedVideoHelpCounts.review === 0 && shotHelpFilter !== "review"}
        onClick={() => changeShotHelpFilter("review")}
        title="Show shots that need user review"
        type="button"
      >
        <span>Review</span>
        <strong>{selectedVideoHelpCounts.review}</strong>
      </button>
      <button
        className={shotHelpFilter === "unidentified" ? "active" : ""}
        disabled={selectedVideoHelpCounts.unidentified === 0 && shotHelpFilter !== "unidentified"}
        onClick={() => changeShotHelpFilter("unidentified")}
        title="Show shots that have no split review state"
        type="button"
      >
        <span>Unidentified</span>
        <strong>{selectedVideoHelpCounts.unidentified}</strong>
      </button>
      <button
        className={shotHelpFilter === "ignored" ? "active ignored" : "ignored"}
        disabled={selectedVideoHelpCounts.ignored === 0 && shotHelpFilter !== "ignored"}
        onClick={() => changeShotHelpFilter("ignored")}
        title="Show reviewed shots intentionally excluded from correction and training"
        type="button"
      >
        <EyeOff size={13} />
        <span>Ignored</span>
        <strong>{selectedVideoHelpCounts.ignored}</strong>
      </button>
      <button
        className={shotHelpFilter === "over_split" ? "active help" : "help"}
        disabled={selectedVideoPlannedMergeCount === 0 && shotHelpFilter !== "over_split"}
        onClick={() => changeShotHelpFilter("over_split")}
        title="Show merge help requests"
        type="button"
      >
        <CircleHelp size={14} />
        <span>Merge</span>
        <strong>{selectedVideoPlannedMergeCount}</strong>
      </button>
      <button
        className={shotHelpFilter === "under_split_candidate" ? "active help" : "help"}
        disabled={selectedVideoPlannedSplitCount === 0 && shotHelpFilter !== "under_split_candidate"}
        onClick={() => changeShotHelpFilter("under_split_candidate")}
        title="Show split help requests"
        type="button"
      >
        <CircleHelp size={14} />
        <span>Split</span>
        <strong>{selectedVideoPlannedSplitCount}</strong>
      </button>
    </div>
  );
  const shotStageFilterBar = (
    <div className="shotStageFilterBar">
      <button
        className="mobileStageBack mobileShotsBack"
        onClick={navigateMobileStageBack}
        type="button"
      >
        <ChevronLeft size={16} />
        <span>Shows</span>
      </button>
    </div>
  );
  const shotHelpFilterFooter = shotHelpFilter === "all" || shotHelpFilter === "ignored" || visibleFilteredHelpRequests.length === 0 ? null : (
    <div className="shotHelpBulkFooter">
      <span>
        {shotHelpFilter === "review"
          ? formatReviewCount(visibleFilteredHelpIssueCount)
          : formatIssueCount(visibleFilteredHelpIssueCount)} left
      </span>
      <button
        disabled={visibleFilteredHelpRequests.some((helpRequest) => pendingHelpRequestIds.includes(helpRequest.id))}
        onClick={() => markFilteredHelpRequestsGood()}
        title="Mark every visible help request in this filter as good"
        type="button"
      >
        <Check size={14} />
        <span>Mark all good</span>
      </button>
    </div>
  );
  const selectedVideoShotsLoading = selectedVideoId !== null && loadingShotVideoIds.includes(selectedVideoId);
  const selectedShowInitialShotsLoading = selectedShowId > 0 && initialShotLoadShowId === selectedShowId;
  const selectedDeletePending = selectedShots.some((shot) => pendingDeleteShotKeys.includes(shot.key));
  const selectedMergePending = selectedShots.some((shot) => pendingMergeShotKeys.includes(shot.key));

  const showEpisodeRail = visibleEpisodes.length > 1;

  useEffect(() => {
    if (!showEpisodeRail || selectedEpisodeKey === null) {
      return;
    }
    const rail = episodeRailRef.current;
    if (!rail) {
      return;
    }
    const button = Array.from(rail.querySelectorAll<HTMLButtonElement>("[data-episode-key]"))
      .find((item) => item.dataset.episodeKey === selectedEpisodeKey);
    if (!button) {
      return;
    }
    window.requestAnimationFrame(() => {
      button.scrollIntoView({
        block: "nearest",
        inline: "center",
        behavior: "auto"
      });
    });
  }, [showEpisodeRail, selectedEpisodeKey, selectedShowId, visibleEpisodes]);

  const showSeasonLabels = useMemo(() => {
    const seasonNumbers = new Set(
      visibleEpisodes.map((episode) => episode.seasonNo)
    );
    return seasonNumbers.size > 1;
  }, [visibleEpisodes]);
  const episodeStorageKey = `${EPISODE_STORAGE_KEY}.${selectedShowId}`;
  const seasonGroups = useMemo(() => {
    const groups: Array<{ deleted: boolean; seasonNo: number; episodes: BrowseEpisode[] }> = [];
    for (const deleted of [false, true]) {
      for (const episode of visibleEpisodes) {
        if (episode.deleted !== deleted) {
          continue;
        }
        const seasonNo = episode.seasonNo;
        let group = groups.find((item) => item.deleted === deleted && item.seasonNo === seasonNo);
        if (!group) {
          group = { deleted, seasonNo, episodes: [] };
          groups.push(group);
        }
        group.episodes.push(episode);
      }
    }
    return groups;
  }, [visibleEpisodes]);

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_DELETED_STORAGE_KEY, showDeleted ? "1" : "0");
    } catch {
      // Deleted visibility is a UI preference; ignore storage failures.
    }
  }, [showDeleted]);

  useEffect(() => {
    const nextShots = browseDataShots(data);
    setShows(data.shows);
    setVideos(data.videos);
    setEpisodes(data.episodes);
    setShotEditJobs(data.shotEditJobs);
    previousShotEditJobsRef.current = data.shotEditJobs;
    setMotionStatus(data.motionStatus);
    setSelectedShow(data.selectedShow);
    selectedShowIdRef.current = data.selectedShow?.id || 0;
    setShots(nextShots);
    setLoadedShotVideoIds(Array.from(new Set(nextShots.map((shot) => shot.videoId))));
    setLoadingShotVideoIds([]);
    setInitialShotLoadShowId(null);
    initialShotShowIdRef.current = 0;
    const nextEpisode = firstEpisodeWithShots(data.episodes, nextShots);
    selectedEpisodeKeyRef.current = nextEpisode?.key ?? null;
    setSelectedEpisodeKey(nextEpisode?.key ?? null);
    selectedVideoIdRef.current = nextEpisode?.videoId ?? firstVideoWithShots(data.videos, nextShots)?.id ?? null;
    setSelectedVideoId(selectedVideoIdRef.current);
    setLoadedStoredShow(false);
  }, [data]);

  useEffect(() => {
    if (!selectedShowId || shots.length === 0 || hydratedShowDetailsRef.current.has(selectedShowId)) {
      return;
    }
    const showId = selectedShowId;
    const requestId = showRequestIdRef.current;
    hydratedShowDetailsRef.current.add(showId);
    let cancelled = false;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        void fetchShowSection(showId)
          .then((payload) => {
            if (cancelled || showRequestIdRef.current !== requestId) {
              return;
            }
            applyShowSection(payload, showId);
          })
          .catch(() => {
            hydratedShowDetailsRef.current.delete(showId);
          });
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) {
        window.cancelAnimationFrame(secondFrame);
      }
    };
  }, [selectedShowId, shots.length]);

  useEffect(() => {
    if (
      !selectedShowId ||
      selectedVideoId === null ||
      !loadedShotVideoIds.includes(selectedVideoId) ||
      shotDetailsRequestIdRef.current.has(selectedVideoId)
    ) {
      return;
    }
    scheduleShotDetailsLoad(selectedVideoId, selectedShowId);
  }, [loadedShotVideoIds, selectedShowId, selectedVideoId]);

  useEffect(() => {
    currentVideoIdsRef.current = videos.map((video) => video.id);
  }, [videos]);

  useEffect(() => {
    loadedShotVideoIdsRef.current = loadedShotVideoIds;
  }, [loadedShotVideoIds]);

  useEffect(() => {
    selectedShowIdRef.current = selectedShowId;
  }, [selectedShowId]);

  useEffect(() => {
    selectedVideoIdRef.current = selectedVideoId;
  }, [selectedEpisode?.key, selectedVideoId]);

  useEffect(() => {
    selectedEpisodeKeyRef.current = selectedEpisodeKey;
  }, [selectedEpisodeKey]);

  useEffect(() => {
    mobileStageRef.current = mobileStage;
  }, [mobileStage]);

  useEffect(() => {
    isMobileShotsRef.current = isMobileShots;
  }, [isMobileShots]);

  useEffect(() => {
    if (!isMobileShots) {
      delete document.documentElement.dataset.shotsMobileStage;
      return;
    }
    document.documentElement.dataset.shotsMobileStage = mobileStage;
    return () => {
      delete document.documentElement.dataset.shotsMobileStage;
    };
  }, [isMobileShots, mobileStage]);

  useEffect(() => {
    const query = window.matchMedia(MOBILE_SHOTS_QUERY);

    function syncMobileMode() {
      const matches = query.matches;
      isMobileShotsRef.current = matches;
      setIsMobileShots(matches);
      if (!matches) {
        return;
      }
      const state = window.history.state as MobileStageHistoryState | null;
      const stage = isMobileShotStage(state?.story37MobileStage)
        ? state.story37MobileStage
        : defaultMobileStageForPath(window.location.pathname);
      mobileStageRef.current = stage;
      setMobileStage(stage);
      window.history.replaceState(mobileHistoryState(stage, selectedShowIdRef.current), "", window.location.href);
    }

    syncMobileMode();
    query.addEventListener("change", syncMobileMode);
    return () => query.removeEventListener("change", syncMobileMode);
  }, []);

  useEffect(() => {
    if (loadedStoredShow) {
      return;
    }
    setLoadedStoredShow(true);
    if (window.location.pathname !== "/shots") {
      if (selectedShowId) {
        centerShowRowSoon(selectedShowId);
      }
      return;
    }
    try {
      const storedShowId = parseStoredShowId(localStorage.getItem(SELECTED_SHOW_STORAGE_KEY));
      if (!storedShowId || storedShowId === selectedShowId) {
        if (storedShowId || selectedShowId) {
          centerShowRowSoon(storedShowId || selectedShowId);
        }
        return;
      }
      if (!shows.some((show) => show.id === storedShowId)) {
        return;
      }
      void selectShow(storedShowId, true);
      centerShowRowSoon(storedShowId);
    } catch {
      // Show persistence is optional.
    }
  }, [loadedStoredShow, selectedShowId, shows]);

  useEffect(() => {
    if (!selectedShowId) {
      return;
    }
    persistSelectedShowId(selectedShowId);
  }, [selectedShowId]);

  useEffect(() => {
    const previousJobs = previousShotEditJobsRef.current;
    const currentJobIds = new Set(shotEditJobs.map((job) => job.id));
    const completedJobs = previousJobs.filter((job) => !currentJobIds.has(job.id));
    const completedVideoIds = Array.from(new Set(completedJobs.map((job) => job.videoId)));
    previousShotEditJobsRef.current = shotEditJobs;
    if (completedVideoIds.length === 0) {
      return;
    }

    setLoadedShotVideoIds((current) => current.filter((videoId) => !completedVideoIds.includes(videoId)));
    void fetchShotEditJobDetails(completedJobs.map((job) => job.id))
      .then((jobs) => {
        const detailsById = new Map(jobs.map((job) => [job.id, job]));
        for (const job of completedJobs) {
          const detail = detailsById.get(job.id);
          if (!detail) {
            continue;
          }
          pushToast(detail.status === "failed" ? "error" : "success", shotEditToastText(detail));
        }
      })
      .catch(() => {
        for (const job of completedJobs) {
          pushToast("info", shotEditToastText(job));
        }
      });
  }, [shotEditJobs]);

  useEffect(() => {
    if (!selectedShowId || shotEditJobs.length === 0) {
      return;
    }
    const timer = window.setInterval(() => {
      const showId = selectedShowId;
      void fetchShowSection(showId)
        .then((payload) => applyShowSection(payload, showId))
        .catch(() => {
          // Shot edit jobs are durable in DB; the next poll or navigation will refresh them.
        });
    }, SHOT_EDIT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [selectedShowId, shotEditJobs.length]);

  useEffect(() => {
    if (shotGridRef.current) {
      shotGridRef.current.scrollTop = 0;
    }
  }, [selectedShowId, selectedVideoId]);

  useEffect(() => {
    function shotNeedsUserHelp(shot: BrowseShot) {
      const filter = shotHelpFilterRef.current;
      return shotSplitHelpState(shot) === "user_need_to_help" &&
        (
          filter === "all" ||
          shot.helpRequests.some((helpRequest) => helpRequestMatchesFilter(helpRequest, filter))
        );
    }

    function rowNeedsUserHelp(row: ShotGridVirtualRow) {
      return row.type === "shots" && row.shots.some((shot) => shotNeedsUserHelp(shot));
    }

    function userHelpShotInRow(row: ShotGridVirtualRow, direction: "next" | "prev") {
      if (row.type !== "shots") {
        return null;
      }
      const shots = direction === "next" ? row.shots : [...row.shots].reverse();
      return shots.find((shot) => shotNeedsUserHelp(shot)) || null;
    }

    function scrollShotListPage(direction: "next" | "prev") {
      const grid = shotGridRef.current;
      if (!grid) {
        return;
      }
      const currentTop = grid.scrollTop;
      const viewportTop = currentTop;
      const viewportBottom = currentTop + grid.clientHeight;
      const maxTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
      const metrics = shotGridMetrics(grid.clientWidth, isMobileShotsRef.current);
      const { rows } = buildShotGridRows(filteredShotsRef.current, metrics.columns, metrics.tileSize);
      if (direction === "prev") {
        const firstIncomplete = rows.find((row) => {
          const rowBottom = row.top + row.height;
          return row.top < viewportTop - 1 && rowBottom > viewportTop + 1;
        });
        if (firstIncomplete) {
          grid.scrollTop = Math.max(0, firstIncomplete.top + firstIncomplete.height - grid.clientHeight);
          return;
        }
        const targetTop = Math.max(0, currentTop - grid.clientHeight);
        const targetBottom = targetTop + grid.clientHeight;
        const previousVisible = rows
          .filter((row) => row.top < currentTop - 1)
          .reverse()
          .find((row) => row.top + row.height <= targetBottom + 1);
        grid.scrollTop = previousVisible ? Math.max(0, previousVisible.top) : targetTop;
        return;
      }

      const firstIncomplete = rows.find((row) => {
        const rowBottom = row.top + row.height;
        return row.top < viewportBottom - 1 && rowBottom > viewportBottom + 1;
      });
      const firstAfterViewport = rows.find((row) => row.top >= viewportBottom - 1);
      const targetTop = firstIncomplete
        ? firstIncomplete.top
        : firstAfterViewport
          ? firstAfterViewport.top
          : maxTop;
      grid.scrollTop = Math.min(maxTop, targetTop > currentTop + 1 ? targetTop : currentTop + grid.clientHeight);
    }

    function scrollShotListHelp(direction: "next" | "prev") {
      const grid = shotGridRef.current;
      if (!grid) {
        return false;
      }
      const rowOffset = shotGridRowOffset(grid);
      const contentTop = Math.max(0, grid.scrollTop - rowOffset);
      const contentBottom = contentTop + grid.clientHeight;
      const maxTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
      const metrics = shotGridMetrics(grid.clientWidth, isMobileShotsRef.current);
      const { rows } = buildShotGridRows(filteredShotsRef.current, metrics.columns, metrics.tileSize);
      if (rows.length === 0) {
        return false;
      }

      let targetRow: ShotGridVirtualRow | undefined;
      let targetShot: BrowseShot | null = null;
      const selectedShots = selectedShotsRef.current;
      const currentShotKey =
        selectedShotRef.current?.key ||
        selectedShots[selectedShots.length - 1]?.key ||
        lastSelectedShotKeyRef.current;
      let selectedRowIndex = -1;
      if (currentShotKey) {
        selectedRowIndex = rows.findIndex((row) =>
          row.type === "shots" && row.shots.some((shot) => shot.key === currentShotKey)
        );
        if (selectedRowIndex >= 0) {
          const selectedRow = rows[selectedRowIndex];
          const selectedShotIndex = selectedRow.type === "shots"
            ? selectedRow.shots.findIndex((shot) => shot.key === currentShotKey)
            : -1;
          if (selectedRow.type === "shots" && selectedShotIndex >= 0) {
            const sameRowIndexes = direction === "next"
              ? selectedRow.shots.slice(selectedShotIndex + 1).map((_, index) => selectedShotIndex + 1 + index)
              : selectedRow.shots.slice(0, selectedShotIndex).map((_, index) => index).reverse();
            for (const shotIndex of sameRowIndexes) {
              const shot = selectedRow.shots[shotIndex];
              if (shotNeedsUserHelp(shot)) {
                targetRow = selectedRow;
                targetShot = shot;
                break;
              }
            }
          }
          if (!targetShot && direction === "next") {
            for (let index = selectedRowIndex + 1; index < rows.length; index += 1) {
              const row = rows[index];
              if (!rowNeedsUserHelp(row)) {
                continue;
              }
              targetRow = row;
              targetShot = userHelpShotInRow(row, direction);
              break;
            }
          } else if (!targetShot) {
            for (let index = selectedRowIndex - 1; index >= 0; index -= 1) {
              const row = rows[index];
              if (!rowNeedsUserHelp(row)) {
                continue;
              }
              targetRow = row;
              targetShot = userHelpShotInRow(row, direction);
              break;
            }
          }
        }
      }

      const firstVisibleIndex = rows.findIndex((row) => row.top + row.height > contentTop + 1 && row.top < contentBottom - 1);
      const anchorIndex = firstVisibleIndex >= 0
        ? firstVisibleIndex
        : rows.findIndex((row) => row.top >= contentTop - 1);
      const currentIndex = anchorIndex >= 0 ? anchorIndex : rows.length - 1;
      const currentRow = rows[currentIndex];
      const currentRowScrollTop = rowOffset + currentRow.top;
      const currentRowNeedsAlignment = rowNeedsUserHelp(currentRow) && Math.abs(currentRowScrollTop - grid.scrollTop) > 1;

      if (targetRow && targetShot) {
        // 선택된 shot을 기준으로 같은 행의 다음 help를 먼저 선택한다.
      } else if (selectedRowIndex >= 0) {
        // 선택된 shot 기준 탐색이 방향 끝까지 도달했다는 뜻이다. 뷰포트 정렬/스캔은
        // 반대편(직전) help 행을 잘못 고르므로 건너뛰고 아래 순환 검색으로 넘어간다.
      } else if (currentRowNeedsAlignment) {
        targetRow = currentRow;
      } else if (direction === "next") {
        for (let index = currentIndex; index < rows.length; index += 1) {
          const row = rows[index];
          if (rowNeedsUserHelp(row) && rowOffset + row.top > grid.scrollTop + 1) {
            targetRow = row;
            break;
          }
        }
      } else {
        for (let index = currentIndex - 1; index >= 0; index -= 1) {
          const row = rows[index];
          if (rowNeedsUserHelp(row)) {
            targetRow = row;
            break;
          }
        }
      }

      // 끝에 도달해 더 갈 help shot이 없으면 반대편 끝의 help shot으로 순환한다(현재 shot은 건너뛴다).
      const wrapHelpShot = () => {
        const wrapRows = direction === "next" ? rows : [...rows].reverse();
        for (const row of wrapRows) {
          if (row.type !== "shots") {
            continue;
          }
          const wrapShots = direction === "next" ? row.shots : [...row.shots].reverse();
          const wrapShot = wrapShots.find((shot) => shot.key !== currentShotKey && shotNeedsUserHelp(shot));
          if (wrapShot) {
            return { row, shot: wrapShot };
          }
        }
        return null;
      };

      if (!targetRow) {
        const wrapped = wrapHelpShot();
        if (!wrapped) {
          return false;
        }
        targetRow = wrapped.row;
        targetShot = wrapped.shot;
      }
      grid.scrollTop = Math.min(maxTop, Math.max(0, rowOffset + targetRow.top));
      targetShot = targetShot || userHelpShotInRow(targetRow, direction);
      if (!targetShot || targetShot.key === currentShotKey) {
        // 마지막 행이 그리드 하단(maxTop)에 걸려 상단 정렬이 불가능하면 뷰포트 스캔이
        // 현재 shot을 다시 고를 수 있다. 이 경우 순환 검색으로 목적지를 바꾼다.
        const wrapped = wrapHelpShot();
        if (!wrapped) {
          return false;
        }
        targetRow = wrapped.row;
        targetShot = wrapped.shot;
        grid.scrollTop = Math.min(maxTop, Math.max(0, rowOffset + targetRow.top));
      }
      // Tab 순회로 들어온 이동은 탭 이동 모드로 기록한다.
      tabNavModeRef.current = true;
      selectShotByKeyboard(targetShot);
      return true;
    }

    tabJumpNextRef.current = () => scrollShotListHelp("next");

    function focusShotTileSoon(shotKey: string) {
      revealShotTileSoon(shotKey);
    }

    function selectShotByKeyboard(shot: BrowseShot, options: { play?: boolean } = {}) {
      setSelectedShotKeys([shot.key]);
      setLastSelectedShotKey(shot.key);
      selectedShotsRef.current = [shot];
      selectedShotRef.current = shot;
      lastSelectedShotKeyRef.current = shot.key;
      publishLastBrowseShot(shot);
      void refreshShotVideoAssets(shot.videoId);
      if (options.play) {
        setPlayRequestId((current) => current + 1);
      }
      focusShotTileSoon(shot.key);
    }

    function visibleShotsForEpisode(episode: BrowseEpisode, sourceShots: BrowseShot[]) {
      const filter = shotHelpFilterRef.current;
      return sourceShots
        .filter((shot) => episodeContainsShot(episode, shot) && (showDeletedRef.current || !shot.softDeleted))
        .filter((shot) => {
          if (filter === "all") {
            return true;
          }
          if (filter === "unidentified") {
            return shotSplitHelpState(shot) === "unidentified";
          }
          return shotMatchesActionableHelpFilter(shot, filter);
        })
        .sort((a, b) => a.startMs - b.startMs || a.shotNo - b.shotNo || a.shotId - b.shotId);
    }

    async function selectEpisodeEdgeShot(episode: BrowseEpisode, direction: "next" | "prev", options: { play?: boolean } = {}) {
      const expectedShowId = selectedShowIdRef.current;
      const videoId = episode.videoId;
      selectedEpisodeKeyRef.current = episode.key;
      selectedVideoIdRef.current = videoId;
      setSelectedEpisodeKey(episode.key);
      setSelectedVideoId(videoId);

      let sourceShots = shotsRef.current;
      if (!loadedShotVideoIdsRef.current.includes(videoId)) {
        setLoadingShotVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
        try {
          const payload = await fetchVideoShots(videoId);
          if (
            selectedShowIdRef.current !== expectedShowId ||
            selectedEpisodeKeyRef.current !== episode.key ||
            selectedVideoIdRef.current !== videoId ||
            !currentVideoIdsRef.current.includes(videoId)
          ) {
            return;
          }
          sourceShots = payload.shots;
          setShots((current) => [
            ...current.filter((shot) => shot.videoId !== videoId),
            ...payload.shots
          ]);
          shotsRef.current = [
            ...shotsRef.current.filter((shot) => shot.videoId !== videoId),
            ...payload.shots
          ];
          setLoadedShotVideoIds((current) => {
            const next = current.includes(videoId) ? current : [...current, videoId];
            loadedShotVideoIdsRef.current = next;
            return next;
          });
        } catch (error) {
          pushToast("error", error instanceof Error ? error.message : "episode shots load failed");
          return;
        } finally {
          setLoadingShotVideoIds((current) => current.filter((id) => id !== videoId));
        }
      }

      const episodeShots = visibleShotsForEpisode(episode, sourceShots);
      const targetShot = direction === "next" ? episodeShots[0] : episodeShots[episodeShots.length - 1];
      if (!targetShot) {
        setSelectedShotKeys([]);
        setLastSelectedShotKey(null);
        return;
      }
      selectShotByKeyboard(targetShot, options);
    }

    function handleShotTab(direction: "next" | "prev", options: { play?: boolean } = {}) {
      const currentShot = selectedShotRef.current;
      if (!currentShot) {
        return false;
      }

      const currentShots = filteredShotsRef.current;
      const currentIndex = currentShots.findIndex((shot) => shot.key === currentShot.key);
      if (direction === "next" && currentIndex >= 0 && currentIndex < currentShots.length - 1) {
        selectShotByKeyboard(currentShots[currentIndex + 1], options);
        return true;
      }
      if (direction === "prev" && currentIndex > 0) {
        selectShotByKeyboard(currentShots[currentIndex - 1], options);
        return true;
      }

      const currentEpisodes = visibleEpisodesRef.current;
      const currentEpisodeIndex = currentEpisodes.findIndex((episode) =>
        episode.key === selectedEpisodeKeyRef.current || episodeContainsShot(episode, currentShot)
      );
      if (currentEpisodeIndex < 0) {
        return false;
      }
      const targetEpisode = currentEpisodes[currentEpisodeIndex + (direction === "next" ? 1 : -1)];
      if (!targetEpisode) {
        return false;
      }
      void selectEpisodeEdgeShot(targetEpisode, direction, options);
      return true;
    }

    function handleHorizontalShotSelection(direction: HorizontalShotSelectionDirection, extend: boolean) {
      const currentShots = filteredShotsRef.current;
      const selection = resolveHorizontalShotSelection(
        currentShots.map((shot) => shot.key),
        selectedShotsRef.current.map((shot) => shot.key),
        direction,
        extend
      );
      if (!selection) {
        return false;
      }
      const targetShot = currentShots.find((shot) => shot.key === selection.focusKey);
      if (!targetShot) {
        return false;
      }
      if (!extend) {
        selectShotByKeyboard(targetShot, { play: true });
        return true;
      }

      const selectedKeySet = new Set(selection.selectedKeys);
      const nextSelectedShots = currentShots.filter((shot) => selectedKeySet.has(shot.key));
      setSelectedShotKeys(selection.selectedKeys);
      selectedShotsRef.current = nextSelectedShots;
      selectedShotRef.current = nextSelectedShots.length === 1 ? nextSelectedShots[0] : null;
      if (!lastSelectedShotKeyRef.current) {
        setLastSelectedShotKey(targetShot.key);
        lastSelectedShotKeyRef.current = targetShot.key;
      }
      publishLastBrowseShot(targetShot);
      void refreshShotVideoAssets(targetShot.videoId);
      focusShotTileSoon(targetShot.key);
      return true;
    }

    function handleShotArrowNext(extend = false) {
      if (extend || selectedShotsRef.current.length > 1) {
        return handleHorizontalShotSelection("next", extend);
      }
      const currentShot = selectedShotRef.current;
      if (!currentShot) {
        const firstShot = filteredShotsRef.current[0];
        if (!firstShot) {
          return false;
        }
        selectShotByKeyboard(firstShot, { play: true });
        return true;
      }
      return handleShotTab("next", { play: true });
    }

    function handleShotArrowPrev(extend = false) {
      if (extend || selectedShotsRef.current.length > 1) {
        return handleHorizontalShotSelection("prev", extend);
      }
      const currentShot = selectedShotRef.current;
      if (!currentShot) {
        const lastShot = filteredShotsRef.current[filteredShotsRef.current.length - 1];
        if (!lastShot) {
          return false;
        }
        selectShotByKeyboard(lastShot, { play: true });
        return true;
      }
      return handleShotTab("prev", { play: true });
    }

    function scrollShotRowIntoView(row: Extract<ShotGridVirtualRow, { type: "shots" }>) {
      const grid = shotGridRef.current;
      if (!grid) {
        return;
      }
      const rowOffset = shotGridRowOffset(grid);
      const rowTop = rowOffset + row.top;
      const rowBottom = rowTop + row.height;
      const viewportTop = grid.scrollTop;
      const viewportBottom = viewportTop + grid.clientHeight;
      const maxTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
      if (rowTop < viewportTop + 1) {
        grid.scrollTop = Math.max(0, rowTop);
      } else if (rowBottom > viewportBottom - 1) {
        grid.scrollTop = Math.min(maxTop, Math.max(0, rowBottom - grid.clientHeight));
      }
    }

    function handleShotArrowVertical(direction: "up" | "down") {
      const currentShots = filteredShotsRef.current;
      if (currentShots.length === 0) {
        return false;
      }

      const grid = shotGridRef.current;
      const metrics = shotGridMetrics(grid?.clientWidth || 0, isMobileShotsRef.current);
      const shotRows = buildShotGridRows(currentShots, metrics.columns, metrics.tileSize)
        .rows
        .filter((row): row is Extract<ShotGridVirtualRow, { type: "shots" }> => row.type === "shots");
      if (shotRows.length === 0) {
        return false;
      }

      const currentShot = selectedShotRef.current;
      if (!currentShot) {
        const targetRow = direction === "down" ? shotRows[0] : shotRows[shotRows.length - 1];
        const targetShot = direction === "down" ? targetRow.shots[0] : targetRow.shots[targetRow.shots.length - 1];
        if (!targetShot) {
          return false;
        }
        scrollShotRowIntoView(targetRow);
        selectShotByKeyboard(targetShot, { play: true });
        return true;
      }

      const currentRowIndex = shotRows.findIndex((row) => row.shots.some((shot) => shot.key === currentShot.key));
      if (currentRowIndex < 0) {
        return false;
      }
      const currentShotIndex = shotRows[currentRowIndex].shots.findIndex((shot) => shot.key === currentShot.key);
      if (currentShotIndex < 0) {
        return false;
      }
      const targetRow = shotRows[currentRowIndex + (direction === "down" ? 1 : -1)];
      if (!targetRow) {
        return false;
      }
      const targetShot = targetRow.shots[Math.min(currentShotIndex, targetRow.shots.length - 1)];
      if (!targetShot) {
        return false;
      }
      scrollShotRowIntoView(targetRow);
      selectShotByKeyboard(targetShot, { play: true });
      return true;
    }

    function isShotGridKeyboardTarget(target: EventTarget | null) {
      if (!(target instanceof HTMLElement)) {
        return true;
      }
      if (target === document.body || target === document.documentElement) {
        return true;
      }
      return target.closest(".browseShotGrid, .browseShotTile") !== null;
    }

    function handleKeyDown(event: KeyboardEvent) {
      const isSpace = event.code === "Space" || event.key === " ";
      const isHelpJump = event.key === "Tab";
      const isDismissPreview = event.key === "Escape";
      const isSceneToggle = !event.shiftKey && (event.code === "Slash" || event.key === "/");
      const isDeleteToggle = event.code === "Backspace" || event.key === "Backspace" || event.code === "Delete" || event.key === "Delete";
      const isMergeShortcut = !event.shiftKey && (event.code === "KeyM" || event.key.toLowerCase() === "m");
      const isNextShotShortcut = event.key === "ArrowRight";
      const isPrevShotShortcut = event.key === "ArrowLeft";
      const isDownShotShortcut = !event.shiftKey && event.key === "ArrowDown";
      const isUpShotShortcut = !event.shiftKey && event.key === "ArrowUp";
      const isMultiGoodShortcut = !event.shiftKey && (event.code === "KeyG" || event.key.toLowerCase() === "g");
      if (!isSpace && !isHelpJump && !isDismissPreview && !isSceneToggle && !isDeleteToggle && !isMergeShortcut && !isNextShotShortcut && !isPrevShotShortcut && !isDownShotShortcut && !isUpShotShortcut && !isMultiGoodShortcut) {
        return;
      }
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) {
        return;
      }
      if (isTextEditingTarget(event.target)) {
        return;
      }
      if (isDismissPreview) {
        // lightbox가 열려 있으면 lightbox 닫기를 우선한다.
        if (lightboxImageRef.current) {
          return;
        }
        tabNavModeRef.current = false;
        if (selectedShotsRef.current.length > 0) {
          // 프리뷰 닫기: 선택을 해제하면 오른쪽 패널은 작품 진입 시와 같은 ShowInfoPanel로 돌아간다.
          event.preventDefault();
          event.stopPropagation();
          setSelectedShotKeys([]);
          setLastSelectedShotKey(null);
        }
        return;
      }
      if (isHelpJump) {
        if (!isShotGridKeyboardTarget(event.target)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        scrollShotListHelp(event.shiftKey ? "prev" : "next");
        return;
      }
      if (isNextShotShortcut || isPrevShotShortcut || isDownShotShortcut || isUpShotShortcut) {
        // 화살표 이동은 인접 샷 탐색이므로 탭 이동 모드를 해제한다.
        tabNavModeRef.current = false;
      }
      if (isNextShotShortcut) {
        if (!handleShotArrowNext(event.shiftKey)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isPrevShotShortcut) {
        if (!handleShotArrowPrev(event.shiftKey)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isDownShotShortcut || isUpShotShortcut) {
        if (!handleShotArrowVertical(isDownShotShortcut ? "down" : "up")) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const shortcutShots = selectedShotsRef.current;
      const canDelete = isDeleteToggle && shortcutShots.length > 0;
      const canMerge =
        isMergeShortcut &&
        shortcutShots.length > 1 &&
        shortcutShots.every((shot, index) => index === 0 || shot.shotNo === shortcutShots[index - 1].shotNo + 1);
      const canMarkGood = isMultiGoodShortcut && shortcutShots.length > 1;
      if (isDeleteToggle && !canDelete) {
        return;
      }
      if (isMergeShortcut && !canMerge) {
        return;
      }
      if (isMultiGoodShortcut && !canMarkGood) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (canDelete) {
        void runMultiActionRef.current?.("delete", shortcutShots);
      } else if (canMerge) {
        void runMultiActionRef.current?.("merge", shortcutShots);
      } else if (canMarkGood) {
        void markSelectedShotsGoodRef.current?.(shortcutShots);
      } else if (isSceneToggle) {
        const shot = selectedShotRef.current;
        if (shot && pendingSceneShotKeyRef.current !== shot.key) {
          void toggleSceneStart(shot);
        }
      } else {
        scrollShotListPage(event.shiftKey ? "prev" : "next");
      }
    }

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  useEffect(() => {
    // 작품/에피소드가 바뀌면 탭 이동 모드를 해제한다.
    tabNavModeRef.current = false;
  }, [selectedShowId, selectedVideoId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const showId = selectedShowId;
      void fetchShowListSection(showId)
        .then((payload) => {
          setShows(payload.shows);
          if (showId && payload.selectedShow && selectedShowIdRef.current === showId) {
            setSelectedShow(payload.selectedShow);
          }
        })
        .catch(() => {
          // Polling is best-effort; visible data stays as-is on transient failures.
        });
    }, 15000);
    return () => window.clearInterval(timer);
  }, [selectedShowId]);

  useEffect(() => {
    const showId = selectedShow?.id;
    const videoId = selectedVideoId;
    if (!showId || videoId === null) {
      return;
    }
    let cancelled = false;
    setSplitCorrectionStatusLoadingVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
    void fetchSplitCorrectionStatus(showId, videoId)
      .then((status) => {
        if (cancelled || selectedShowIdRef.current !== showId || selectedVideoIdRef.current !== videoId) {
          return;
        }
        setSplitCorrectionStatuses((current) => ({
          ...current,
          [videoId]: status
        }));
        const activeJobId = status.correctionActive ? Number(status.correctionJob?.id || 0) : 0;
        if (activeJobId > 0) {
          setSplitCorrectionWatchJobIds((current) => ({ ...current, [videoId]: activeJobId }));
        }
        setSplitCorrectionStatusLoadingVideoIds((current) => current.filter((id) => id !== videoId));
      })
      .catch(() => {
        // 상태를 확인하지 못했으면 중복 Apply를 막기 위해 Checking 상태를 유지한다.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedShow?.id, selectedVideoId]);

  useEffect(() => {
    const showId = selectedShow?.id;
    const videoId = selectedVideoId;
    if (!showId || videoId === null || selectedVideoCorrectionCount === 0) return;
    let cancelled = false;
    void fetchSplitCorrectionPreview(showId, videoId)
      .then((preview) => {
        if (cancelled || selectedShowIdRef.current !== showId || selectedVideoIdRef.current !== videoId) return;
        setSplitCorrectionPreviews((current) => ({ ...current, [videoId]: preview }));
      })
      .catch(() => {
        // 상세 개수는 늦게 도착해도 status 기반 실행 잠금에는 영향을 주지 않는다.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedShow?.id, selectedVideoCorrectionCount, selectedVideoId]);

  useEffect(() => {
    const showId = selectedShow?.id;
    const videoId = selectedVideoId;
    if (!showId || videoId === null) {
      return;
    }
    const targetShowId = showId;
    const targetVideoId = videoId;
    let cancelled = false;
    async function pollLearningStatus() {
      try {
        const status = await fetchSplitLearningStatus(targetShowId, targetVideoId);
        if (!cancelled && selectedShowIdRef.current === targetShowId) {
          setSplitLearningStatus(status);
        }
      } catch {
        // 다음 poll에서 복구한다.
      }
    }
    void pollLearningStatus();
    if (!trainingGbdt && !splitLearningActive) {
      return () => {
        cancelled = true;
      };
    }
    const timer = window.setInterval(() => void pollLearningStatus(), 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [selectedShow?.id, selectedVideoId, splitLearningActive, trainingGbdt]);

  useEffect(() => {
    const showId = selectedShow?.id;
    const videoId = selectedVideoId;
    if (!showId || videoId === null || (!applyingSplitCorrection && !selectedVideoCorrectionRunning)) {
      return;
    }
    const targetShowId = showId;
    const targetVideoId = videoId;
    let cancelled = false;
    let wasActive = selectedVideoCorrectionRunning;
    async function pollSplitCorrectionStatus() {
      try {
        const status = await fetchSplitCorrectionStatus(targetShowId, targetVideoId);
        if (cancelled || selectedShowIdRef.current !== targetShowId || selectedVideoIdRef.current !== targetVideoId) {
          return;
        }
        setSplitCorrectionStatuses((current) => ({
          ...current,
          [targetVideoId]: status
        }));
        if (wasActive && !status.correctionActive) {
          await Promise.all([
            refreshShowSection(targetShowId),
            refreshSplitCorrectionUiState(targetShowId, targetVideoId)
          ]);
          if (cancelled || selectedShowIdRef.current !== targetShowId || selectedVideoIdRef.current !== targetVideoId) {
            return;
          }
          setSplitCorrectionWatchJobIds((current) => {
            if (!current[targetVideoId]) {
              return current;
            }
            const next = { ...current };
            delete next[targetVideoId];
            return next;
          });
          void fetchShowListSection(targetShowId)
            .then((payload) => {
              setShows(payload.shows);
              if (payload.selectedShow && selectedShowIdRef.current === targetShowId) {
                setSelectedShow(payload.selectedShow);
              }
            })
            .catch(() => {
              // The row refresh above is enough for button state.
            });
        }
        wasActive = Boolean(status.correctionActive);
      } catch {
        // Keep the current busy state on transient status failures.
      }
    }
    void pollSplitCorrectionStatus();
    const timer = window.setInterval(() => {
      void pollSplitCorrectionStatus();
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [applyingSplitCorrection, selectedShow?.id, selectedVideoCorrectionRunning, selectedVideoId]);

  useEffect(() => {
    function handlePopState(event: PopStateEvent) {
      if (isMobileShotsRef.current) {
        const state = event.state as MobileStageHistoryState | null;
        const stage = isMobileShotStage(state?.story37MobileStage)
          ? state.story37MobileStage
          : defaultMobileStageForPath(window.location.pathname);
        mobileStageRef.current = stage;
        setMobileStage(stage);
      }
      const match = window.location.pathname.match(/^\/shots\/(\d+)$/);
      const nextShowId = match ? Number(match[1]) : selectedShowIdRef.current;
      if (nextShowId && nextShowId !== selectedShowIdRef.current) {
        void selectShow(nextShowId, false);
      }
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectedShowId, shows]);

  useEffect(() => {
    if (!selectedShowId || visibleEpisodes.length === 0) {
      selectedEpisodeKeyRef.current = null;
      setSelectedEpisodeKey(null);
      selectedVideoIdRef.current = null;
      setSelectedVideoId(null);
      setSelectedShotKeys([]);
      setLastSelectedShotKey(null);
      setLoadedEpisodeKey(episodeStorageKey);
      return;
    }

    const sameShowAlreadyLoaded = loadedEpisodeKey === episodeStorageKey;
    const selectedEpisodeStillExists =
      selectedEpisodeKey !== null && visibleEpisodes.some((episode) => episode.key === selectedEpisodeKey);
    if (sameShowAlreadyLoaded && selectedEpisodeStillExists) {
      return;
    }

    const fallbackEpisode = firstEpisodeWithShots(visibleEpisodes, shots);
    let nextEpisode = fallbackEpisode;
    const stored = storedEpisodeSelection(selectedShowId);
    if (stored.episodeKey) {
      nextEpisode = visibleEpisodes.find((episode) => episode.key === stored.episodeKey) || nextEpisode;
    } else if (stored.videoId !== null) {
      nextEpisode = episodeForVideo(visibleEpisodes, stored.videoId) || nextEpisode;
    }
    const episodeChanged = nextEpisode?.key !== selectedEpisodeKey;
    selectedEpisodeKeyRef.current = nextEpisode?.key ?? null;
    setSelectedEpisodeKey(nextEpisode?.key ?? null);
    selectedVideoIdRef.current = nextEpisode?.videoId ?? null;
    setSelectedVideoId(nextEpisode?.videoId ?? null);
    if (!sameShowAlreadyLoaded || episodeChanged) {
      setSelectedShotKeys([]);
      setLastSelectedShotKey(null);
    }
    setLoadedEpisodeKey(episodeStorageKey);
  }, [episodeStorageKey, loadedEpisodeKey, selectedEpisodeKey, selectedShowId, shots, visibleEpisodes]);

  useEffect(() => {
    if (loadedEpisodeKey !== episodeStorageKey) {
      return;
    }
    if (!selectedShowId || !selectedEpisode) {
      return;
    }
    if (!visibleEpisodes.some((episode) => episode.key === selectedEpisode.key)) {
      return;
    }
    try {
      localStorage.setItem(
        episodeStorageKey,
        JSON.stringify({ episodeKey: selectedEpisode.key, videoId: selectedEpisode.videoId })
      );
    } catch {
      // Episode persistence is optional.
    }
  }, [episodeStorageKey, loadedEpisodeKey, selectedEpisode, selectedShowId, visibleEpisodes]);

  useEffect(() => {
    if (selectedVideoId === null) {
      return;
    }
    if (!visibleVideos.some((video) => video.id === selectedVideoId)) {
      return;
    }
    if (selectedShowId && initialShotShowIdRef.current === selectedShowId) {
      return;
    }
    if (loadedShotVideoIds.includes(selectedVideoId) || loadingShotVideoIds.includes(selectedVideoId)) {
      return;
    }

    const videoId = selectedVideoId;
    setLoadingShotVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
    void fetchVideoShots(videoId, false)
      .then((payload) => {
        if (selectedVideoIdRef.current !== videoId || !currentVideoIdsRef.current.includes(videoId)) {
          return;
        }
        setShots((current) => [
          ...current.filter((shot) => shot.videoId !== videoId),
          ...payload.shots
        ]);
        setLoadedShotVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
        scheduleShotDetailsLoad(videoId, selectedShowId);
      })
      .catch(() => {
        // The empty state already reflects that no ready shots are available.
      })
      .finally(() => {
        setLoadingShotVideoIds((current) => current.filter((id) => id !== videoId));
      });
  }, [loadedShotVideoIds, loadingShotVideoIds, selectedShowId, selectedVideoId, visibleVideos]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(layoutStorageKey);
      let nextLeft = 20;
      let nextMid = 40;
      if (stored) {
        const parsed = JSON.parse(stored) as { leftPct?: number; midPct?: number };
        const parsedLeft = clamp(Number(parsed.leftPct), 14, 36);
        const parsedMid = clamp(Number(parsed.midPct), 22, Math.min(64, 78 - parsedLeft));
        if (Number.isFinite(parsedLeft) && Number.isFinite(parsedMid)) {
          nextLeft = parsedLeft;
          nextMid = parsedMid;
        }
      }
      setLeftPct(nextLeft);
      setMidPct(nextMid);
    } catch {
      // Ignore invalid stored layout and keep defaults.
    } finally {
      setLoadedLayoutKey(layoutStorageKey);
    }
  }, [layoutStorageKey]);

  useEffect(() => {
    if (loadedLayoutKey !== layoutStorageKey) {
      return;
    }
    try {
      localStorage.setItem(
        layoutStorageKey,
        JSON.stringify({
          leftPct: Math.round(leftPct * 100) / 100,
          midPct: Math.round(midPct * 100) / 100
        })
      );
    } catch {
      // Layout persistence is optional.
    }
  }, [layoutStorageKey, loadedLayoutKey, leftPct, midPct]);

  async function fetchShowSection(showId: number, includeMotionStatus = true) {
    const response = await fetch(`/api/browse-show/${showId}${includeMotionStatus ? "" : "?motion=0"}`);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<BrowseData>;
  }

  async function fetchShowInitialShots(
    showId: number,
    preferredVideoId: number | null,
    includeDetails = true
  ) {
    const query = new URLSearchParams();
    if (preferredVideoId) {
      query.set("videoId", String(preferredVideoId));
    }
    if (!includeDetails) {
      query.set("details", "0");
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    const response = await fetch(`/api/browse-show-shots/${showId}${suffix}`);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const payload = await response.json() as BrowseShotFetchPayload;
    return {
      ...payload,
      video: payload.video || null,
      shots: expandBrowseShotPayload(payload),
      requestedShowFound: Boolean(payload.requestedShowFound)
    };
  }

  async function fetchVideoShots(videoId: number, includeDetails = true) {
    const response = await fetch(`/api/browse-video-shots/${videoId}${includeDetails ? "" : "?details=0"}`);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const payload = await response.json() as BrowseShotFetchPayload;
    return { shots: expandBrowseShotPayload(payload) };
  }

  async function fetchVideoShotState(videoId: number) {
    const response = await fetch(`/api/browse-video-shots/${videoId}?details=state`);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const payload = await response.json() as { helpRows?: BrowseShotHelpCompactRow[] };
    return expandBrowseShotHelpRows(payload.helpRows);
  }

  function scheduleShotDetailsLoad(videoId: number, expectedShowId: number) {
    const requestId = (shotDetailsRequestIdRef.current.get(videoId) || 0) + 1;
    shotDetailsRequestIdRef.current.set(videoId, requestId);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const loadFullDetails = () => {
          window.setTimeout(() => {
            void fetchVideoShots(videoId)
              .then((payload) => {
                if (
                  shotDetailsRequestIdRef.current.get(videoId) !== requestId ||
                  selectedShowIdRef.current !== expectedShowId
                ) {
                  return;
                }
                setShots((current) => [
                  ...current.filter((shot) => shot.videoId !== videoId),
                  ...payload.shots
                ]);
              })
              .catch(() => {
                // 상세 motion·자막은 다음 선택이나 refresh에서 다시 받을 수 있다.
              });
          }, 1500);
        };
        void fetchVideoShotState(videoId)
          .then((helpRequestsByShotId) => {
            if (
              shotDetailsRequestIdRef.current.get(videoId) !== requestId ||
              selectedShowIdRef.current !== expectedShowId
            ) {
              return;
            }
            setShots((current) => current.map((shot) =>
              shot.videoId === videoId
                ? { ...shot, helpRequests: helpRequestsByShotId.get(shot.shotId) || [] }
                : shot
            ));
          })
          .catch(() => {
            // 기본 썸네일 목록은 이미 사용할 수 있으므로 state 실패만으로 화면을 비우지 않는다.
          })
          .finally(loadFullDetails);
      });
    });
  }

  async function refreshShotVideoAssets(videoId: number, force = false) {
    const showId = selectedShowIdRef.current;
    if (!showId || !currentVideoIdsRef.current.includes(videoId)) {
      return;
    }
    const now = Date.now();
    const lastRefresh = shotAssetRefreshTsRef.current.get(videoId) || 0;
    if (!force && now - lastRefresh < 10_000) {
      return;
    }
    if (shotAssetRefreshInFlightRef.current.has(videoId)) {
      return;
    }
    shotAssetRefreshTsRef.current.set(videoId, now);
    shotAssetRefreshInFlightRef.current.add(videoId);
    try {
      const payload = await fetchVideoShots(videoId);
      if (selectedShowIdRef.current !== showId || !currentVideoIdsRef.current.includes(videoId)) {
        return;
      }
      setShots((current) => [
        ...current.filter((shot) => shot.videoId !== videoId),
        ...payload.shots
      ]);
      setLoadedShotVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
    } catch {
      // Asset refresh is opportunistic; the current shot list remains usable on transient failures.
    } finally {
      shotAssetRefreshInFlightRef.current.delete(videoId);
    }
  }

  async function fetchShowListSection(showId = selectedShowId) {
    const query = showId ? `?selectedShowId=${showId}` : "";
    const response = await fetch(`/api/browse-shows${query}`);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<{
      shows: BrowseShow[];
      selectedShow: BrowseShow | null;
      requestedShowFound: boolean;
    }>;
  }

  async function fetchShotEditJobDetails(jobIds: number[]) {
    if (jobIds.length === 0) {
      return [];
    }
    const response = await fetch(`/api/shot-edit-jobs?ids=${jobIds.join(",")}`);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const payload = (await response.json()) as { jobs?: ShotEditJobDetail[] };
    return payload.jobs || [];
  }

  async function fetchSplitCorrectionStatus(showId: number, videoId: number) {
    const response = await fetch(`/api/shot-split-correction/apply?showId=${showId}&videoId=${videoId}`, {
      cache: "no-store"
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const payload = (await response.json()) as SplitCorrectionStatus & { ok?: boolean };
    return payload;
  }

  async function fetchSplitCorrectionPreview(showId: number, videoId: number) {
    return fetchJsonWithTimeout<SplitCorrectionPreview>("/api/shot-split-correction/apply", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ showId, videoId, dryRun: true })
    });
  }

  async function refreshSplitCorrectionUiState(showId: number, videoId: number) {
    const [helpRequestsByShotId, preview] = await Promise.all([
      fetchVideoShotState(videoId),
      fetchSplitCorrectionPreview(showId, videoId)
    ]);
    if (selectedShowIdRef.current !== showId || selectedVideoIdRef.current !== videoId) {
      return;
    }
    setShots((current) => current.map((shot) =>
      shot.videoId === videoId
        ? { ...shot, helpRequests: helpRequestsByShotId.get(shot.shotId) || [] }
        : shot
    ));
    setSplitCorrectionPreviews((current) => ({
      ...current,
      [videoId]: preview
    }));
  }

  async function fetchSplitLearningStatus(showId: number, videoId: number) {
    const response = await fetch(`/api/shot-split-learning/train?showId=${showId}&videoId=${videoId}`, {
      cache: "no-store"
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<SplitCorrectionStatus & { ok?: boolean }>;
  }

  function pushToast(kind: BrowseToast["kind"], text: string) {
    const id = toastIdRef.current + 1;
    toastIdRef.current = id;
    setToasts((current) => [...current, { id, kind, text }].slice(-5));
    if (kind === "error") {
      return;
    }
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 7000);
  }

  function dismissToast(id: number) {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }

  function upsertActiveShotEditJob(result: {
    id?: number;
    action?: "split" | "merge";
    videoId?: number;
    shotId?: number;
    shotIds?: number[];
    status?: number | "pending" | "running";
    progressMsg?: string | null;
  }) {
    const id = Number(result.id || 0);
    const videoId = Number(result.videoId || 0);
    const shotId = Number(result.shotId || result.shotIds?.[0] || 0);
    if (!id || !result.action || !videoId || !shotId) {
      return;
    }
    const status =
      result.status === 1 || result.status === "running" ? "running" : "pending";
    const job: BrowseShotEditJob = {
      id,
      action: result.action,
      videoId,
      shotId,
      shotIds: result.shotIds && result.shotIds.length > 0 ? result.shotIds : [shotId],
      status,
      progressMsg: result.progressMsg || null
    };
    setShotEditJobs((current) => {
      const without = current.filter((item) => item.id !== id);
      return [...without, job].sort((left, right) => left.id - right.id);
    });
  }

  function shotEditToastText(job: ShotEditJobDetail | BrowseShotEditJob) {
    if (job.status === "failed") {
      return `${job.action} failed: ${"lastError" in job && job.lastError ? job.lastError : job.progressMsg || "unknown error"}`;
    }
    const result = "resultJson" in job ? job.resultJson : null;
    if (job.action === "split") {
      if (result && result.changed === false) {
        const detectAttempts = result.detect_attempts;
        const attemptCount = Number(result.attempt_count || (Array.isArray(detectAttempts) ? detectAttempts.length : 0));
        return attemptCount > 0
          ? `split shot ${job.shotId}: no cut found after ${attemptCount} sensitive passes`
          : `split shot ${job.shotId}: no cut found`;
      }
      const created = Number(result?.created || 0);
      const usedProfile = typeof result?.used_profile === "string" ? ` (${result.used_profile})` : "";
      return created > 0 ? `split shot ${job.shotId}: created ${created} shots${usedProfile}` : `split shot ${job.shotId}: done`;
    }
    const count = job.shotIds.length;
    return count > 1 ? `merge done: ${count} shots` : "merge done";
  }

  function applyShowSection(payload: BrowseData, expectedShowId?: number) {
    const payloadShowId = payload.selectedShow?.id || 0;
    if (
      expectedShowId !== undefined &&
      (payloadShowId !== expectedShowId || selectedShowIdRef.current !== expectedShowId)
    ) {
      return false;
    }
    if (payload.shows.length > 0) {
      setShows(payload.shows);
    }
    setVideos(payload.videos);
    setEpisodes(payload.episodes);
    setShotEditJobs(payload.shotEditJobs);
    setMotionStatus(payload.motionStatus);
    setSelectedShow(payload.selectedShow);
    return true;
  }

  useEffect(() => {
    if (!selectedShowId || !motionProgressShouldPoll(motionStatus)) {
      return;
    }
    let inFlight = false;
    const timer = window.setInterval(() => {
      if (inFlight) {
        return;
      }
      inFlight = true;
      const showId = selectedShowId;
      void fetchShowSection(showId)
        .then((payload) => {
          const applied = applyShowSection(payload, showId);
          if (applied && !motionProgressShouldPoll(payload.motionStatus)) {
            const videoId = selectedVideoIdRef.current;
            if (videoId) {
              // 진행 중에는 가벼운 상태만 갱신하고, 완료 시 현재 영상만 한 번 새로 받는다.
              void refreshShotVideoAssets(videoId, true);
            }
          }
        })
        .catch(() => {
          // The worker pipeline can outlive this page; the next poll or navigation will refresh it.
        })
        .finally(() => { inFlight = false; });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [motionStatus, selectedShowId]);

  function updateVideoEpisodeText(videoId: number, title: string | null, summary: string | null) {
    setVideos((current) =>
      current.map((video) => (video.id === videoId ? { ...video, title, summary } : video))
    );
  }

  function updateVideoDeleted(videoId: number, deleted: boolean) {
    setVideos((current) =>
      current.map((video) => (video.id === videoId ? { ...video, deleted } : video))
    );
  }

  function updateShowText(showId: number, title: string, summary: string | null) {
    setShows((current) =>
      current.map((show) => (show.id === showId ? { ...show, title, summary } : show))
    );
    setSelectedShow((current) => (current?.id === showId ? { ...current, title, summary } : current));
  }

  function updateShowWikiUrl(showId: number, wikiUrl: string | null) {
    setShows((current) =>
      current.map((show) => (show.id === showId ? { ...show, wikiUrl } : show))
    );
    setSelectedShow((current) => (current?.id === showId ? { ...current, wikiUrl } : current));
  }

  function updateShowMeta(show: BrowseShow) {
    setShows((current) => current.map((item) => (item.id === show.id ? show : item)));
    setSelectedShow((current) => (current?.id === show.id ? show : current));
  }

  function scrollPreviewTop() {
    if (previewPaneRef.current) {
      previewPaneRef.current.scrollTop = 0;
    }
  }

  function scrollPreviewBottom() {
    const scroll = () => {
      if (previewPaneRef.current) {
        previewPaneRef.current.scrollTop = previewPaneRef.current.scrollHeight;
      }
    };
    scroll();
    window.requestAnimationFrame(() => window.requestAnimationFrame(scroll));
  }

  function scrollPreviewForShow(show: BrowseShow | null) {
    if (show?.status === "processing") {
      scrollPreviewBottom();
      return;
    }
    scrollPreviewTop();
  }

  function centerShowRow(showId: number) {
    const table = showTableRef.current;
    if (!table) {
      return;
    }
    const row = table.querySelector<HTMLElement>(`[data-show-id="${showId}"]`);
    row?.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" });
  }

  function centerShowRowSoon(showId: number) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => centerShowRow(showId));
    });
  }

  async function refreshShowSection(showId = selectedShowId) {
    if (!showId) {
      return null;
    }
    const payload = await fetchShowSection(showId);
    if (!applyShowSection(payload, showId)) {
      return null;
    }
    return payload;
  }

  async function retryVideo(videoId: number) {
    const showId = selectedShowIdRef.current;
    setRetryingVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
    setShowLoadError("");
    try {
      const response = await fetch(`/api/video-retry/${videoId}`, { method: "POST" });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      if (showId && selectedShowIdRef.current === showId) {
        await refreshShowSection(showId);
        void fetchShowListSection(showId)
          .then((payload) => {
            setShows(payload.shows);
            if (payload.selectedShow && selectedShowIdRef.current === showId) {
              setSelectedShow(payload.selectedShow);
            }
          })
          .catch(() => {
            // The row refresh above is enough for retry feedback.
          });
      }
    } catch (error) {
      setShowLoadError(error instanceof Error ? error.message : "video retry failed");
    } finally {
      setRetryingVideoIds((current) => current.filter((id) => id !== videoId));
    }
  }

  async function resplitShow(showId: number) {
    if (!showId) {
      return;
    }
    const confirmed = window.confirm(
      "Re-split this show with the latest detection algorithm?\n\n" +
        "This queues every episode that is not already split with the current model revision. " +
        "Episodes already queued or running, and episodes already done on the current revision, are left untouched.\n" +
        "For an isolated bad cut, cancel and select the shot, then use re-split this shot.\n" +
        "Manual scene starts, logical episode starts, and soft-deleted shots are remapped by time; motion analysis and embeddings must be regenerated.\n\n" +
        "Continue?"
    );
    if (!confirmed) {
      return;
    }
    setResplittingShowId(showId);
    setShowLoadError("");
    try {
      const response = await fetch(`/api/show-resplit/${showId}`, { method: "POST" });
      const result = (await response.json().catch(() => null)) as
        | {
            ok?: boolean;
            queued?: number[];
            skippedActive?: number[];
            skippedUpToDate?: number[];
            failed?: Array<{ videoId: number; error: string }>;
            error?: string;
          }
        | null;
      if (!response.ok || !result) {
        throw new Error(result?.error || (await response.text()) || "re-split failed");
      }
      const queuedCount = result.queued?.length ?? 0;
      const skippedActiveCount = result.skippedActive?.length ?? 0;
      const skippedUpToDateCount = result.skippedUpToDate?.length ?? 0;
      const failedCount = result.failed?.length ?? 0;
      pushToast(
        failedCount > 0 ? "info" : "success",
        failedCount > 0
          ? `re-split queued ${queuedCount}, skipped ${skippedActiveCount} active / ${skippedUpToDateCount} current, ${failedCount} failed`
          : queuedCount > 0
            ? `re-split queued ${queuedCount}, skipped ${skippedActiveCount} active / ${skippedUpToDateCount} already current`
            : `nothing to queue: ${skippedActiveCount} active, ${skippedUpToDateCount} already current`
      );
      if (selectedShowIdRef.current === showId) {
        await refreshShowSection(showId);
        void fetchShowListSection(showId)
          .then((payload) => {
            setShows(payload.shows);
            if (payload.selectedShow && selectedShowIdRef.current === showId) {
              setSelectedShow(payload.selectedShow);
            }
          })
          .catch(() => {
            // Row refresh above is enough for feedback.
          });
      }
    } catch (error) {
      setShowLoadError(error instanceof Error ? error.message : "show re-split failed");
    } finally {
      setResplittingShowId((current) => (current === showId ? null : current));
    }
  }

  async function resplitEpisode(videoId: number) {
    const showId = selectedShowIdRef.current;
    const confirmed = window.confirm(
      "Re-split only this episode with the latest detection algorithm?\n\n" +
        "The source video is re-detected and re-encoded. Manual scene starts and logical episode starts are remapped to the nearest new shot boundaries.\n\n" +
        "Continue?"
    );
    if (!confirmed) {
      return;
    }
    setResplittingVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
    setShowLoadError("");
    try {
      const response = await fetch(`/api/video-resplit/${videoId}`, { method: "POST" });
      const result = (await response.json().catch(() => null)) as
        | { ok?: boolean; videoId?: number; alreadyActive?: boolean; error?: string }
        | null;
      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || "episode re-split failed");
      }
      pushToast(
        result.alreadyActive ? "info" : "success",
        result.alreadyActive
          ? `video ${videoId} is already queued or running`
          : `episode re-split queued for video ${videoId}`
      );
      if (showId && selectedShowIdRef.current === showId) {
        await refreshShowSection(showId);
        void fetchShowListSection(showId)
          .then((payload) => {
            setShows(payload.shows);
            if (payload.selectedShow && selectedShowIdRef.current === showId) {
              setSelectedShow(payload.selectedShow);
            }
          })
          .catch(() => {
            // Episode row refresh already reflects the queued state.
          });
      }
    } catch (error) {
      setShowLoadError(error instanceof Error ? error.message : "episode re-split failed");
    } finally {
      setResplittingVideoIds((current) => current.filter((id) => id !== videoId));
    }
  }

  async function deleteEpisode(videoId: number, deleted = true) {
    const showId = selectedShowIdRef.current;
    setDeletingVideoIds((current) => current.includes(videoId) ? current : [...current, videoId]);
    setShowLoadError("");
    try {
      const response = await fetch(`/api/video-delete/${videoId}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ deleted })
      });
      const result = (await response.json().catch(() => null)) as { video?: BrowseVideo; error?: string } | null;
      if (!response.ok || !result?.video) {
        throw new Error(result?.error || `episode ${deleted ? "delete" : "restore"} failed`);
      }
      updateVideoDeleted(videoId, deleted);
      if (showId && selectedShowIdRef.current === showId) {
        await refreshShowSection(showId);
      }
    } catch (error) {
      setShowLoadError(error instanceof Error ? error.message : `episode ${deleted ? "delete" : "restore"} failed`);
    } finally {
      setDeletingVideoIds((current) => current.filter((id) => id !== videoId));
    }
  }

  function startInitialShotLoad(requestId: number, showId: number, preferredVideoId: number | null) {
    void fetchShowInitialShots(showId, preferredVideoId, false)
      .then((payload) => {
        if (showRequestIdRef.current !== requestId || selectedShowIdRef.current !== showId) {
          return;
        }
        if (preferredVideoId !== null && selectedVideoIdRef.current !== preferredVideoId) {
          initialShotShowIdRef.current = 0;
          setInitialShotLoadShowId(null);
          setLoadingShotVideoIds((current) => current.filter((id) => id !== preferredVideoId));
          return;
        }
        initialShotShowIdRef.current = 0;
        setInitialShotLoadShowId(null);
        setLoadingShotVideoIds([]);
        if (!payload.video) {
          setSelectedEpisodeKey(null);
          selectedEpisodeKeyRef.current = null;
          setSelectedVideoId(null);
          selectedVideoIdRef.current = null;
          setLoadedEpisodeKey(`${EPISODE_STORAGE_KEY}.${showId}`);
          return;
        }
        selectedVideoIdRef.current = payload.video.id;
        setSelectedVideoId(payload.video.id);
        setShots(payload.shots);
        setLoadedShotVideoIds([payload.video.id]);
        scheduleShotDetailsLoad(payload.video.id, showId);
        setSelectedShotKeys([]);
        setLastSelectedShotKey(null);
        setLoadedEpisodeKey(`${EPISODE_STORAGE_KEY}.${showId}`);
      })
      .catch((error) => {
        if (showRequestIdRef.current !== requestId || selectedShowIdRef.current !== showId) {
          return;
        }
        initialShotShowIdRef.current = 0;
        setInitialShotLoadShowId(null);
        setLoadingShotVideoIds([]);
        setShowLoadError(error instanceof Error ? error.message : "shot load failed");
      });
  }

  /// 인덱싱 우선순위 토글(0↔9). 스케줄러가 GREATEST(show, episode) 우선순위 DESC로
  /// 에피소드를 고르므로, 누르면 다음 선택에서 즉시 새치기한다.
  async function toggleIndexPriority(payload: { showId?: number; episodeId?: number; key?: string }, current: number) {
    const next = current > 0 ? 0 : 9;
    if (payload.showId) {
      setShows((items) => items.map((item) => (item.id === payload.showId ? { ...item, indexPriority: next } : item)));
    }
    if (payload.key) {
      setEpisodes((items) => items.map((item) => (item.key === payload.key ? { ...item, indexPriority: next } : item)));
    }
    try {
      const response = await fetch("/api/index-priority", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ showId: payload.showId, episodeId: payload.episodeId, priority: next })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      // 실패 시 롤백
      if (payload.showId) {
        setShows((items) => items.map((item) => (item.id === payload.showId ? { ...item, indexPriority: current } : item)));
      }
      if (payload.key) {
        setEpisodes((items) => items.map((item) => (item.key === payload.key ? { ...item, indexPriority: current } : item)));
      }
      window.alert(error instanceof Error ? error.message : "priority update failed");
    }
  }

  async function selectShow(showId: number, pushHistory = true) {
    const requestId = showRequestIdRef.current + 1;
    showRequestIdRef.current = requestId;
    const preferredSelection = storedEpisodeSelection(showId);
    const preferredVideoId = preferredSelection.videoId;
    selectedShowIdRef.current = showId;
    selectedVideoIdRef.current = preferredVideoId;
    selectedEpisodeKeyRef.current = preferredSelection.episodeKey;
    initialShotShowIdRef.current = showId;
    const knownShow = shows.find((show) => show.id === showId) || null;
    scrollPreviewForShow(knownShow);
    setShowLoadingId(showId);
    setInitialShotLoadShowId(showId);
    setShowLoadError("");
    setSelectedShow(knownShow);
    setVideos([]);
    setShotEditJobs([]);
    previousShotEditJobsRef.current = [];
    setMotionStatus(null);
    setShots([]);
    setLoadedShotVideoIds([]);
    setLoadingShotVideoIds(preferredVideoId ? [preferredVideoId] : []);
    setSelectedVideoId(preferredVideoId);
    setSelectedEpisodeKey(preferredSelection.episodeKey);
    setSelectedShotKeys([]);
    setLastSelectedShotKey(null);
    setLoadedEpisodeKey(null);
    if (pushHistory) {
      const mobileStageState = isMobileShotsRef.current ? { story37MobileStage: mobileStageRef.current } : {};
      window.history.pushState({ showId, ...mobileStageState }, "", `/shots/${showId}`);
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => startInitialShotLoad(requestId, showId, preferredVideoId));
    });

    try {
      const payload = await fetchShowSection(showId, false);
      if (showRequestIdRef.current !== requestId || !payload) {
        return;
      }
      applyShowSection(payload, showId);
      const selectableVideoIds = new Set(
        (showDeleted ? payload.videos : payload.videos.filter((video) => !video.deleted)).map((video) => video.id)
      );
      const selectableEpisodes = payload.episodes.filter((episode) => showDeleted || selectableVideoIds.has(episode.videoId));
      const preferredEpisode = preferredSelection.episodeKey
        ? selectableEpisodes.find((episode) => episode.key === preferredSelection.episodeKey) || null
        : null;
      const nextEpisode =
        preferredEpisode ||
        (preferredVideoId ? episodeForVideo(selectableEpisodes, preferredVideoId) : null) ||
        selectableEpisodes[0] ||
        null;
      const nextVideoId = nextEpisode?.videoId ?? null;
      selectedEpisodeKeyRef.current = nextEpisode?.key ?? null;
      setSelectedEpisodeKey(nextEpisode?.key ?? null);
      selectedVideoIdRef.current = nextVideoId;
      setSelectedVideoId(nextVideoId);
      setLoadingShotVideoIds(nextVideoId ? [nextVideoId] : []);
      setLoadedEpisodeKey(`${EPISODE_STORAGE_KEY}.${showId}`);
      scrollPreviewForShow(payload.selectedShow);
      if (nextVideoId === null) {
        initialShotShowIdRef.current = 0;
        setInitialShotLoadShowId(null);
        setShots([]);
        setLoadedShotVideoIds([]);
      }
      void fetch(`/api/show-motion-index/${showId}`)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(await response.text());
          }
          return response.json() as Promise<{ status?: BrowseShowMotionStatus }>;
        })
        .then((result) => {
          if (showRequestIdRef.current === requestId && selectedShowIdRef.current === showId && result.status) {
            setMotionStatus(result.status);
          }
        })
        .catch(() => {
          // Motion status is non-critical for switching shows.
        });
    } catch (error) {
      if (showRequestIdRef.current === requestId) {
        initialShotShowIdRef.current = 0;
        setInitialShotLoadShowId(null);
        setLoadingShotVideoIds([]);
        setShowLoadError(error instanceof Error ? error.message : "show load failed");
      }
    } finally {
      if (showRequestIdRef.current === requestId) {
        setShowLoadingId(null);
      }
    }
  }

  function dragSplitter(splitter: "left" | "right", event: React.PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    const fixedRightPct = clamp(100 - leftPct - midPct, 22, 64);

    const move = (moveEvent: PointerEvent) => {
      const pointerPct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      if (splitter === "left") {
        const nextLeft = clamp(pointerPct, 14, Math.min(36, 100 - fixedRightPct - 22));
        setLeftPct(nextLeft);
        setMidPct(100 - fixedRightPct - nextLeft);
        return;
      }

      const nextLeftAndMid = clamp(pointerPct, leftPct + 22, 78);
      setMidPct(nextLeftAndMid - leftPct);
    };

    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };

    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  }

  function selectShot(shot: BrowseShot, event: React.MouseEvent<HTMLButtonElement>) {
    // 마우스로 임의 샷을 선택하면 탭 이동 모드를 해제한다.
    tabNavModeRef.current = false;
    if (event.shiftKey && lastSelectedShotKey) {
      const anchorIndex = filteredShots.findIndex((item) => item.key === lastSelectedShotKey);
      const shotIndex = filteredShots.findIndex((item) => item.key === shot.key);
      if (anchorIndex >= 0 && shotIndex >= 0) {
        const start = Math.min(anchorIndex, shotIndex);
        const end = Math.max(anchorIndex, shotIndex);
        setSelectedShotKeys(filteredShots.slice(start, end + 1).map((item) => item.key));
        return;
      }
    }
    setSelectedShotKeys([shot.key]);
    setLastSelectedShotKey(shot.key);
    publishLastBrowseShot(shot);
    void refreshShotVideoAssets(shot.videoId);
    setPlayRequestId((current) => current + 1);
    if (isMobileShotsRef.current) {
      goToMobileStage("preview", true);
    }
  }

  function focusShot(shot: BrowseShot) {
    if (selectedShotKeys.length === 1 && selectedShotKeys[0] === shot.key) {
      return;
    }
    setSelectedShotKeys([shot.key]);
    setLastSelectedShotKey(shot.key);
    publishLastBrowseShot(shot);
    void refreshShotVideoAssets(shot.videoId);
  }

  function clearShotSelectionFromGrid(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (target instanceof HTMLElement && target.closest(".browseShotTile")) {
      return;
    }
    if (selectedShotKeys.length > 0) {
      setSelectedShotKeys([]);
      setLastSelectedShotKey(null);
    }
    scrollPreviewForShow(selectedShow);
  }

  async function toggleSceneStart(shot: BrowseShot) {
    setPendingSceneShotKey(shot.key);
    try {
      const result = await fetchJsonWithTimeout<{
        sceneStart: number;
        sceneStarts?: Array<{ shotId: number; sceneStart: number }>;
      }>(`/api/shot-scene-start/${shot.videoId}/${shot.shotId}`, {
        method: "POST"
      });
      const sceneStartMap = new Map(
        (result.sceneStarts || []).map((item) => [Number(item.shotId), Number(item.sceneStart || 0)])
      );
      setShots((current) =>
        current.map((item) => {
          if (item.videoId !== shot.videoId) {
            return item;
          }
          if (result.sceneStarts) {
            return { ...item, sceneStart: sceneStartMap.get(item.shotId) || 0 };
          }
          return item.key === shot.key ? { ...item, sceneStart: Number(result.sceneStart || 0) } : item;
        })
      );
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "scene start update failed");
    } finally {
      setPendingSceneShotKey(null);
    }
  }

  async function setSceneStart(shot: BrowseShot, enabled: boolean) {
    if ((shot.sceneStart > 0) === enabled) {
      return;
    }
    await toggleSceneStart(shot);
  }

  async function updateEpisodeStart(shot: BrowseShot, action: "set" | "unset") {
    if (pendingEpisodeShotKey) {
      return;
    }
    setPendingEpisodeShotKey(shot.key);
    try {
      const result = await fetchJsonWithTimeout<{ episodes?: BrowseEpisode[]; error?: string }>(
        `/api/shot-episode-start/${shot.videoId}/${shot.shotId}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ action })
        }
      );
      const nextEpisodes = result.episodes || [];
      if (nextEpisodes.length === 0) {
        throw new Error("episode update returned no episodes");
      }
      setEpisodes(nextEpisodes);
      const targetEpisode =
        action === "set"
          ? nextEpisodes.find((episode) =>
              episode.videoId === shot.videoId &&
              (episode.startShotId === shot.shotId || episode.startMs === shot.startMs)
            ) || episodeForShot(nextEpisodes, shot)
          : episodeForShot(nextEpisodes, shot);
      if (targetEpisode) {
        selectedEpisodeKeyRef.current = targetEpisode.key;
        setSelectedEpisodeKey(targetEpisode.key);
        selectedVideoIdRef.current = targetEpisode.videoId;
        setSelectedVideoId(targetEpisode.videoId);
        setSelectedShotKeys([shot.key]);
        setLastSelectedShotKey(shot.key);
      }
      setSelectedShow((current) => current ? { ...current, episodeCount: nextEpisodes.length } : current);
      setShows((current) =>
        current.map((show) =>
          show.id === selectedShowIdRef.current ? { ...show, episodeCount: nextEpisodes.length } : show
        )
      );
      pushToast("success", action === "set" ? "episode start set" : "episode start unset");
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "episode boundary update failed");
    } finally {
      setPendingEpisodeShotKey(null);
    }
  }

  async function markIntroOutro(shot: BrowseShot, mode: "intro_end" | "outro_start") {
    if (pendingRangeMark) {
      return;
    }
    setPendingRangeMark({ shotKey: shot.key, mode });
    try {
      const result = await fetchJsonWithTimeout<{
        deletedShotIds?: number[];
        markerShotId?: number | null;
        sceneStarts?: Array<{ shotId: number; sceneStart: number }>;
        error?: string;
      }>("/api/shot-range-mark", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          videoId: shot.videoId,
          shotId: shot.shotId,
          mode
        })
      });

      const deletedShotIds = new Set((result.deletedShotIds || []).map(Number));
      const sceneStartMap = new Map(
        (result.sceneStarts || []).map((item) => [Number(item.shotId), Number(item.sceneStart || 0)])
      );
      setShots((current) =>
        current.map((item) => {
          if (item.videoId !== shot.videoId) {
            return item;
          }
          return {
            ...item,
            flags: deletedShotIds.has(item.shotId) ? item.flags | 1 : item.flags,
            softDeleted: deletedShotIds.has(item.shotId) ? true : item.softDeleted,
            sceneStart: sceneStartMap.get(item.shotId) || 0
          };
        })
      );
      pushToast(
        "success",
        mode === "intro_end"
          ? `intro marked: deleted ${deletedShotIds.size} shots`
          : `outro marked: deleted ${deletedShotIds.size} shots`
      );
      void refreshShowSection().catch((error) => {
        pushToast("error", error instanceof Error ? `refresh failed: ${error.message}` : "refresh failed");
      });
      if (mode === "intro_end") {
        window.requestAnimationFrame(() => {
          if (shotGridRef.current) {
            shotGridRef.current.scrollTop = shotGridRef.current.scrollHeight;
          }
        });
      }
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "shot range mark failed");
    } finally {
      setPendingRangeMark(null);
    }
  }

  async function splitShot(shot: BrowseShot) {
    if (
      pendingSplitShotKeys.includes(shot.key) ||
      shotEditJobs.some(
        (job) => job.action === "split" && (job.shotId === shot.shotId || job.shotIds.includes(shot.shotId))
      )
    ) {
      return;
    }
    setPendingSplitShotKeys((current) => [...current, shot.key]);
    try {
      const result = await fetchJsonWithTimeout<
        | {
            mode?: "video_rejudge";
            markedShotId?: number;
            helpReview?: {
              issueMask?: number;
              helpRequest?: BrowseShotHelpRequest | null;
            } | null;
            encodingJob?: { id?: number } | null;
          }
        | null
      >(`/api/shot-split/${shot.videoId}/${shot.shotId}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          observedHelpState: shotSplitHelpState(shot, splitHelpReviewOverrides[shot.shotId])
        })
      });
      if (result) {
        const nextIssueMask = Number(
          result.helpReview?.issueMask ??
          ((shotSplitHelpIssueMask(shot) | SPLIT_ISSUE_UNDER) & SPLIT_ISSUE_MASK_ALL)
        );
        applySplitHelpLocally(shot.shotId, nextIssueMask, result.helpReview?.helpRequest || null);
        pushToast("success", `shot ${shot.shotId} marked split; video rejudge queued`);
      }
      await refreshShowSection();
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "split feedback failed");
    } finally {
      setPendingSplitShotKeys((current) => current.filter((key) => key !== shot.key));
    }
  }

  async function reprocessShotMotion(shot: BrowseShot) {
    const showId = selectedShowIdRef.current || selectedShowId;
    if (!showId || pendingMotionReprocessShotIds.includes(shot.shotId)) {
      return;
    }
    pushToast(
      "success",
      `shot ${shot.shotId}: v4 lineage automatically reindexes only changed replacements after re-split`
    );
  }

  function applyHelpRequestStatusLocally(helpRequestIds: number[], status: BrowseShotHelpRequestStatus) {
    const idSet = new Set(helpRequestIds);
    setShots((current) =>
      current.map((shot) => ({
        ...shot,
        helpRequests: status === "open"
          ? shot.helpRequests.map((helpRequest) => idSet.has(helpRequest.id) ? { ...helpRequest, status } : helpRequest)
          : shot.helpRequests.filter((helpRequest) => !idSet.has(helpRequest.id))
      }))
    );
  }

  async function updateHelpRequestStatuses(
    helpRequests: BrowseShotHelpRequest[],
    status: BrowseShotHelpRequestStatus,
    successText?: string
  ) {
    const uniqueHelpRequests = Array.from(new Map(helpRequests.map((helpRequest) => [helpRequest.id, helpRequest])).values());
    const targetIds = uniqueHelpRequests
      .map((helpRequest) => helpRequest.id)
      .filter((id) => !pendingHelpRequestIds.includes(id));
    if (targetIds.length === 0) {
      return;
    }
    setPendingHelpRequestIds((current) => Array.from(new Set([...current, ...targetIds])));
    try {
      await fetchJsonWithTimeout("/api/shot-help/bulk", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ids: targetIds, status })
      });
      applyHelpRequestStatusLocally(targetIds, status);
      pushToast("success", successText || (targetIds.length === 1 ? "help updated" : `${targetIds.length} help requests updated`));
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "help update failed");
    } finally {
      setPendingHelpRequestIds((current) => current.filter((id) => !targetIds.includes(id)));
    }
  }

  function applySplitHelpLocally(shotId: number, issueMask: number, helpRequest: BrowseShotHelpRequest | null) {
    const normalizedIssueMask = issueMask & SPLIT_ISSUE_MASK_ALL;
    setSplitHelpReviewOverrides((current) => ({
      ...current,
      [shotId]: {
        helpState: normalizedIssueMask === 0 ? "resolved" : "system_need_to_adjust",
        issueMask: normalizedIssueMask
      }
    }));
    setShots((current) =>
      current.map((shot) => {
        if (shot.shotId !== shotId) {
          return shot;
        }
        if (normalizedIssueMask === 0) {
          return helpRequest ? { ...shot, helpRequests: [helpRequest] } : shot;
        }
        if (shot.helpRequests.length > 0) {
          return {
            ...shot,
            helpRequests: shot.helpRequests.map((item) => ({
              ...item,
              status: "open",
              helpState: "system_need_to_adjust",
              issueMask: normalizedIssueMask,
              userIssueMask: normalizedIssueMask
            }))
          };
        }
        return helpRequest ? { ...shot, helpRequests: [helpRequest] } : shot;
      })
    );
  }

  function applySplitHelpIgnoredLocally(
    shotId: number,
    ignored: boolean,
    helpRequest: BrowseShotHelpRequest | null
  ) {
    setSplitHelpReviewOverrides((current) => ({
      ...current,
      [shotId]: {
        helpState: ignored ? "ignored" : "user_need_to_help",
        issueMask: 0
      }
    }));
    if (!helpRequest) {
      return;
    }
    setShots((current) => current.map((shot) =>
      shot.shotId === shotId ? { ...shot, helpRequests: [helpRequest] } : shot
    ));
  }

  function markVideoSplitLearningDirty(videoId: number) {
    const nowTs = Math.floor(Date.now() / 1000);
    setVideos((current) =>
      current.map((video) => {
        if (video.id !== videoId) {
          return video;
        }
        if (Number(video.splitLearningDirtyCount || 0) > 0) {
          return video;
        }
        return {
          ...video,
          splitLearningDirtyCount: Math.max(1, Number(video.splitLearningDirtyCount || 0)),
          splitLearningLabelCount: Math.max(1, Number(video.splitLearningLabelCount || 0)),
          splitLearningLabelUpdatedTs: Math.max(Number(video.splitLearningLabelUpdatedTs || 0), nowTs)
        };
      })
    );
    const showId = selectedShowIdRef.current;
    if (showId > 0) {
      if (splitLearningRefreshTimerRef.current !== null) {
        window.clearTimeout(splitLearningRefreshTimerRef.current);
      }
      // 연속 검수 중에는 매 키 입력마다 전역 학습 상태를 다시 읽지 않는다.
      splitLearningRefreshTimerRef.current = window.setTimeout(() => {
        splitLearningRefreshTimerRef.current = null;
        void fetchSplitLearningStatus(showId, videoId)
          .then((status) => setSplitLearningStatus(status))
          .catch(() => undefined);
      }, 750);
    }
  }

  useEffect(() => () => {
    if (splitLearningRefreshTimerRef.current !== null) {
      window.clearTimeout(splitLearningRefreshTimerRef.current);
    }
  }, []);

  // 탭 이동 모드일 때 리뷰 키의 자동 진행을 다음 help shot으로 보낸다.
  function reviewTabJumpNext() {
    if (!tabNavModeRef.current) {
      return false;
    }
    return tabJumpNextRef.current ? tabJumpNextRef.current() : false;
  }

  async function applySplitHelpReview(shot: BrowseShot, issueMask: number) {
    const normalizedIssueMask = normalizeUserAuthoredSplitIssueMask(issueMask);
    if (pendingSplitHelpShotIdSetRef.current.has(shot.shotId)) {
      return false;
    }
    pendingSplitHelpShotIdSetRef.current.add(shot.shotId);
    setPendingSplitHelpShotIds((current) => Array.from(new Set([...current, shot.shotId])));
    try {
      return await splitHelpSaveQueueRef.current.enqueue(async () => {
        const result = await fetchShotSplitHelpJson<{
          helpRequest?: BrowseShotHelpRequest | null;
          helpState?: BrowseShotHelpState;
          issueMask?: number;
        }>({
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            shotId: shot.shotId,
            issueMask: normalizedIssueMask,
            observedHelpState: shotSplitHelpState(shot, splitHelpReviewOverrides[shot.shotId]),
            helpRequestIds: shot.helpRequests.map((helpRequest) => helpRequest.id)
          })
        });
        applySplitHelpLocally(shot.shotId, Number(result.issueMask ?? normalizedIssueMask), result.helpRequest || null);
        markVideoSplitLearningDirty(shot.videoId);
        return true;
      });
    } catch (error) {
      pushToast(
        "error",
        `shot ${shot.shotId}: ${error instanceof Error ? error.message : "split help update failed"}`
      );
      return false;
    } finally {
      pendingSplitHelpShotIdSetRef.current.delete(shot.shotId);
      setPendingSplitHelpShotIds((current) => current.filter((id) => id !== shot.shotId));
    }
  }

  async function setSplitHelpIgnored(shot: BrowseShot, ignored: boolean) {
    if (pendingSplitHelpShotIdSetRef.current.has(shot.shotId)) {
      return false;
    }
    pendingSplitHelpShotIdSetRef.current.add(shot.shotId);
    setPendingSplitHelpShotIds((current) => Array.from(new Set([...current, shot.shotId])));
    try {
      return await splitHelpSaveQueueRef.current.enqueue(async () => {
        const result = await fetchJsonWithTimeout<{
          helpRequest?: BrowseShotHelpRequest | null;
          ignored?: boolean;
          ignoredLabelCount?: number;
        }>("/api/shot-split-help/ignore", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ shotId: shot.shotId, ignored })
        });
        applySplitHelpIgnoredLocally(shot.shotId, Boolean(result.ignored), result.helpRequest || null);
        if (Number(result.ignoredLabelCount || 0) > 0) {
          markVideoSplitLearningDirty(shot.videoId);
        }
        return true;
      });
    } catch (error) {
      pushToast(
        "error",
        `shot ${shot.shotId}: ${error instanceof Error ? error.message : "split help ignore update failed"}`
      );
      return false;
    } finally {
      pendingSplitHelpShotIdSetRef.current.delete(shot.shotId);
      setPendingSplitHelpShotIds((current) => current.filter((id) => id !== shot.shotId));
    }
  }

  async function markSelectedShotsGood(targetShots = selectedShots) {
    const uniqueShots = Array.from(new Map(targetShots.map((shot) => [shot.shotId, shot])).values());
    if (uniqueShots.length === 0 || uniqueShots.some((shot) => pendingSplitHelpShotIdSetRef.current.has(shot.shotId))) {
      return false;
    }
    const targetShotIds = uniqueShots.map((shot) => shot.shotId);
    for (const shotId of targetShotIds) {
      pendingSplitHelpShotIdSetRef.current.add(shotId);
    }
    setPendingSplitHelpShotIds((current) => Array.from(new Set([...current, ...targetShotIds])));
    try {
      const shotsByVideo = new Map<number, BrowseShot[]>();
      for (const shot of uniqueShots) {
        shotsByVideo.set(shot.videoId, [...(shotsByVideo.get(shot.videoId) || []), shot]);
      }
      const outcomes: Array<
        | { ok: true; shot: BrowseShot; result: { helpRequest?: BrowseShotHelpRequest | null; issueMask?: number } }
        | { ok: false; shot: BrowseShot; error: unknown }
      > = [];
      for (const [videoId, videoShots] of shotsByVideo) {
        try {
          const result = await fetchShotSplitHelpJson<{
            helpReviews?: Array<{
              shotId: number;
              helpRequest?: BrowseShotHelpRequest | null;
              issueMask?: number;
            }>;
          }>({
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              videoId,
              items: videoShots.map((shot) => ({
                shotId: shot.shotId,
                issueMask: 0,
                observedHelpState: shotSplitHelpState(shot, splitHelpReviewOverrides[shot.shotId]),
                helpRequestIds: shot.helpRequests.map((helpRequest) => helpRequest.id)
              }))
            })
          });
          const reviewByShotId = new Map(
            (result.helpReviews || []).map((review) => [Number(review.shotId), review])
          );
          for (const shot of videoShots) {
            const review = reviewByShotId.get(shot.shotId);
            if (!review) {
              throw new Error(`good review missing for shot ${shot.shotId}`);
            }
            outcomes.push({ ok: true, shot, result: review });
          }
        } catch (error) {
          outcomes.push(...videoShots.map((shot) => ({ ok: false as const, shot, error })));
        }
      }
      const succeeded = outcomes.filter((outcome) => outcome.ok);
      const failed = outcomes.filter((outcome) => !outcome.ok);
      for (const outcome of succeeded) {
        applySplitHelpLocally(outcome.shot.shotId, Number(outcome.result.issueMask ?? 0), outcome.result.helpRequest || null);
      }
      for (const videoId of new Set(succeeded.map((outcome) => outcome.shot.videoId))) {
        markVideoSplitLearningDirty(videoId);
      }
      if (succeeded.length > 0) {
        pushToast("success", `${succeeded.length} shot${succeeded.length === 1 ? "" : "s"} marked good`);
      }
      if (failed.length > 0) {
        const firstError = failed[0].error;
        pushToast(
          "error",
          `${failed.length} shot${failed.length === 1 ? "" : "s"} failed: ${firstError instanceof Error ? firstError.message : "split help update failed"}`
        );
      }
      return failed.length === 0;
    } finally {
      const targetShotIdSet = new Set(targetShotIds);
      for (const shotId of targetShotIds) {
        pendingSplitHelpShotIdSetRef.current.delete(shotId);
      }
      setPendingSplitHelpShotIds((current) => current.filter((id) => !targetShotIdSet.has(id)));
    }
  }

  async function markSelectedShotsIgnored(targetShots = selectedShots) {
    const uniqueShots = Array.from(new Map(targetShots.map((shot) => [shot.shotId, shot])).values());
    if (uniqueShots.length === 0 || uniqueShots.some((shot) => pendingSplitHelpShotIdSetRef.current.has(shot.shotId))) {
      return false;
    }
    const targetShotIds = uniqueShots.map((shot) => shot.shotId);
    for (const shotId of targetShotIds) {
      pendingSplitHelpShotIdSetRef.current.add(shotId);
    }
    setPendingSplitHelpShotIds((current) => Array.from(new Set([...current, ...targetShotIds])));
    try {
      const outcomes: Array<
        | { ok: true; shot: BrowseShot; result: { helpRequest?: BrowseShotHelpRequest | null; ignoredLabelCount?: number } }
        | { ok: false; shot: BrowseShot; error: unknown }
      > = [];
      for (const shot of uniqueShots) {
        try {
          const result = await splitHelpSaveQueueRef.current.enqueue(async () =>
            fetchJsonWithTimeout<{
              helpRequest?: BrowseShotHelpRequest | null;
              ignored?: boolean;
              ignoredLabelCount?: number;
            }>("/api/shot-split-help/ignore", {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ shotId: shot.shotId, ignored: true })
            })
          );
          outcomes.push({ ok: true, shot, result });
        } catch (error) {
          outcomes.push({ ok: false, shot, error });
        }
      }
      const succeeded = outcomes.filter((outcome) => outcome.ok);
      const failed = outcomes.filter((outcome) => !outcome.ok);
      for (const outcome of succeeded) {
        applySplitHelpIgnoredLocally(outcome.shot.shotId, true, outcome.result.helpRequest || null);
        if (Number(outcome.result.ignoredLabelCount || 0) > 0) {
          markVideoSplitLearningDirty(outcome.shot.videoId);
        }
      }
      if (succeeded.length > 0) {
        pushToast("success", `${succeeded.length} shot${succeeded.length === 1 ? "" : "s"} ignored`);
      }
      if (failed.length > 0) {
        const firstError = failed[0].error;
        pushToast(
          "error",
          `${failed.length} shot${failed.length === 1 ? "" : "s"} failed: ${firstError instanceof Error ? firstError.message : "split help ignore update failed"}`
        );
      }
      return failed.length === 0;
    } finally {
      const targetShotIdSet = new Set(targetShotIds);
      for (const shotId of targetShotIds) {
        pendingSplitHelpShotIdSetRef.current.delete(shotId);
      }
      setPendingSplitHelpShotIds((current) => current.filter((id) => !targetShotIdSet.has(id)));
    }
  }

  async function applyMergeSplitCorrection() {
    if (!selectedShow || selectedVideoId === null || selectedVideoCorrectionButtonBusy || splitLearningActive || selectedVideoCorrectionCount === 0) {
      return;
    }
    const targetShowId = selectedShow.id;
    const targetVideoId = selectedVideoId;
    setApplyingSplitCorrection(true);
    try {
      const result = await fetchJsonWithTimeout<{
        queued?: number;
        job?: {
          id?: number;
          status?: number;
          progress_msg?: string;
        };
        applied?: {
          splitCount?: number;
          mergeCount?: number;
          mergeSkippedCount?: number;
        };
        orphanCleanup?: {
          reviewRows?: { created?: number };
          closedRows?: { affectedRows?: number };
        };
        remainingState2Count?: number;
        splitLearning?: {
          dirtyCount?: number;
          labelCount?: number;
          labelUpdatedTs?: number;
          modelTs?: number;
        };
        mode?: string;
        overMergePlan?: { waitingForUnderPhase?: boolean; skippedCount?: number };
      }>("/api/shot-split-correction/apply", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          showId: targetShowId,
          videoId: targetVideoId
        })
      });
      if (
        result.mode === "state2_source_video_correction_queued" ||
        result.mode === "state2_source_video_correction_already_running" ||
        result.mode === "state2_source_video_correction_worker_restarted"
      ) {
        const correctionJobId = Number(result.job?.id || 0);
        if (correctionJobId > 0) {
          setSplitCorrectionWatchJobIds((current) => ({ ...current, [targetVideoId]: correctionJobId }));
        }
        setSplitCorrectionStatuses((current) => ({
          ...current,
          [targetVideoId]: {
            correctionActive: true,
            correctionJob: result.job || null,
            state2Count: selectedVideoCorrectionCount
          }
        }));
        pushToast(
          "info",
          result.mode === "state2_source_video_correction_already_running"
            ? `split feedback correction is already running${result.job?.id ? ` (#${result.job.id})` : ""}`
            : result.mode === "state2_source_video_correction_worker_restarted"
              ? `split feedback correction worker restarted${result.job?.id ? ` (#${result.job.id})` : ""}`
              : `split feedback correction queued${result.job?.id ? ` (#${result.job.id})` : ""}`
        );
        await refreshShowSection();
        return;
      }
      const splitCount = Number(result.applied?.splitCount || 0);
      const mergeCount = Number(result.applied?.mergeCount || 0);
      const mergeSkippedCount = Number(result.applied?.mergeSkippedCount || 0);
      const orphanClosed = Number(result.orphanCleanup?.closedRows?.affectedRows || 0);
      const remainingState2Count = Number(result.remainingState2Count || 0);
      pushToast(
        splitCount + mergeCount + orphanClosed > 0 ? "success" : "info",
        remainingState2Count > 0
          ? `split feedback still needs work: ${remainingState2Count}`
          : splitCount + mergeCount + orphanClosed > 0
            ? `split feedback applied: ${splitCount} split, ${mergeCount} merge${mergeSkippedCount > 0 ? `, preserved ${mergeSkippedCount}` : ""}${orphanClosed > 0 ? `, closed ${orphanClosed} stale` : ""}`
            : "no state-2 split feedback to apply"
      );
      await Promise.all([
        refreshShowSection(),
        refreshSplitCorrectionUiState(targetShowId, targetVideoId)
      ]);
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "Merge/Split apply failed");
    } finally {
      setApplyingSplitCorrection(false);
    }
  }

  async function trainGbdtFromNewGood() {
    if (!selectedShow || selectedVideoId === null || trainingGbdt || splitLearningActive || splitLearningDirtyCount === 0) {
      return;
    }
    setTrainingGbdt(true);
    try {
      const result = await fetchJsonWithTimeout<{
        mode?: string;
        learningJob?: SplitCorrectionJobStatus & { alreadyRunning?: boolean; skipped?: boolean };
      }>("/api/shot-split-learning/train", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ showId: selectedShow.id, videoId: selectedVideoId })
      });
      const learningJob = result.learningJob || null;
      setSplitLearningStatus((current) => ({
        ...(current || { correctionActive: false }),
        learningActive: Boolean(learningJob && Number(learningJob.status || 0) < 2),
        learningJob,
      }));
      pushToast(
        "info",
        learningJob?.skipped
          ? "GBDT is already current"
          : learningJob?.alreadyRunning
            ? `GBDT training is already running${learningJob.id ? ` (#${learningJob.id})` : ""}`
            : `GBDT training queued${learningJob?.id ? ` (#${learningJob.id})` : ""}`
      );
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : "GBDT training failed to queue");
    } finally {
      setTrainingGbdt(false);
    }
  }

  async function markFilteredHelpRequestsGood() {
    if (visibleFilteredHelpRequests.length === 0) {
      return;
    }
    await updateHelpRequestStatuses(
      visibleFilteredHelpRequests,
      "confirmed_ok",
      `${formatIssueCount(visibleFilteredHelpIssueCount)} marked good`
    );
  }

  function addPendingActionKeys(action: "delete" | "merge", keys: string[]) {
    const setPending = action === "delete" ? setPendingDeleteShotKeys : setPendingMergeShotKeys;
    setPending((current) => Array.from(new Set([...current, ...keys])));
  }

  function removePendingActionKeys(action: "delete" | "merge", keys: string[]) {
    const setPending = action === "delete" ? setPendingDeleteShotKeys : setPendingMergeShotKeys;
    const keySet = new Set(keys);
    setPending((current) => current.filter((key) => !keySet.has(key)));
  }

  async function runMultiAction(action: "delete" | "merge", targetShots = selectedShots) {
    const videoId = targetShots[0]?.videoId ?? selectedVideoId;
    if (
      videoId === null ||
      targetShots.length === 0 ||
      (action === "merge" && targetShots.length < 2) ||
      targetShots.some((shot) => shot.videoId !== videoId)
    ) {
      return;
    }
    const targetKeys = targetShots.map((shot) => shot.key);
    const pendingKeys = action === "delete" ? pendingDeleteShotKeys : pendingMergeShotKeys;
    if (targetKeys.some((key) => pendingKeys.includes(key))) {
      return;
    }
    const restoreDeletedShots = action === "delete" && targetShots.every((shot) => shot.softDeleted);
    addPendingActionKeys(action, targetKeys);
    try {
      if (action === "merge") {
        const orderedTargets = [...targetShots].sort((left, right) =>
          left.videoId - right.videoId || left.shotNo - right.shotNo || left.shotId - right.shotId
        );
        const result = await fetchJsonWithTimeout<{
          helpReviews?: Array<{
            shotId: number;
            helpRequest?: BrowseShotHelpRequest | null;
            helpState?: BrowseShotHelpState;
            issueMask?: number;
          }>;
        }>("/api/shot-merge", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            videoId,
            items: orderedTargets.map((shot) => ({
              shotId: shot.shotId,
              observedHelpState: shotSplitHelpState(shot, splitHelpReviewOverrides[shot.shotId]),
              helpRequestIds: shot.helpRequests.map((helpRequest) => helpRequest.id)
            }))
          })
        });
        const reviewByShotId = new Map(
          (result.helpReviews || []).map((review) => [Number(review.shotId), review])
        );
        for (const shot of orderedTargets) {
          const review = reviewByShotId.get(shot.shotId);
          if (!review || !Number.isInteger(Number(review.issueMask))) {
            throw new Error(`merge review missing for shot ${shot.shotId}`);
          }
          applySplitHelpLocally(shot.shotId, Number(review.issueMask), review.helpRequest || null);
        }
        markVideoSplitLearningDirty(videoId);
        pushToast("success", `${targetShots.length} shot${targetShots.length === 1 ? "" : "s"} marked for merge`);
        return;
      }
      await fetchJsonWithTimeout<
        { ok?: boolean } | null
      >("/api/shot-delete-toggle", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          videoId,
          shotIds: targetShots.map((shot) => shot.shotId),
          restore: restoreDeletedShots
        })
      });
      if (action === "delete") {
        const targetKeys = new Set(targetShots.map((shot) => shot.key));
        setShots((current) =>
          current.map((shot) =>
            targetKeys.has(shot.key)
              ? {
                  ...shot,
                  softDeleted: !restoreDeletedShots,
                  flags: restoreDeletedShots ? shot.flags & ~1 : shot.flags | 1
                }
              : shot
          )
        );
        pushToast(
          "success",
          `${restoreDeletedShots ? "restored" : "deleted"} ${targetShots.length} shot${targetShots.length === 1 ? "" : "s"}`
        );
      } else {
        setSelectedShotKeys([]);
        setLastSelectedShotKey(null);
      }
      await refreshShowSection();
    } catch (error) {
      pushToast("error", error instanceof Error ? error.message : `${action} failed`);
    } finally {
      removePendingActionKeys(action, targetKeys);
    }
  }

  runMultiActionRef.current = runMultiAction;
  markSelectedShotsGoodRef.current = markSelectedShotsGood;

  async function registerSource(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const path = sourcePath.trim();
    if (!path || sourceRegisterBusy) {
      return;
    }
    setSourceRegisterBusy(true);
    setSourceRegisterMessage("");
    try {
      const response = await fetch("/api/source-register", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ path })
      });
      const result = (await response.json().catch(() => null)) as { error?: string; pid?: number; mode?: string } | null;
      if (!response.ok) {
        throw new Error(result?.error || "source register failed");
      }
      setSourcePath("");
      setSourceRegisterMessage(`${result?.mode || "source"} queued${result?.pid ? `:${result.pid}` : ""}`);
      window.setTimeout(() => {
        void refreshShowSection().catch(() => {
          // The source queue can outlive this page; visible data will update on the next poll.
        });
      }, 1500);
    } catch (error) {
      setSourceRegisterMessage(error instanceof Error ? error.message : "source register failed");
    } finally {
      setSourceRegisterBusy(false);
    }
  }

  return (
    <main
      className={`browseWorkspace mobileStage-${mobileStage}`}
      onPointerCancel={() => {
        mobileSwipeRef.current = null;
      }}
      onPointerDown={handleMobileStagePointerDown}
      onPointerMove={handleMobileStagePointerMove}
      onPointerUp={handleMobileStagePointerEnd}
      ref={workspaceRef}
      style={{ gridTemplateColumns: `${leftPct}% 6px ${midPct}% 6px minmax(0, 1fr)` }}
    >
      <section className="browsePane showPane" aria-label="Shows">
        <div className="browsePaneHeader">
          <div className="showTypeTabs" aria-label="Show type filter">
            {[
              ["all", "all"],
              [1, "movie"],
              [2, "drama"],
              [3, "vertical"]
            ].map(([value, label]) => (
              <button
                className={`showTypeTab ${showTypeFilter === value ? "active" : ""}`}
                key={String(value)}
                onClick={() => setShowTypeFilter(value as "all" | 1 | 2 | 3)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="showTable" role="table" aria-label="Show list" ref={showTableRef}>
          {visibleShows.map((show) => (
            <a
              className={`showTableRow ${selectedShow?.id === show.id ? "active" : ""}`}
              data-show-id={show.id}
              href={`/shots/${show.id}`}
              key={show.id}
              onClick={(event) => {
                event.preventDefault();
                if (isMobileShotsRef.current) {
                  void selectShow(show.id, false);
                  goToMobileStage("shots", true, show.id);
                  return;
                }
                void selectShow(show.id);
              }}
              role="row"
            >
              <span className="showTypeMarkerCell">
                {show.type === 3 ? <span aria-label="Vertical show" className="showVerticalIcon" title="Vertical" /> : null}
              </span>
              <button
                aria-label={show.indexPriority > 0 ? "Remove show index priority" : "Prioritize show indexing"}
                aria-pressed={show.indexPriority > 0}
                className={`showPriorityButton ${show.indexPriority > 0 ? "active" : ""}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void toggleIndexPriority({ showId: show.id }, show.indexPriority);
                }}
                title={show.indexPriority > 0 ? "Indexing priority ON — click to remove" : "Prioritize this show in NPU indexing"}
                type="button"
              >
                <Zap size={11} />
              </button>
              <span className="showListTitle">
                <span>{show.title}</span>
                {show.episodeCount > 1 ? <span className="showEpisodeCount">({show.episodeCount})</span> : null}
                {show.status !== "done" ? (
                  <span className={`showStatusTag ${show.status}`}>{show.status}</span>
                ) : null}
                <MotionProgressBadge progress={show.motionProgress} />
              </span>
            </a>
          ))}
        </div>
        {!isMobileShots ? (
          <div className="showSourceFooter">
            <div className="showSourceActions">
              <div className="showSourceAddWrap">
                {sourceAdderOpen ? (
                  <form className="sourcePathForm sourcePathPopover" onSubmit={registerSource}>
                    <input
                      autoFocus
                      disabled={sourceRegisterBusy}
                      onChange={(event) => setSourcePath(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape" && !sourceRegisterBusy) {
                          setSourceAdderOpen(false);
                          setSourceRegisterMessage("");
                          setSourcePath("");
                        }
                      }}
                      placeholder="/video1/ko/..."
                      type="text"
                      value={sourcePath}
                    />
                    <button disabled={sourceRegisterBusy || !sourcePath.trim()} title="Add source" type="submit">
                      <Plus size={16} />
                    </button>
                    <button
                      disabled={sourceRegisterBusy}
                      onClick={() => {
                        setSourceAdderOpen(false);
                        setSourceRegisterMessage("");
                        setSourcePath("");
                      }}
                      title="Close"
                      type="button"
                    >
                      <X size={16} />
                    </button>
                  </form>
                ) : null}
                <button
                  aria-expanded={sourceAdderOpen}
                  aria-label="Add source"
                  className={`showAddSourceButton${sourceAdderOpen ? " active" : ""}`}
                  onClick={() => setSourceAdderOpen((open) => !open)}
                  title="Add source"
                  type="button"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                className="showTrainGbdtButton"
                disabled={trainingGbdt || splitLearningActive || splitLearningDirtyCount === 0}
                onClick={trainGbdtFromNewGood}
                title={
                  splitLearningActive
                    ? `GBDT learning is active${splitLearningStatus?.learningJob?.progress_msg ? `: ${splitLearningStatus.learningJob.progress_msg}` : ""}`
                    : splitLearningDirtyCount > 0
                      ? `Train ${splitLearningDirtyCount} new Good labels across ${splitLearningAffectedVideoCount} videos; no correction will run`
                      : "No new Good labels to train"
                }
                type="button"
              >
                <span>Train GBDT</span>
                {splitLearningDirtyCount > 0 ? <strong>{splitLearningDirtyCount}</strong> : null}
              </button>
              <div className="showH2vControl">
                <button
                  className={`showH2vButton ${h2vState.state}`}
                  aria-label="Outpaint to 9x16 controls"
                  type="button"
                >
                  {h2vState.state === "starting" || h2vState.state === "stopping" ? <LoaderCircle className="spinIcon" size={15} /> : <span className="h2vGlyph">h2v</span>}
                </button>
                <div className="showH2vMenu" role="dialog" aria-label="Outpaint to 9x16">
                  <strong className="showH2vMenuTitle">Outpaint to 9x16</strong>
                  <div className={`showH2vMenuState ${h2vState.state}`}>{h2vStatusLabel}</div>
                  {h2vState.run ? <div className="showH2vMenuStats">
                    {h2vState.run.history.length > 0 ? <div className="showH2vHistory">
                      {h2vState.run.history.map((entry) => (
                        <button
                          className="showH2vHistoryRow"
                          key={`${entry.showId}-${entry.firstTs}`}
                          onClick={() => { void selectShow(entry.showId); }}
                          title={`Open ${entry.showTitle}`}
                          type="button"
                        >
                          <span className="showH2vHistoryTime">{h2vHistoryStartedAt(entry.firstTs)}</span>
                          <span className="showH2vHistoryTitle">{entry.showTitle}</span>
                          <span className="showH2vHistoryEps">{h2vHistoryEps(entry)}</span>
                        </button>
                      ))}
                    </div> : null}
                    <span>{h2vState.run.todo} to-do · {h2vState.run.encoding} encoding{h2vState.run.failed ? ` · ${h2vState.run.failed} failed` : ""}</span>
                  </div> : <div className="showH2vMenuStats"><span>Ready to start</span></div>}
                  <div className="showH2vGpuButtons">
                    {(["gpuk0","gpuk1"] as const).map((gpu,index)=>{const active=Boolean(h2vState.run?.enabled[gpu]);return <button aria-pressed={active} className={active?"active":""} disabled={h2vState.state==="stopping"} key={gpu} onClick={()=>void setH2vGpu(gpu,!active)} type="button">GPU {index}</button>;})}
                  </div>
                </div>
              </div>
              <div className="showNpuControl">
                <button
                  className={`showNpuButton ${npuState.state}`}
                  aria-label="NPU shot indexing controls"
                  type="button"
                >
                  {npuActionBusy ? <LoaderCircle className="spinIcon" size={15} /> : <span className="npuGlyph">npu</span>}
                </button>
                <div className="showNpuMenu" role="dialog" aria-label="NPU shot indexing">
                  <strong className="showNpuMenuTitle">NPU shot indexing</strong>
                  <div className={`showNpuMenuState ${npuState.state}`}>{npuStatusLabel}</div>
                  {npuState.run ? (
                    <div className="showNpuMenuStats">
                      <span>{npuXLine}</span>
                      <span>{npuYLine}</span>
                      {npuZLine ? <span>{npuZLine}</span> : null}
                      {npuVLine ? <span>{npuVLine}</span> : null}
                      {npuDiskLine ? <span className={npuDiskLine.className}>{npuDiskLine.text}</span> : null}
                    </div>
                  ) : (
                    <div className="showNpuMenuStats">
                      <span>Analyzes motion packets, activity labels, pose, and visual keywords on NPU until every queue drains.</span>
                    </div>
                  )}
                  {npuRecentVisual.length ? (
                    <div className="showNpuRecent">
                      <strong>Recent visual</strong>
                      <div className="showNpuRecentRows">
                        {npuRecentVisual.map((block) => (
                          <button
                            className="showNpuRecentRow"
                            key={`visual-${block.startedTs}-${block.showId}`}
                            onClick={() => { void selectShow(block.showId); }}
                            title={`Open ${block.showTitle}`}
                            type="button"
                          >
                            <span className="showNpuRecentTime">{npuRecentStartedLabel(block.startedTs)}</span>
                            <span className="showNpuRecentTitle">{block.showTitle}</span>
                            <span className="showNpuRecentEps">{npuRecentEps(block)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {npuRecentVl.length ? (
                    <div className="showNpuRecent">
                      <strong>Recent motion</strong>
                      <div className="showNpuRecentRows">
                        {npuRecentVl.map((block) => (
                          <button
                            className="showNpuRecentRow"
                            key={`vl-${block.startedTs}-${block.showId}`}
                            onClick={() => { void selectShow(block.showId); }}
                            title={`Open ${block.showTitle}`}
                            type="button"
                          >
                            <span className="showNpuRecentTime">{npuRecentStartedLabel(block.startedTs)}</span>
                            <span className="showNpuRecentTitle">{block.showTitle}</span>
                            <span className="showNpuRecentEps">{npuRecentEps(block)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {npuRecentDoodle.length ? (
                    <div className="showNpuRecent">
                      <strong>Recent Doodles</strong>
                      <div className="showNpuRecentRows">
                        {npuRecentDoodle.map((block) => (
                          <button
                            className="showNpuRecentRow"
                            key={`doodle-${block.startedTs}-${block.showId}`}
                            onClick={() => { void selectShow(block.showId); }}
                            title={`Open ${block.showTitle}`}
                            type="button"
                          >
                            <span className="showNpuRecentTime">{npuRecentStartedLabel(block.startedTs)}</span>
                            <span className="showNpuRecentTitle">{block.showTitle}</span>
                            <span className="showNpuRecentEps">{npuRecentEps(block)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <button
                    className={`showNpuActionButton ${npuState.state}`}
                    disabled={npuActionBusy}
                    onClick={() => void requestNpu(npuState.state === "inactive" ? "start" : "stop")}
                    type="button"
                  >
                    {npuActionBusy ? <LoaderCircle className="spinIcon" size={13} /> : null}
                    <span>{npuActionBusy ? `${npuStatusLabel}…` : npuState.state === "inactive" ? "Start" : "Stop"}</span>
                  </button>
                </div>
              </div>
            </div>
            {sourceRegisterMessage ? <div className="sourcePathStatus">{sourceRegisterMessage}</div> : null}
          </div>
        ) : null}
      </section>

      <div
        className="columnSplitter"
        onPointerDown={(event) => dragSplitter("left", event)}
        role="separator"
        aria-orientation="vertical"
      />

      <section className={`browsePane shotPane ${showEpisodeRail ? "hasEpisodeRail" : ""}`} aria-label="Shots">
        {showEpisodeRail ? (
          <div className="shotPaneTop">
            <div className="browseEpisodeRail" aria-label="Episodes" ref={episodeRailRef}>
              {seasonGroups.map((group) => (
                <div className="episodeGroupInline" key={`${group.deleted ? "deleted" : "active"}-${group.seasonNo ?? "no-season"}`}>
                  {showSeasonLabels ? (
                    <span className="episodeSeasonLabel">S{group.seasonNo}</span>
                  ) : null}
                  {group.episodes.map((episode) => {
                    const video = videoById.get(episode.videoId) || null;
                    return (
                      <div className="episodeChip" key={episode.key}>
                        <button
                          className={`episodeButton ${selectedEpisodeKey === episode.key ? "active" : ""} ${episode.deleted ? "deleted" : ""}`}
                          data-episode-key={episode.key}
                          data-video-id={episode.videoId}
                          onClick={() => {
                            scrollPreviewForShow(selectedShow);
                            initialShotShowIdRef.current = 0;
                            setInitialShotLoadShowId(null);
                            selectedEpisodeKeyRef.current = episode.key;
                            setSelectedEpisodeKey(episode.key);
                            selectedVideoIdRef.current = episode.videoId;
                            setSelectedVideoId(episode.videoId);
                            setSelectedShotKeys([]);
                            setLastSelectedShotKey(null);
                          }}
                          title={video?.srcPath || browseEpisodeLabel(episode, showSeasonLabels)}
                          type="button"
                        >
                          <span>{episode.episodeNo}</span>
                          <MotionProgressBadge hideWhenDone progress={episode.motionProgress} />
                        </button>
                        <button
                          aria-label={episode.indexPriority > 0 ? "Remove episode index priority" : "Prioritize episode indexing"}
                          aria-pressed={episode.indexPriority > 0}
                          className={`episodePriorityButton ${episode.indexPriority > 0 ? "active" : ""}`}
                          onClick={() => void toggleIndexPriority({ episodeId: episode.id ?? 0, key: episode.key }, episode.indexPriority)}
                          title={episode.indexPriority > 0 ? "Indexing priority ON — click to remove" : "Prioritize this episode in NPU indexing"}
                          type="button"
                        >
                          <Zap size={10} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <VirtualShotGrid
          emptyContent={
            <div className="browseEmptyState">
              {showLoadingId !== null
                ? "Loading show..."
                : selectedShowInitialShotsLoading || selectedVideoShotsLoading
                  ? "Loading shots..."
                    : showLoadError
                      ? showLoadError
                    : shotHelpFilter === "review"
                      ? "No review-only help requests"
                    : shotHelpFilter === "ignored"
                      ? "No ignored split reviews"
                    : shotHelpFilter === "over_split"
                      ? "No merge help requests"
                      : shotHelpFilter === "under_split_candidate"
                        ? "No split help requests"
                      : selectedVideo && selectedVideo.shotCount > 0
                        ? `No ready thumbnails yet. ${videoProgressLabel(selectedVideo)}`
                        : "No shots yet"}
            </div>
          }
          filterBar={isMobileShots ? shotStageFilterBar : null}
          footer={shotHelpFilterFooter}
          gridRef={shotGridRef}
          isMobile={isMobileShots}
          onClearSelection={clearShotSelectionFromGrid}
          onFocusShot={focusShot}
          onSelectShot={selectShot}
          selectedShotKeys={selectedShotKeys}
          shotEditJobs={shotEditJobs}
          splitGbdtRejudgeActive={selectedVideoCorrectionRunning}
          shots={filteredShots}
        />
        {filteredShots.length > 0 ? (
          <div className="shotScrollButtons" aria-label="Shot list navigation">
            <button
              aria-label="Jump to top"
              onClick={() => {
                if (shotGridRef.current) {
                  shotGridRef.current.scrollTop = 0;
                }
              }}
              title="Jump to top"
              type="button"
            >
              <ArrowUp size={17} />
            </button>
            <button
              aria-label="Jump to bottom"
              onClick={() => {
                if (shotGridRef.current) {
                  shotGridRef.current.scrollTop = shotGridRef.current.scrollHeight;
                }
              }}
              title="Jump to bottom"
              type="button"
            >
              <ArrowDown size={17} />
            </button>
          </div>
        ) : null}
        <div className="shotPaneFooter">
          <div className="shotPaneFooterLeft">
            {shotHelpFilterBar}
            <button
              className="applyMergeSplitButton"
              disabled={selectedVideoCorrectionButtonBusy || splitLearningActive || selectedVideoCorrectionCount === 0}
              onClick={applyMergeSplitCorrection}
              title={
                selectedVideoCorrectionStatusPending
                  ? "Checking whether a split correction job is already running"
                  : splitLearningActive
                  ? `Apply Merge/Split is disabled while GBDT learning is active${splitLearningStatus?.learningJob?.progress_msg ? `: ${splitLearningStatus.learningJob.progress_msg}` : ""}`
                  : selectedVideoCorrectionRunning
                  ? `A split correction job is already queued or running${selectedSplitCorrectionStatus?.correctionJob?.progress_msg ? `: ${selectedSplitCorrectionStatus.correctionJob.progress_msg}` : ""}`
                  : selectedVideoCorrectionCount > 0
                    ? `Dry-run: ${selectedVideoPlannedSplitCount} split, ${selectedVideoPlannedMergeCount} merge${selectedVideoCorrectionBlockerCount > 0 ? `; ${selectedVideoCorrectionBlockerCount} blocker(s)` : ""}. This does not train GBDT.`
                    : "No state-2 split feedback to apply"
              }
              type="button"
            >
              {selectedVideoCorrectionButtonBusy ? <LoaderCircle className="spinIcon" size={14} /> : <Scissors size={14} />}
              <span>{selectedVideoCorrectionStatusPending ? "Checking" : selectedVideoCorrectionRunning ? "Fixing" : applyingSplitCorrection ? "Applying" : selectedVideoCorrectionButtonLabel}</span>
            </button>
          </div>
          <label className="uiSwitchControl">
            <input
              checked={showDeleted}
              onChange={(event) => setShowDeleted(event.target.checked)}
              type="checkbox"
            />
            <span aria-hidden="true" className="uiSwitchTrack">
              <span className="uiSwitchThumb" />
            </span>
            <span>Show Deleted</span>
          </label>
        </div>
      </section>

      <div
        className="columnSplitter"
        onPointerDown={(event) => dragSplitter("right", event)}
        role="separator"
        aria-orientation="vertical"
      />

      <section className="browsePane previewPane" aria-label="Preview">
        <div className="previewPaneScroll" ref={previewPaneRef}>
          <button
            className="mobileStageBack mobilePreviewBack"
            onClick={navigateMobileStageBack}
            type="button"
          >
            <ChevronLeft size={16} />
            <span>Shots</span>
          </button>
          {selectedShots.length > 1 ? (
            <MultiShotPanel
              deletePending={selectedDeletePending}
              goodPending={selectedShots.some((shot) => pendingSplitHelpShotIds.includes(shot.shotId))}
              ignorePending={selectedShots.some((shot) => pendingSplitHelpShotIds.includes(shot.shotId))}
              mergePending={selectedMergePending}
              shots={selectedShots}
              onDelete={() => runMultiAction("delete")}
              onGood={() => markSelectedShotsGood()}
              onIgnore={() => markSelectedShotsIgnored()}
              onMerge={() => runMultiAction("merge")}
            />
          ) : selectedShot ? (
            <PreviewPanel
              adjacencyShots={selectedEpisodeBaseShots}
              episodes={episodes}
              mediaBaseUrl={data.mediaBaseUrl || ""}
              playlist={filteredShots}
              playRequestId={playRequestId}
              rangeMarkPending={pendingRangeMark}
              sceneStartPendingKey={pendingSceneShotKey}
              showType={selectedShow?.type || 0}
              splitJobs={shotEditJobs.filter((job) => job.action === "split")}
              splitPendingShotKeys={pendingSplitShotKeys}
              shot={selectedShot}
              video={selectedVideo}
              pendingDeleteShotKeys={pendingDeleteShotKeys}
              pendingEpisodeShotKey={pendingEpisodeShotKey}
              pendingSplitHelpShotIds={pendingSplitHelpShotIds}
              splitHelpOverride={splitHelpReviewOverrides[selectedShot.shotId]}
              splitHelpOverrides={splitHelpReviewOverrides}
              onApplySplitHelpReview={applySplitHelpReview}
              onSetSplitHelpIgnored={setSplitHelpIgnored}
              onReviewTabJumpNext={reviewTabJumpNext}
              onDeleteShot={(shot) => runMultiAction("delete", [shot])}
              onMarkIntroEnd={(shot) => markIntroOutro(shot, "intro_end")}
              onMarkOutroStart={(shot) => markIntroOutro(shot, "outro_start")}
              onPlaybackShotChange={(shotKey) => {
                // 프리뷰(인접 이동/연속재생/존 클릭)가 선택을 바꾼 경우도 탭 이동 모드가 아니다.
                tabNavModeRef.current = false;
                setSelectedShotKeys([shotKey]);
                setLastSelectedShotKey(shotKey);
                revealShotTileSoon(shotKey);
              }}
              onReprocessShotMotion={reprocessShotMotion}
              onSetEpisodeStart={updateEpisodeStart}
              onSetSceneStart={setSceneStart}
              onSplitShot={splitShot}
              pendingMotionReprocessShotIds={pendingMotionReprocessShotIds}
            />
          ) : (
            <ShowInfoPanel
              data={currentData}
              selectedEpisode={selectedEpisode}
              selectedVideoId={selectedVideoId}
              shots={shots}
              onShowTextSaved={updateShowText}
              onShowWikiUrlSaved={updateShowWikiUrl}
              onShowMetaSaved={updateShowMeta}
              onMotionStatusUpdated={setMotionStatus}
              onDeleteEpisode={deleteEpisode}
              onRetryVideo={retryVideo}
              onResplitEpisode={resplitEpisode}
              onResplitShow={resplitShow}
              onVideoEpisodeTextSaved={updateVideoEpisodeText}
              onLightboxImageChange={handleLightboxImageChange}
              deletingVideoIds={deletingVideoIds}
              retryingVideoIds={retryingVideoIds}
              resplittingVideoIds={resplittingVideoIds}
              resplittingShowId={resplittingShowId}
              showDeleted={showDeleted}
              showSeasonLabels={showSeasonLabels}
            />
          )}
        </div>
        <div className="previewScrollButtons" aria-label="Preview panel navigation">
          <button
            aria-label="Jump preview to top"
            onClick={() => {
              if (previewPaneRef.current) {
                previewPaneRef.current.scrollTop = 0;
              }
            }}
            title="Jump to top"
            type="button"
          >
            <ArrowUp size={17} />
          </button>
          <button
            aria-label="Jump preview to bottom"
            onClick={() => {
              if (previewPaneRef.current) {
                previewPaneRef.current.scrollTop = previewPaneRef.current.scrollHeight;
              }
            }}
            title="Jump to bottom"
            type="button"
          >
            <ArrowDown size={17} />
          </button>
        </div>
      </section>
      {toasts.length > 0 ? (
        <div className="browseToasts" aria-live="polite" aria-relevant="additions">
          {toasts.map((toast) => (
            <div
              className={`browseToast ${toast.kind}`}
              key={toast.id}
              role={toast.kind === "error" ? "alert" : "status"}
            >
              <div className="browseToastText">{toast.text}</div>
              <button
                aria-label="Close toast"
                className="browseToastIconButton browseToastClose"
                onClick={() => dismissToast(toast.id)}
                type="button"
              >
                <X size={14} />
              </button>
              <button
                aria-label="Copy toast text"
                className="browseToastIconButton browseToastCopy"
                onClick={() => void copyTextToClipboard(toast.text)}
                type="button"
              >
                <Copy size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </main>
  );
}

function ShowInfoPanel({
  data,
  deletingVideoIds,
  retryingVideoIds,
  resplittingVideoIds,
  resplittingShowId,
  selectedEpisode,
  selectedVideoId,
  showDeleted,
  showSeasonLabels,
  shots,
  onShowTextSaved,
  onShowWikiUrlSaved,
  onShowMetaSaved,
  onMotionStatusUpdated,
  onDeleteEpisode,
  onRetryVideo,
  onResplitEpisode,
  onResplitShow,
  onVideoEpisodeTextSaved,
  onLightboxImageChange
}: {
  data: BrowseData;
  deletingVideoIds: number[];
  selectedEpisode: BrowseEpisode | null;
  selectedVideoId: number | null;
  retryingVideoIds: number[];
  resplittingVideoIds: number[];
  resplittingShowId: number | null;
  showDeleted: boolean;
  showSeasonLabels: boolean;
  shots: BrowseShot[];
  onShowTextSaved: (showId: number, title: string, summary: string | null) => void;
  onShowWikiUrlSaved: (showId: number, wikiUrl: string | null) => void;
  onShowMetaSaved: (show: BrowseShow) => void;
  onMotionStatusUpdated: (status: BrowseShowMotionStatus | null) => void;
  onDeleteEpisode: (videoId: number, deleted?: boolean) => void;
  onRetryVideo: (videoId: number) => void;
  onResplitEpisode: (videoId: number) => void;
  onResplitShow: (showId: number) => void;
  onVideoEpisodeTextSaved: (videoId: number, title: string | null, summary: string | null) => void;
  onLightboxImageChange: (image: string | null) => void;
}) {
  const show = data.selectedShow;
  const [editingShowId, setEditingShowId] = useState<number | null>(null);
  const [draftShowTitle, setDraftShowTitle] = useState("");
  const [draftShowSummary, setDraftShowSummary] = useState("");
  const [savingShowText, setSavingShowText] = useState(false);
  const [showTextError, setShowTextError] = useState("");
  const [editingWikiShowId, setEditingWikiShowId] = useState<number | null>(null);
  const [draftWikiUrl, setDraftWikiUrl] = useState("");
  const [savingWikiUrl, setSavingWikiUrl] = useState(false);
  const [wikiUrlError, setWikiUrlError] = useState("");
  const [savingShowMeta, setSavingShowMeta] = useState<"type" | "lang" | null>(null);
  const [showMetaError, setShowMetaError] = useState("");
  const [editingVideoId, setEditingVideoId] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftSummary, setDraftSummary] = useState("");
  const [savingEpisodeText, setSavingEpisodeText] = useState(false);
  const [episodeTextError, setEpisodeTextError] = useState("");
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [motionBusy, setMotionBusy] = useState(false);
  const [motionError, setMotionError] = useState("");
  const [motionSearchQuery, setMotionSearchQuery] = useState("");
  const [motionSearchBusy, setMotionSearchBusy] = useState(false);
  const [motionSearchError, setMotionSearchError] = useState("");
  const [motionSearchResults, setMotionSearchResults] = useState<MotionSearchResult[]>([]);

  useEffect(() => {
    setEditingShowId(null);
    setDraftShowTitle("");
    setDraftShowSummary("");
    setShowTextError("");
    setEditingWikiShowId(null);
    setDraftWikiUrl("");
    setWikiUrlError("");
    setSavingShowMeta(null);
    setShowMetaError("");
    setMotionBusy(false);
    setMotionError("");
    setMotionSearchQuery("");
    setMotionSearchError("");
    setMotionSearchResults([]);
  }, [show?.id]);

  useEffect(() => {
    setEditingVideoId(null);
    setDraftTitle("");
    setDraftSummary("");
    setEpisodeTextError("");
  }, [selectedVideoId]);

  useEffect(() => {
    onLightboxImageChange(lightboxImage);
  }, [lightboxImage, onLightboxImageChange]);

  useEffect(() => {
    if (!lightboxImage) {
      return;
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setLightboxImage(null);
      }
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [lightboxImage]);

  if (!show) {
    return null;
  }

  const activeShow = show;
  const editingShowText = editingShowId === activeShow.id;
  const editingWikiUrl = editingWikiShowId === activeShow.id;
  const activeWikiKind = wikiKind(activeShow.wikiUrl);
  const showSummaryText = activeShow.summary?.trim() || "";
  const showCharacters = activeShow.characters || [];
  const localizedTitles = activeShow.localizedTitles || [];
  const selectedVideo = selectedVideoId === null
    ? null
    : data.videos.find((video) => video.id === selectedVideoId) || null;
  const pathVideo = data.videos[0] || null;
  const showEpisodeText = Boolean(selectedVideo && selectedEpisode);
  const showPathText = pathVideo
    ? showEpisodeText
      ? dirNameFromPath(pathVideo.srcPath)
      : pathVideo.srcPath
    : "";
  const physicalEpisodeInfo = selectedEpisode?.sourceKind === "physical";
  const selectedEpisodeOwnsWholeVideo = Boolean(
    selectedEpisode &&
    selectedEpisode.startMs === 0 &&
    data.episodes.filter((episode) => episode.videoId === selectedEpisode.videoId).length === 1
  );
  const editingEpisodeText = Boolean(physicalEpisodeInfo && selectedVideo !== null && editingVideoId === selectedVideo.id);
  const deletingSelectedVideo = selectedVideo !== null && deletingVideoIds.includes(selectedVideo.id);
  const resplittingSelectedVideo = Boolean(
    selectedVideo && (
      resplittingVideoIds.includes(selectedVideo.id) ||
      isActiveBrowseEncodingJobStatus(selectedVideo.encodingJobStatus)
    )
  );
  const activeResplitVideoCount = data.videos.filter(
    (video) => !video.deleted && isActiveBrowseEncodingJobStatus(video.encodingJobStatus)
  ).length;
  const idleResplitVideoCount = data.videos.filter(
    (video) => !video.deleted && video.shotCount > 0 && !isActiveBrowseEncodingJobStatus(video.encodingJobStatus)
  ).length;
  const episodeTitleText = physicalEpisodeInfo
    ? selectedVideo?.title?.trim() || ""
    : selectedEpisode?.title?.trim() || "";
  const episodeSummaryText = physicalEpisodeInfo ? selectedVideo?.summary?.trim() || "" : "";
  const selectedEpisodeShots = selectedEpisode
    ? shots.filter((shot) => episodeContainsShot(selectedEpisode, shot) && !shot.softDeleted)
    : [];
  const selectedEpisodeDurationMs = selectedEpisode
    ? selectedEpisodeShots.reduce((sum, shot) => sum + shot.durMs, 0)
    : 0;
  const motion = data.motionStatus;
  const corpusReady = activeShow.status === "done" && activeShow.shotCount > 0;
  const motionEmbedded = Boolean(
    motion &&
      motion.totalShots > 0 &&
      motion.processedDone >= motion.totalShots &&
      motion.analysisActive === 0 &&
      motion.analysisFailed === 0
  );
  const motionHasEmbedding = Boolean(motion && motion.embeddingDone > 0);
  const motionButtonProgressText = motionProgressTextFromStatus(motion);
  const motionStatusText = motion
    ? `processed ${motion.processedDone}/${motion.totalShots} vlm ${motion.motionDone}/${motion.totalShots} embed ${motion.embeddingDone}/${motion.totalShots} pose ${motion.poseDone}/${motion.totalShots} depth ${motion.depthDone}/${motion.totalShots} edge ${motion.edgeDone}/${motion.totalShots} dialogue ${motion.dialogueDone} grammar ${motion.grammarDone}/${motion.totalShots}`
    : "not queued";

  function startShowTextEdit() {
    setEditingShowId(activeShow.id);
    setDraftShowTitle(activeShow.title);
    setDraftShowSummary(activeShow.summary || "");
    setShowTextError("");
  }

  function cancelShowTextEdit() {
    setEditingShowId(null);
    setDraftShowTitle("");
    setDraftShowSummary("");
    setShowTextError("");
  }

  async function saveShowText() {
    if (savingShowText) {
      return;
    }
    setSavingShowText(true);
    setShowTextError("");
    try {
      const response = await fetch(`/api/show-details/${activeShow.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title: draftShowTitle,
          summary: draftShowSummary
        })
      });
      const result = (await response.json().catch(() => null)) as
        | { show?: BrowseShow; error?: string }
        | null;
      if (!response.ok || !result?.show) {
        throw new Error(result?.error || "show save failed");
      }
      onShowTextSaved(result.show.id, result.show.title, result.show.summary);
      setEditingShowId(null);
      setDraftShowTitle("");
      setDraftShowSummary("");
    } catch (error) {
      setShowTextError(error instanceof Error ? error.message : "show save failed");
    } finally {
      setSavingShowText(false);
    }
  }

  async function enqueueMotionIndex() {
    if (motionBusy) {
      return;
    }
    if (motion?.source === "v4") {
      return;
    }
    if (!window.confirm(
      `Approve the current shot split of ${activeShow.title} for Motion Indexer v4?\n\nOnly this show and these current shot boundaries will be queued.`
    )) {
      return;
    }
    setMotionBusy(true);
    setMotionError("");
    try {
      const response = await fetch(`/api/show-motion-index/${activeShow.id}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ approveV4: true })
      });
      const result = (await response.json().catch(() => null)) as
        | { status?: BrowseShowMotionStatus; error?: string }
        | null;
      if (!response.ok || !result?.status) {
        throw new Error(result?.error || "embedding enqueue failed");
      }
      onMotionStatusUpdated(result.status);
    } catch (error) {
      setMotionError(error instanceof Error ? error.message : "embedding enqueue failed");
    } finally {
      setMotionBusy(false);
    }
  }

  async function runMotionSearch() {
    const query = motionSearchQuery.trim();
    if (!query || motionSearchBusy) {
      return;
    }
    setMotionSearchBusy(true);
    setMotionSearchError("");
    try {
      const response = await fetch("/api/motion-search", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ query, showId: activeShow.id, limit: 24 })
      });
      const result = (await response.json().catch(() => null)) as
        | { results?: MotionSearchResult[]; error?: string }
        | null;
      if (!response.ok || !result?.results) {
        throw new Error(result?.error || "motion search failed");
      }
      setMotionSearchResults(result.results);
    } catch (error) {
      setMotionSearchError(error instanceof Error ? error.message : "motion search failed");
    } finally {
      setMotionSearchBusy(false);
    }
  }

  function cancelWikiUrlEdit() {
    setEditingWikiShowId(null);
    setDraftWikiUrl("");
    setWikiUrlError("");
  }

  async function saveWikiUrl() {
    if (savingWikiUrl) {
      return;
    }
    setSavingWikiUrl(true);
    setWikiUrlError("");
    try {
      const response = await fetch(`/api/show-wiki/${activeShow.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ wikiUrl: draftWikiUrl })
      });
      const result = (await response.json().catch(() => null)) as
        | { show?: BrowseShow; error?: string }
        | null;
      if (!response.ok || !result?.show) {
        throw new Error(result?.error || "wiki save failed");
      }
      onShowWikiUrlSaved(result.show.id, result.show.wikiUrl);
      cancelWikiUrlEdit();
    } catch (error) {
      setWikiUrlError(error instanceof Error ? error.message : "wiki save failed");
    } finally {
      setSavingWikiUrl(false);
    }
  }

  async function saveShowMeta(patch: { type?: number; lang?: string }) {
    const field = patch.type !== undefined ? "type" : "lang";
    if (savingShowMeta === field) {
      return;
    }
    setSavingShowMeta(field);
    setShowMetaError("");
    try {
      const response = await fetch(`/api/show-meta/${activeShow.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch)
      });
      const result = (await response.json().catch(() => null)) as
        | { show?: BrowseShow; error?: string }
        | null;
      if (!response.ok || !result?.show) {
        throw new Error(result?.error || "show meta save failed");
      }
      onShowMetaSaved(result.show);
    } catch (error) {
      setShowMetaError(error instanceof Error ? error.message : "show meta save failed");
    } finally {
      setSavingShowMeta(null);
    }
  }

  function startEpisodeTextEdit() {
    if (!selectedVideo || !physicalEpisodeInfo) {
      return;
    }
    setEditingVideoId(selectedVideo.id);
    setDraftTitle(selectedVideo.title || "");
    setDraftSummary(selectedVideo.summary || "");
    setEpisodeTextError("");
  }

  function cancelEpisodeTextEdit() {
    setEditingVideoId(null);
    setDraftTitle("");
    setDraftSummary("");
    setEpisodeTextError("");
  }

  async function saveEpisodeText() {
    if (!selectedVideo || !physicalEpisodeInfo || savingEpisodeText) {
      return;
    }
    setSavingEpisodeText(true);
    setEpisodeTextError("");
    try {
      const response = await fetch(`/api/video-details/${selectedVideo.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title: draftTitle,
          summary: draftSummary
        })
      });
      const result = (await response.json().catch(() => null)) as
        | { video?: BrowseVideo; error?: string }
        | null;
      if (!response.ok || !result?.video) {
        throw new Error(result?.error || "episode text save failed");
      }
      onVideoEpisodeTextSaved(result.video.id, result.video.title, result.video.summary);
      setEditingVideoId(null);
      setDraftTitle("");
      setDraftSummary("");
    } catch (error) {
      setEpisodeTextError(error instanceof Error ? error.message : "episode text save failed");
    } finally {
      setSavingEpisodeText(false);
    }
  }

  function copyVideoListAsTsv(event: React.ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    const selection = window.getSelection();
    const selectedIds = new Set<number>();
    if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
      const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("[data-video-id]"));
      for (const row of rows) {
        const videoId = Number(row.dataset.videoId);
        if (!Number.isFinite(videoId)) {
          continue;
        }
        for (let index = 0; index < selection.rangeCount; index += 1) {
          try {
            if (selection.getRangeAt(index).intersectsNode(row)) {
              selectedIds.add(videoId);
              break;
            }
          } catch {
            // Ignore detached nodes from a stale selection range.
          }
        }
      }
    }
    const copiedVideos = selectedIds.size > 0
      ? data.videos.filter((video) => selectedIds.has(video.id))
      : data.videos.filter((video) => showDeleted || !video.deleted);
    event.clipboardData.setData("text/plain", copiedVideos.map((video) => videoTsvLine(video, showSeasonLabels)).join("\n"));
  }

  function renderWikiControl() {
    if (editingWikiUrl) {
      return (
        <form
          className="showWikiForm"
          onSubmit={(event) => {
            event.preventDefault();
            void saveWikiUrl();
          }}
        >
          <input
            autoFocus
            disabled={savingWikiUrl}
            onChange={(event) => setDraftWikiUrl(event.target.value)}
            placeholder="https://..."
            type="text"
            value={draftWikiUrl}
          />
          <button disabled={savingWikiUrl || !draftWikiUrl.trim()} type="submit">
            Save
          </button>
          <button disabled={savingWikiUrl} onClick={cancelWikiUrlEdit} type="button">
            Cancel
          </button>
          {wikiUrlError ? <strong>{wikiUrlError}</strong> : null}
        </form>
      );
    }

    if (activeShow.wikiUrl) {
      return (
        <a
          className={`showWikiLink ${activeWikiKind || "wiki"}`}
          href={activeShow.wikiUrl}
          rel="noreferrer"
          target="_blank"
          title={activeShow.wikiUrl}
        >
          {activeWikiKind === "namu" ? (
            <img alt="" className="showWikiIcon" src={NAMU_WIKI_ICON_URL} />
          ) : activeWikiKind === "wikipedia" ? (
            <img alt="" className="showWikiIcon" src={WIKIPEDIA_ICON_URL} />
          ) : (
            "wiki"
          )}
        </a>
      );
    }

    return (
      <button
        className="showWikiButton"
        onClick={() => {
          setEditingWikiShowId(activeShow.id);
          setDraftWikiUrl("");
          setWikiUrlError("");
        }}
        type="button"
      >
        +wiki
      </button>
    );
  }

  function renderShowMetaControls() {
    const hasKnownType = SHOW_TYPE_OPTIONS.some((option) => option.value === activeShow.type);
    const hasKnownLang = SHOW_LANG_OPTIONS.includes(activeShow.lang);
    return (
      <>
        <select
          aria-label="Show type"
          className="showMetaSelect"
          disabled={savingShowMeta === "type"}
          onChange={(event) => {
            const type = Number(event.target.value);
            if (Number.isFinite(type) && type !== activeShow.type) {
              void saveShowMeta({ type });
            }
          }}
          title="Show type"
          value={String(activeShow.type)}
        >
          {!hasKnownType ? <option value={activeShow.type}>{activeShow.typeLabel}</option> : null}
          {SHOW_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Show language"
          className="showMetaSelect"
          disabled={savingShowMeta === "lang"}
          onChange={(event) => {
            const lang = event.target.value;
            if (lang && lang !== activeShow.lang) {
              void saveShowMeta({ lang });
            }
          }}
          title="Show language"
          value={activeShow.lang}
        >
          {!hasKnownLang ? <option value={activeShow.lang}>{activeShow.lang}</option> : null}
          {SHOW_LANG_OPTIONS.map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </select>
      </>
    );
  }

  return (
    <div className="showInfoPanel">
      {showEpisodeText && selectedVideo && selectedEpisode ? (
        <section className="infoBlock episodeInfoBlock" aria-label="Episode title and summary">
          {physicalEpisodeInfo ? (
            <button
              aria-label={selectedVideo.deleted ? "Restore episode" : "Delete episode"}
              className={`episodeDeleteButton ${selectedVideo.deleted ? "restore" : ""}`}
              disabled={deletingSelectedVideo}
              onClick={() => onDeleteEpisode(selectedVideo.id, !selectedVideo.deleted)}
              title={selectedVideo.deleted ? "Restore episode" : "Delete episode"}
              type="button"
            >
              {selectedVideo.deleted ? <RotateCcw size={14} /> : <Trash2 size={14} />}
            </button>
          ) : null}
          <div className="episodeTitleLine">
            {editingEpisodeText ? (
              <textarea
                className="episodeTitleTextarea"
                disabled={savingEpisodeText}
                maxLength={1024}
                onChange={(event) => setDraftTitle(event.target.value)}
                placeholder={browseEpisodeLabel(selectedEpisode, showSeasonLabels)}
                rows={2}
                value={draftTitle}
              />
            ) : (
              <h2 className="episodeInfoTitle">
                <span>{browseEpisodeLabel(selectedEpisode, showSeasonLabels)}{episodeTitleText ? ` ${episodeTitleText}` : ""}</span>
                {physicalEpisodeInfo ? (
                  <button
                    aria-label="Edit episode title and summary"
                    className="episodeEditIcon"
                    onClick={startEpisodeTextEdit}
                    title="Edit"
                    type="button"
                  >
                    <Pencil size={15} />
                  </button>
                ) : null}
              </h2>
            )}
          </div>

          {editingEpisodeText ? (
            <div className="episodeSummaryEditor">
              <textarea
                disabled={savingEpisodeText}
                maxLength={1000000}
                onChange={(event) => setDraftSummary(event.target.value)}
                rows={6}
                value={draftSummary}
              />
              {episodeTextError ? <div className="episodeEditError">{episodeTextError}</div> : null}
              <div className="episodeEditActions">
                <button disabled={savingEpisodeText} onClick={cancelEpisodeTextEdit} type="button">
                  Cancel
                </button>
                <button disabled={savingEpisodeText} onClick={saveEpisodeText} type="button">
                  {savingEpisodeText ? "Saving" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="episodeSummaryText">
              <p className="episodeFilename">{fileNameFromPath(selectedVideo.srcPath)}</p>
              <p className="episodeInfoMeta">
                (video.id={selectedVideo.id}, duration={formatCompactDuration(selectedEpisodeDurationMs)}, shots={selectedEpisodeShots.length})
              </p>
              {physicalEpisodeInfo || episodeSummaryText ? (
                <p className={episodeSummaryText ? "" : "empty"}>{episodeSummaryText || "No summary yet"}</p>
              ) : null}
            </div>
          )}
          {selectedEpisodeOwnsWholeVideo && selectedVideo.shotCount > 0 && !selectedVideo.deleted ? (
            <div className="episodeInfoActionRow">
              <button
                aria-label="Re-split episode"
                className="episodeResplitButton"
                disabled={resplittingSelectedVideo}
                onClick={() => onResplitEpisode(selectedVideo.id)}
                title="Re-detect and re-encode only this episode video"
                type="button"
              >
                <Scissors size={15} />
                <span>{resplittingSelectedVideo ? "Re-splitting" : "Re-split episode"}</span>
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="infoBlock showInfoBlock" aria-label="Show title and summary">
        <div className="showTitleLine">
          {editingShowText ? (
            <textarea
              className="showTitleTextarea"
              disabled={savingShowText}
              maxLength={255}
              onChange={(event) => setDraftShowTitle(event.target.value)}
              rows={2}
              value={draftShowTitle}
            />
          ) : (
            <h2 className="showInfoTitle">
              <span>{activeShow.title}</span>
              <button
                aria-label="Edit show title and summary"
                className="episodeEditIcon"
                onClick={startShowTextEdit}
                title="Edit"
                type="button"
              >
                <Pencil size={15} />
              </button>
            </h2>
          )}
        </div>
        <div className="showInfoActionRow">
          {renderShowMetaControls()}
          {renderWikiControl()}
          {corpusReady ? (
            <button
              aria-label="Index this show"
              className={`showEmbeddingButton ${motionEmbedded ? "embedded" : ""}`}
              disabled={motionBusy || motion?.source === "v4"}
              onClick={enqueueMotionIndex}
              title={motion ? motionStatusText : "Index this show for motion search"}
              type="button"
            >
              <span className="showEmbeddingButtonStar">★</span>
              <span>
                {motionBusy
                  ? "Queueing"
                  : motion?.source === "v4" && motion.approvalRequestId && !motion.generationId
                    ? "Queued"
                  : motionEmbedded
                    ? "Indexed"
                    : motionButtonProgressText
                      ? "Indexing"
                      : "Index"}
              </span>
            </button>
          ) : null}
          {data.videos.some((video) => !video.deleted && video.shotCount > 0) ? (
            <button
              aria-label="Re-split show"
              className="showResplitButton"
              disabled={resplittingShowId === activeShow.id || idleResplitVideoCount === 0}
              onClick={() => onResplitShow(activeShow.id)}
              title={
                activeResplitVideoCount > 0
                  ? `Queue ${idleResplitVideoCount} idle episode video(s); keep ${activeResplitVideoCount} active video(s) untouched`
                  : "Re-detect and re-encode every idle video in this show"
              }
              type="button"
            >
              <Scissors size={15} />
              <span>
                {resplittingShowId === activeShow.id
                  ? "Queueing"
                  : activeResplitVideoCount > 0
                    ? "Re-split remaining"
                    : "Re-split show"}
              </span>
            </button>
          ) : null}
        </div>
        {showMetaError ? <div className="showMetaError">{showMetaError}</div> : null}
        {editingShowText ? (
          <div className="showSummaryEditor">
            <textarea
              disabled={savingShowText}
              maxLength={1000000}
              onChange={(event) => setDraftShowSummary(event.target.value)}
              rows={6}
              value={draftShowSummary}
            />
            {showTextError ? <div className="episodeEditError">{showTextError}</div> : null}
            <div className="episodeEditActions">
              <button disabled={savingShowText} onClick={cancelShowTextEdit} type="button">
                Cancel
              </button>
              <button disabled={savingShowText} onClick={saveShowText} type="button">
                {savingShowText ? "Saving" : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div className="showInfoBody">
            {activeShow.thumbUrl ? (
              <button
                className="showInfoThumbButton"
                onClick={() => setLightboxImage(activeShow.thumbUrl)}
                title="Open image"
                type="button"
              >
                <img
                  alt=""
                  className="showInfoThumb"
                  decoding="async"
                  loading="lazy"
                  src={activeShow.thumbUrl}
                />
              </button>
            ) : null}
            {localizedTitles.length > 0 ? (
              <div className="showLocalizedTitles">{localizedTitles.join(" / ")}</div>
            ) : null}
            <div className="showInfoMetaLine">
              (show.id={activeShow.id}, duration={formatCompactDuration(activeShow.durationMs)}, videos={activeShow.videoCount})
            </div>
            <p className={`showSummaryText ${showSummaryText ? "" : "empty"}`}>
              {showSummaryText || "No summary yet"}
            </p>
            <ShowCharacters characters={showCharacters} onImageClick={setLightboxImage} />
          </div>
        )}
        <div className="showMotionStatus">
          <span>{motionStatusText}</span>
          {motion?.motionFailed || motion?.embeddingFailed || motion?.analysisFailed ? (
            <strong>{motion.motionFailed + motion.embeddingFailed + motion.analysisFailed} failed</strong>
          ) : null}
          {motionError ? <strong>{motionError}</strong> : null}
        </div>
        <form
          className="motionSearchForm"
          onSubmit={(event) => {
            event.preventDefault();
            void runMotionSearch();
          }}
        >
          <input
            disabled={motionSearchBusy}
            onChange={(event) => setMotionSearchQuery(event.target.value)}
            placeholder="running, handheld chase, close-up reaction..."
            type="search"
            value={motionSearchQuery}
          />
          <button disabled={motionSearchBusy || !motionSearchQuery.trim()} title="Search motion" type="submit">
            <Search size={15} />
          </button>
        </form>
        {motionSearchError ? <div className="motionSearchError">{motionSearchError}</div> : null}
        {motionSearchResults.length > 0 ? (
          <div className="motionSearchResults">
            {motionSearchResults.map((result) => (
              <a
                className="motionSearchResult"
                href={result.videoUrl}
                key={result.shotId}
                target="_blank"
                rel="noreferrer"
                title={result.searchTextEn || result.captionEn || `shot ${result.shotNo}`}
              >
                <img alt="" decoding="async" loading="lazy" src={result.thumbUrl} />
                <span>
                  {episodeLabel(result.seasonNo, result.episodeNo, showSeasonLabels)} · #{result.shotNo}
                  <small>{result.captionEn || result.searchTextEn || "motion result"}</small>
                </span>
                <em>{result.score.toFixed(3)}</em>
              </a>
            ))}
          </div>
        ) : null}
        <div className="showInfoSection">
          <h3>Videos</h3>
          {showPathText ? <div className="showVideoPathRow">{showPathText}</div> : null}
          <div className="showInfoVideoList" onCopy={copyVideoListAsTsv}>
            {/* soft-deleted video는 'Show Deleted' 토글을 켜지 않는 한 목록에서 뺀다 —
                나머지 workspace(visibleVideos)와 같은 규칙으로, re-split 대상과 목록을 일치시킨다. */}
            {data.videos.filter((video) => showDeleted || !video.deleted).map((video) => (
              <ShowInfoVideoRow
                key={video.id}
                onRetry={onRetryVideo}
                retrying={retryingVideoIds.includes(video.id)}
                selected={video.id === selectedVideo?.id}
                showSeasonLabel={showSeasonLabels}
                video={video}
              />
            ))}
          </div>
        </div>
      </section>
      {lightboxImage ? (
        <button
          aria-label="Close image preview"
          className="imageLightbox"
          onClick={() => setLightboxImage(null)}
          type="button"
        >
          <img alt="" src={lightboxImage} />
        </button>
      ) : null}
    </div>
  );
}

function ShowCharacters({
  characters,
  onImageClick
}: {
  characters: BrowseCharacter[];
  onImageClick: (imageUrl: string) => void;
}) {
  if (characters.length === 0) {
    return null;
  }
  return (
    <div className="showCharacters">
      {characters.map((character, index) => (
        <article className="showCharacter" key={`${character.name}-${character.actor || index}`}>
          {character.imageUrl ? (
            <button
              className="showCharacterImageButton"
              onClick={() => onImageClick(character.imageUrl || "")}
              title="Open image"
              type="button"
            >
              <img alt="" className="showCharacterImage" loading="lazy" src={character.imageUrl} />
            </button>
          ) : (
            <div className="showCharacterImage empty" />
          )}
          <div className="showCharacterText">
            <strong>
              {character.name}
              {character.actor ? <span>{character.actor}</span> : null}
            </strong>
            {character.descr ? <p>{character.descr}</p> : null}
          </div>
        </article>
      ))}
    </div>
  );
}

function ShowInfoVideoRow({
  onRetry,
  retrying,
  selected,
  showSeasonLabel,
  video
}: {
  onRetry: (videoId: number) => void;
  retrying: boolean;
  selected: boolean;
  showSeasonLabel: boolean;
  video: BrowseVideo;
}) {
  return (
    <div className={`showInfoVideoRow ${selected ? "active" : ""}`} data-video-id={video.id}>
      <span className="videoIdCell">{video.id}</span>
      <span>{episodeLabel(video.seasonNo, video.episodeNo, showSeasonLabel)}</span>
      <div className="videoProgressCell">
        <span>{videoProgressLabel(video)}</span>
        {video.progressTotal > 0 && video.progressStage > 0 && video.progressStage < 5 ? (
          <span className="videoProgressTrack">
            <span className="videoProgressFill" style={{ width: `${video.progressPercent}%` }} />
          </span>
        ) : null}
      </div>
      <span>{videoShotCountLabel(video)} <span className="videoUnit">shots</span></span>
      <span>{video.width > 0 && video.height > 0 ? video.width : "-"}<span className="videoUnit">x</span>{video.height > 0 ? video.height : ""}</span>
      <span>{video.fpsDen > 0 ? <>{(video.fpsNum / video.fpsDen).toFixed(3)} <span className="videoUnit">fps</span></> : "—"}</span>
      <span>{video.srcSizeBytes > 0 ? <>{videoSizeLabel(video.srcSizeBytes)} <span className="videoUnit">Mb</span></> : "—"}</span>
      <span
        className={`videoSplitRevision ${video.splitGbdtRevision === 0 ? "legacy" : video.splitGbdtRevisionGap > 0 ? "stale" : "current"}`}
        title={video.splitGbdtRevision === 0
          ? "Processed before traceable GBDT lineage"
          : `GBDT revision ${video.splitGbdtRevision}; latest ${video.splitGbdtLatestRevision}`}
      >
        {video.splitGbdtRevision === 0
          ? "legacy"
          : video.splitGbdtRevisionGap === 0
            ? "0"
            : `-${video.splitGbdtRevisionGap}`}
      </span>
      {video.status === "failed" ? (
        <button
          className="videoRetryButton"
          disabled={retrying}
          onClick={() => onRetry(video.id)}
          title="Retry shot making"
          type="button"
        >
          <RotateCcw size={13} />
          <span>{retrying ? "retrying" : "retry"}</span>
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

function MultiShotPanel({
  deletePending,
  goodPending,
  ignorePending,
  mergePending,
  onDelete,
  onGood,
  onIgnore,
  onMerge,
  shots
}: {
  deletePending: boolean;
  goodPending: boolean;
  ignorePending: boolean;
  mergePending: boolean;
  onDelete: () => void;
  onGood: () => void;
  onIgnore: () => void;
  onMerge: () => void;
  shots: BrowseShot[];
}) {
  const contiguous = shots.every((shot, index) => index === 0 || shot.shotNo === shots[index - 1].shotNo + 1);
  const actionPending = deletePending || goodPending || ignorePending || mergePending;
  const deleteVerb = shots.length > 0 && shots.every((shot) => shot.softDeleted) ? "restore" : "delete";
  const deletePendingLabel = deleteVerb === "restore" ? "restoring" : "deleting";

  return (
    <div className="multiShotPanel">
      <div className="multiShotHeader">
        <h2>{shots.length} item selected</h2>
      </div>
      <div className="previewActions">
        <button
          className="sceneStartButton"
          disabled={actionPending}
          onClick={onGood}
          type="button"
        >
          {goodPending ? <LoaderCircle className="spinIcon" size={14} /> : <Check size={14} />}
          <span>{goodPending ? "marking" : "all good"}</span>
        </button>
        <button
          className="sceneStartButton"
          disabled={actionPending}
          onClick={onIgnore}
          type="button"
        >
          {ignorePending ? <LoaderCircle className="spinIcon" size={14} /> : <EyeOff size={14} />}
          <span>{ignorePending ? "ignoring" : "ignore all"}</span>
        </button>
        <button
          className="dangerActionButton"
          disabled={actionPending}
          onClick={onDelete}
          type="button"
        >
          {deletePending ? deletePendingLabel : deleteVerb}
        </button>
        <button
          className="sceneStartButton"
          disabled={actionPending || !contiguous || shots.length < 2}
          onClick={onMerge}
          type="button"
        >
          {mergePending ? "marking" : "merge shots"}
        </button>
      </div>
    </div>
  );
}

function shotHelpLabel(type: string) {
  if (type.startsWith("over_split")) {
    return "Merge?";
  }
  if (type === "under_split_candidate") {
    return "Split?";
  }
  return type.replace(/_/g, " ");
}

function videoBufferedToEnd(video: HTMLVideoElement) {
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    return video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA;
  }
  const duration = video.duration;
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (video.buffered.start(index) <= 0.08 && video.buffered.end(index) >= duration - 0.08) {
      return true;
    }
  }
  return false;
}

function previewVideoStartUrl(url: string, timeOrigin = 0, duration = 0) {
  if (!url) {
    return "";
  }
  if (url.includes("#")) {
    return url;
  }
  // 원본 전체 파일을 재생할 때는 media fragment로 브라우저가 곧장 해당 구간을
  // range 요청하게 만든다. shot mp4(원점 0)는 기존처럼 0.001만 준다.
  if (timeOrigin > 0 && duration > 0) {
    return `${url}#t=${timeOrigin.toFixed(3)},${(timeOrigin + duration).toFixed(3)}`;
  }
  return `${url}#t=0.001`;
}

function subtitleTrackForMode(shot: BrowseShot, mode: PreviewSubtitleMode) {
  const requestedLanguage = mode === "original"
    ? shot.subtitleOriginalLanguage
    : shot.subtitleFamiliarLanguage;
  const otherLanguage = mode === "original"
    ? shot.subtitleFamiliarLanguage
    : shot.subtitleOriginalLanguage;
  return (
    shot.subtitleTracks.find((track) => track.language === requestedLanguage) ||
    shot.subtitleTracks.find((track) => track.language === otherLanguage) ||
    shot.subtitleTracks[0] ||
    null
  );
}

function canSwitchSubtitleLanguage(shot: BrowseShot) {
  const originalLanguage = shot.subtitleOriginalLanguage;
  const familiarLanguage = shot.subtitleFamiliarLanguage;
  return Boolean(
    originalLanguage &&
    familiarLanguage &&
    originalLanguage !== familiarLanguage &&
    shot.subtitleTracks.some((track) => track.language === originalLanguage) &&
    shot.subtitleTracks.some((track) => track.language === familiarLanguage)
  );
}

type MotionPacketRecord = Record<string, unknown>;

type ShotDetectedMotion = {
  generationId: number | null;
  generationLabel: string;
  preview: boolean;
  scope: "approved_show" | "sample" | "legacy";
  shotSearchTextEn: string | null;
  motionPacket: MotionPacketRecord;
};

type ShotDetectedVisual = {
  packet: Record<string, unknown> | null;
  textEn: string;
  embedStatus: number;
};

function VisualDetectedAttributes({ visual }: { visual: ShotDetectedVisual }) {
  const packet = visual.packet || {};
  const list = (key: string) => Array.isArray(packet[key])
    ? packet[key].map((item) => String(item)).filter(Boolean)
    : [];
  const chips = [
    String(packet.era || ""),
    String(packet.time_of_day || ""),
    ...list("weather"),
    ...list("attire"),
    ...list("setting"),
    ...list("environment"),
    ...list("objects")
  ].filter((item) => item && item !== "unknown" && item !== "interior_ambient" ? true : item === "interior_ambient");
  const legibility = String(packet.legibility || "");
  return (
    <section className="visualDetectedAttributes">
      <div className="motionDetectedHeader">
        <h3>Visual</h3>
        <div className="motionDetectedBadges">
          {visual.embedStatus === 2 ? <span>indexed</span> : null}
          {legibility && legibility !== "clear" ? <span className="motionDetectedWarn">{legibility}</span> : null}
        </div>
      </div>
      {chips.length > 0 ? <p className="visualDetectedChips">{chips.join(" · ")}</p> : null}
      {visual.textEn ? <p className="visualDetectedText">{visual.textEn}</p> : null}
    </section>
  );
}

function motionRecord(value: unknown): MotionPacketRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as MotionPacketRecord
    : null;
}

function motionArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function motionText(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const items = value.map(motionText).filter((item): item is string => Boolean(item));
    return items.length > 0 ? items.join(", ") : null;
  }
  return null;
}

function motionPath(packet: MotionPacketRecord, path: string) {
  let value: unknown = packet;
  for (const key of path.split(".")) {
    value = motionRecord(value)?.[key];
  }
  return value;
}

function motionSeconds(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? `${(number / 1000).toFixed(2)}s` : "-";
}

function MotionAttributeRows({ rows }: { rows: Array<[string, unknown]> }) {
  const visibleRows = rows
    .map(([label, value]) => [label, motionText(value)] as const)
    .filter((row): row is readonly [string, string] => row[1] !== null);
  if (visibleRows.length === 0) return <p className="motionDetectedEmpty">검출값 없음</p>;
  return (
    <dl className="motionDetectedRows">
      {visibleRows.map(([label, value]) => (
        <div className="motionDetectedRow" key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** shot 하나의 분석 결과. motion layer v8이면 동작 타임라인을, 구 packet이면 요약만 보여준다.
 *  구 facet 격자(성별·연령·시선·활동분류·센서)는 검색에서 폐기했으므로 여기서도 걷어냈다. */
function MotionDetectedAttributes({ detected }: { detected: ShotDetectedMotion }) {
  const packet = detected.motionPacket;
  const layer = motionRecord(packet.motion_layer);
  const cameraEntry = layer ? motionRecord(motionArray(layer.camera)[0]) : motionRecord(packet.camera);
  const beats = layer ? motionArray(layer.beats).map(motionRecord).filter((item): item is MotionPacketRecord => item !== null) : [];
  const held = layer ? motionArray(layer.held_objects).map(motionRecord).filter((item): item is MotionPacketRecord => item !== null) : [];
  const emotions = layer ? motionArray(layer.emotion_search).map((item) => String(item)) : [];
  const warnings = layer ? motionArray(layer.warnings).map((item) => String(item)) : [];
  const legibility = layer ? String(layer.legibility || "") : "";
  const durationMs = layer ? Number(layer.duration_ms || 0) : 0;

  const cameraLine = cameraEntry
    ? [
      cameraEntry.framing,
      cameraEntry.layout,
      cameraEntry.angle ? `${cameraEntry.angle} angle` : "",
      [cameraEntry.motion, cameraEntry.amplitude, cameraEntry.speed].filter(Boolean).join(" ")
    ].filter(Boolean).map(String).join(" · ")
    : "";

  return (
    <section className="motionDetectedAttributes">
      <div className="motionDetectedHeader">
        <h3>Motion</h3>
        <div className="motionDetectedBadges">
          <span>{detected.generationLabel || (detected.generationId ? `gen ${detected.generationId}` : "legacy")}</span>
          {detected.preview ? <span>staging</span> : null}
          {legibility && legibility !== "clear" ? <span className="motionDetectedWarn">{legibility}</span> : null}
        </div>
      </div>

      {cameraLine ? <p className="motionDetectedCamera">{cameraLine}</p> : null}

      {layer ? (
        <>
          {beats.length > 0 ? (
            <ol className="motionDetectedBeats">
              {beats.map((beat, index) => {
                const speech = motionRecord(beat.speech);
                const start = Number(beat.t || 0);
                const next = beats[index + 1];
                const end = next ? Number(next.t || 0) : durationMs / 1000;
                return (
                  <li key={`${start}-${index}`}>
                    <span className="motionDetectedBeatTime">{start.toFixed(1)}–{end.toFixed(1)}s</span>
                    {Number(beat.subject || 1) > 1 ? <span className="motionDetectedBeatSubject">#{String(beat.subject)}</span> : null}
                    <span>{String(beat.physical || "")}</span>
                    {speech ? <em>{String(speech.delivery)}</em> : null}
                  </li>
                );
              })}
            </ol>
          ) : null}
          {held.length > 0 ? (
            <p className="motionDetectedHeld">
              {held.map((item) => [item.object, item.path].filter(Boolean).map(String).join(" — ")).join(" / ")}
            </p>
          ) : null}
          {emotions.length > 0 ? (
            <p className="motionDetectedEmotions">{emotions.map((word) => <span key={word}>{word}</span>)}</p>
          ) : null}
          {warnings.length > 0 ? (
            <details className="motionDetectedWarnings">
              <summary>정규화 경고 {warnings.length}</summary>
              <ul>{warnings.map((item, index) => <li key={index}>{item}</li>)}</ul>
            </details>
          ) : null}
        </>
      ) : (
        detected.shotSearchTextEn ? <p className="motionDetectedSearchText">{detected.shotSearchTextEn}</p> : null
      )}

      <details className="motionDetectedRaw">
        <summary>전체 검출 JSON</summary>
        <pre>{JSON.stringify(packet, null, 2)}</pre>
      </details>
    </section>
  );
}

function PreviewPanel({
  adjacencyShots,
  episodes,
  mediaBaseUrl,
  onDeleteShot,
  onApplySplitHelpReview,
  onSetSplitHelpIgnored,
  onReviewTabJumpNext,
  onSetEpisodeStart,
  onSetSceneStart,
  onMarkIntroEnd,
  onMarkOutroStart,
  onReprocessShotMotion,
  onSplitShot,
  onPlaybackShotChange,
  pendingDeleteShotKeys,
  pendingEpisodeShotKey,
  pendingMotionReprocessShotIds,
  pendingSplitHelpShotIds,
  playlist,
  playRequestId,
  rangeMarkPending,
  sceneStartPendingKey,
  showType,
  splitJobs,
  splitPendingShotKeys,
  shot,
  splitHelpOverride,
  splitHelpOverrides,
  video
}: {
  adjacencyShots: BrowseShot[];
  episodes: BrowseEpisode[];
  mediaBaseUrl: string;
  onApplySplitHelpReview: (shot: BrowseShot, issueMask: number) => Promise<boolean>;
  onSetSplitHelpIgnored: (shot: BrowseShot, ignored: boolean) => Promise<boolean>;
  onReviewTabJumpNext: () => boolean;
  onDeleteShot: (shot: BrowseShot) => void;
  onSetEpisodeStart: (shot: BrowseShot, action: "set" | "unset") => void;
  onSetSceneStart: (shot: BrowseShot, enabled: boolean) => void;
  onMarkIntroEnd: (shot: BrowseShot) => void;
  onMarkOutroStart: (shot: BrowseShot) => void;
  onPlaybackShotChange: (shotKey: string) => void;
  onReprocessShotMotion: (shot: BrowseShot) => void;
  onSplitShot: (shot: BrowseShot) => void;
  pendingDeleteShotKeys: string[];
  pendingEpisodeShotKey: string | null;
  pendingMotionReprocessShotIds: number[];
  pendingSplitHelpShotIds: number[];
  playlist: BrowseShot[];
  playRequestId: number;
  rangeMarkPending: { shotKey: string; mode: "intro_end" | "outro_start" } | null;
  sceneStartPendingKey: string | null;
  showType: number;
  splitJobs: BrowseShotEditJob[];
  splitPendingShotKeys: string[];
  shot: BrowseShot;
  splitHelpOverride?: SplitHelpReviewOverride;
  splitHelpOverrides: Record<number, SplitHelpReviewOverride>;
  video: BrowseVideo | null;
}) {
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const poseGhostVideoRef = useRef<HTMLVideoElement>(null);
  const depthCanvasRef = useRef<HTMLCanvasElement>(null);
  const depthRangeRailRef = useRef<HTMLDivElement>(null);
  const previewZoneRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [continuousPlayback, setContinuousPlayback] = useState(false);
  const [playbackShotKey, setPlaybackShotKey] = useState(shot.key);
  const [infoShotKey, setInfoShotKey] = useState(shot.key);
  const goodAdvanceCursorRef = useRef(shot.key);
  const keyboardReviewActionPendingRef = useRef(false);
  const [activePlayerIndex, setActivePlayerIndex] = useState(0);
  const [autoplayRequest, setAutoplayRequest] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(storedPreviewPlaybackRate);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(Math.max(0.01, shot.durMs / 1000));
  const [nextPreloadEnabled, setNextPreloadEnabled] = useState(false);
  const [keyframes, setKeyframes] = useState<number[]>([]);
  const [previewSource, setPreviewSource] = useState<PreviewSource>(storedPreviewSource);
  const [subtitleMode, setSubtitleMode] = useState<PreviewSubtitleMode>("familiar");
  const [depthRange, setDepthRange] = useState({ low: 0, high: 100 });
  const [subtitlesCopied, setSubtitlesCopied] = useState(false);
  const [videoDownloadBusy, setVideoDownloadBusy] = useState(false);
  const [detectedMotion, setDetectedMotion] = useState<ShotDetectedMotion | null>(null);
  const [detectedVisual, setDetectedVisual] = useState<ShotDetectedVisual | null>(null);
  // "index now" 우선 처리: idle → busy(요청 중) → waiting(큐에 들어가 결과 폴링 중) → idle(도착)
  const [priorityIndexState, setPriorityIndexState] = useState<"idle" | "busy" | "waiting" | "error">("idle");
  const [priorityIndexMessage, setPriorityIndexMessage] = useState("");
  const priorityIndexShotIdRef = useRef(0);
  const [detectedReloadTick, setDetectedReloadTick] = useState(0);
  const priorityIndexStartedAtRef = useRef(0);
  const [detectedMotionLoading, setDetectedMotionLoading] = useState(false);
  const [detectedMotionError, setDetectedMotionError] = useState<string | null>(null);
  const internalPlaybackShotKeyRef = useRef<string | null>(null);
  const lastPreviewToggleTsRef = useRef(0);
  const previewPointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const isScrubbingRef = useRef(false);
  const manualPreviewSeekRef = useRef(false);
  const scrubWasPlayingRef = useRef(false);
  const previewClockFrameRef = useRef<number | null>(null);
  const progressScrubRef = useRef<ProgressScrubState | null>(null);
  const progressDragCleanupRef = useRef<(() => void) | null>(null);
  const keyframeCacheRef = useRef(new Map<string, number[]>());
  const keyframeLoadingRef = useRef(new Map<string, Promise<number[]>>());
  const playbackShot = playlist.find((item) => item.key === playbackShotKey) || shot;
  // '지금 처리' — 이 shot의 세로 outpaint를 claim 대기열 앞쪽으로 새치기시킨다.
  // NPU doodle 뷰의 전제 조건(세로)을 즉시 채우는 어드민 경로다.
  const [h2vPrioritizeBusy, setH2vPrioritizeBusy] = useState(false);
  const [doodleRequestState, setDoodleRequestState] = useState<"idle" | "outpainting" | "doodling" | "error">("idle");
  const [doodleRequestMessage, setDoodleRequestMessage] = useState("");
  const [doodleReloadTick, setDoodleReloadTick] = useState(0);
  /// 벡터 doodle: depth 영상 위에 프리미티브(눈/홍채/입/눈썹 선)를 캔버스로 즉석 렌더한다.
  /// 프리미티브 JSON은 doodle 생성 시 함께 떨어지는 정적 파일(.mesh.primitives.json).
  type DoodlePrimitives = {
    v: number; fps: number | string; frameCount: number;
    persons: Array<{ frames: Array<Record<string, unknown> & { f: number }> }>;
  };
  const [doodleVectorData, setDoodleVectorData] = useState<{ primitives: DoodlePrimitives; depthUrl: string } | null>(null);
  const [doodlePrimitivesMissing, setDoodlePrimitivesMissing] = useState(false);
  const doodleVectorCanvasRef = useRef<HTMLCanvasElement>(null);
  // doodleVideoUrl에는 항상 캐시버스터(?v=…)가 붙는다. /\.mp4$/ 교체는 쿼리 때문에
  // 매칭에 실패하고, 그 상태로 fetch하면 mp4를 JSON으로 파싱하다 조용히 래스터로
  // 폴백된다(2026-08-31 굵은 선 회귀의 원인). 쿼리를 보존한 채 확장자만 바꾼다.
  const doodleSidecarUrl = (suffix: ".mesh.primitives.json" | ".depth.mp4", sourceUrl?: string): string => {
    const url = sourceUrl ?? playbackShot?.doodleVideoUrl ?? "";
    if (!url) return "";
    const queryAt = url.indexOf("?");
    const base = queryAt === -1 ? url : url.slice(0, queryAt);
    const query = queryAt === -1 ? "" : url.slice(queryAt);
    return base.replace(/\.mp4$/i, suffix) + query;
  };
  const doodlePrimitivesUrl = doodleSidecarUrl(".mesh.primitives.json");
  const doodleDepthUrl = doodleSidecarUrl(".depth.mp4");
  useEffect(() => {
    setDoodleVectorData(null);
    setDoodlePrimitivesMissing(false);
    if (!doodlePrimitivesUrl) return;
    let cancelled = false;
    fetch(doodlePrimitivesUrl).then((response) => {
      if (!response.ok) throw new Error("no primitives");
      return response.json() as Promise<DoodlePrimitives>;
    }).then((primitives) => {
      if (!cancelled) setDoodleVectorData({ primitives, depthUrl: doodleDepthUrl });
    }).catch(() => {
      if (!cancelled) setDoodlePrimitivesMissing(true);
    });
    return () => { cancelled = true; };
  }, [doodlePrimitivesUrl, doodleDepthUrl, doodleReloadTick]);
  /// "Make Doodle": 세로(v-lite)가 없으면 h2v 새치기로 먼저 만들고,
  /// 세로가 준비되면 Loop Z doodle 태스크를 priority로 끼워넣는다.
  async function makeDoodleForCurrentShot() {
    if (doodleRequestState === "outpainting" || doodleRequestState === "doodling") return;
    if (!playbackShot) return;
    setDoodleRequestMessage("");
    try {
      if (!playbackShot.verticalVideoUrl) {
        setDoodleRequestState("outpainting");
        const h2v = await fetch("/api/shot-h2v", {
          method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ action: "prioritize", shotId: playbackShot.shotId, priority: true })
        });
        if (!h2v.ok) {
          const payload = await h2v.json().catch(() => null) as { error?: string } | null;
          throw new Error(payload?.error || `H2V prioritize failed (HTTP ${h2v.status})`);
        }
        setDoodleRequestMessage("9:16 outpainting via H2V queue…");
        // h2v 완료까지 주기적으로 확인
        await new Promise<void>((resolve) => {
          const timer = window.setInterval(async () => {
            const check = await fetch(`/api/browse-show-shots/${playbackShot.shotId > 0 ? 0 : 0}`);
            void check; // 폴링은 아래 effect가 처리
            void timer;
          }, 5000);
          void timer;
        }).catch(() => undefined);
      }
      setDoodleRequestState("doodling");
      const response = await fetch(`/api/shot-priority/${playbackShot.shotId}`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ kind: "doodle" })
      });
      const payload = await response.json() as { error?: string; queued?: boolean; status?: number };
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      if (payload.queued === false && payload.status === 2) {
        // 이미 완료 — 바로 표시
        setDoodleRequestState("idle");
        setDoodleReloadTick((tick) => tick + 1);
        return;
      }
      setDoodleRequestMessage("Doodle task queued — NPU will process next…");
    } catch (error) {
      setDoodleRequestState("error");
      setDoodleRequestMessage(error instanceof Error ? error.message : "make doodle failed");
    }
  }

  async function prioritizeCurrentShotVertical() {
    if (!playbackShot || h2vPrioritizeBusy) return;
    setH2vPrioritizeBusy(true);
    try {
      const response = await fetch("/api/shot-h2v", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "prioritize", shotId: playbackShot.shotId, priority: true }) });
      if (!response.ok) { const payload = await response.json().catch(() => null) as { error?: string } | null; window.alert(payload?.error || "H2V prioritize failed"); }
    } finally {
      setH2vPrioritizeBusy(false);
    }
  }
  const infoShot = playlist.find((item) => item.key === infoShotKey) || playbackShot;
  const currentIndex = playlist.findIndex((item) => item.key === playbackShot.key);
  const previousShot = currentIndex > 0 ? playlist[currentIndex - 1] || null : null;
  const nextShot = currentIndex >= 0 ? playlist[currentIndex + 1] || null : null;
  // 'original'은 잘린 shot mp4가 아니라 원본 전체 파일을 그대로 재생하므로,
  // 재생 시간축의 원점이 0이 아니라 shot의 절대 시작 지점이 된다.
  const originalSourceUrlForShot = originalSourceVideoUrl(video?.srcPath, mediaBaseUrl);
  const sourceIsVertical = playbackShot.height > playbackShot.width;
  // 탭 식별은 그대로 두고 표시 영상만 폴백 체인(자기 영상 → v-lite → lite)으로 고른다.
  // doodle의 depth URL은 doodleVideoUrl에서 동기적으로 유도한다 — primitives fetch를
  // 기다렸다가 URL을 교체하면 (1) 대기 중 v-lite가 먼저 1-2초 노출되고 (2) src 재교체
  // 시 재생이 끊겨 사용자가 다시 눌러야 한다(2026-09-01 지적). 선(canvas)은 fetch가
  // 끝나는 대로 얹히면 되고 영상 src는 처음부터 depth로 확정한다.
  const activePreviewMedia = previewSource === "doodle" && doodleDepthUrl
    ? { url: doodleDepthUrl, own: true }
    : previewTabVideo(playbackShot, previewSource, originalSourceUrlForShot);
  const nextPreviewMedia = nextShot
    ? (previewSource === "doodle" && nextShot.doodleVideoUrl
        ? { url: doodleSidecarUrl(".depth.mp4", nextShot.doodleVideoUrl), own: true }
        : previewTabVideo(nextShot, previewSource, originalSourceUrlForShot))
    : null;
  const effectivePreviewSource = previewSource;
  // 9x16 캔버스는 v-lite/pose/depth/edge 4개 탭만 쓴다. lite와 original은
  // 원본 비율을 as-is로 보여준다. 세로 소스는 자연 비율을 유지한다.
  const previewUsesVerticalFrame = (effectivePreviewSource === "vertical-lite"
    || effectivePreviewSource === "doodle"
    || effectivePreviewSource === "pose"
    || effectivePreviewSource === "depth"
    || effectivePreviewSource === "edge") && !sourceIsVertical;
  // 9x16 캔버스 안의 배치 규칙: 표시되는 영상이 9x16이면 y=0에 딱 맞게 채우고,
  // 가로(lite) 영상일 때만 세로 중심을 위에서 25% 지점에 맞춘다(outpaint 구도).
  const shownVideoIsVertical = activePreviewMedia.own
    ? effectivePreviewSource === "vertical-lite"
      || effectivePreviewSource === "doodle"
      || ((effectivePreviewSource === "pose" || effectivePreviewSource === "depth" || effectivePreviewSource === "edge") && playbackShot.hasVert)
    : Boolean(playbackShot.verticalVideoUrl);
  const previewLandscapeInVertical = previewUsesVerticalFrame && !shownVideoIsVertical;
  const depthMediaOwn = effectivePreviewSource === "depth" && activePreviewMedia.own;
  const videoWidth = previewUsesVerticalFrame ? 9 : playbackShot.width > 0 ? playbackShot.width : 16;
  const videoHeight = previewUsesVerticalFrame ? 16 : playbackShot.height > 0 ? playbackShot.height : 9;
  const playbackVideoUrl = activePreviewMedia.url;
  const nextShotVideoUrl = nextPreviewMedia ? nextPreviewMedia.url : "";
  const progressDuration = Math.max(0.01, playbackShot.durMs / 1000);
  const previewTimeOrigin = effectivePreviewSource === "original" && activePreviewMedia.own ? playbackShot.startMs / 1000 : 0;
  const nextPreviewTimeOrigin = effectivePreviewSource === "original" && nextPreviewMedia?.own && nextShot ? nextShot.startMs / 1000 : 0;
  const previewTimeOriginRef = useRef(previewTimeOrigin);
  previewTimeOriginRef.current = previewTimeOrigin;
  const previewDurationRef = useRef(progressDuration);
  previewDurationRef.current = progressDuration;
  // pose 미리보기에서는 원본 shot 영상을 10% 유령 레이어로 겹쳐 보여준다. 유령이 소리를
  // 담당하고 재생/정지/탐색/배속은 항상 pose 영상(활성 슬롯)을 따라가게 rAF로 동기화한다.
  useEffect(() => {
    if (effectivePreviewSource !== "pose") return;
    const ghost = poseGhostVideoRef.current;
    const main = activePlayerIndex === 0 ? videoARef.current : videoBRef.current;
    if (!ghost || !main) return;
    let frame = 0;
    const sync = () => {
      if (Math.abs(ghost.currentTime - main.currentTime) > 0.12) ghost.currentTime = main.currentTime;
      ghost.playbackRate = main.playbackRate;
      ghost.muted = main.muted;
      ghost.volume = main.volume;
      if (main.paused) {
        if (!ghost.paused) ghost.pause();
      } else if (ghost.paused) {
        void ghost.play().catch(() => undefined);
      }
      frame = requestAnimationFrame(sync);
    };
    frame = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(frame);
  }, [effectivePreviewSource, activePlayerIndex, playbackShot.key]);
  useEffect(() => {
    if (previewSource === "vertical-lite" && sourceIsVertical) {
      setPreviewSource("lite");
      localStorage.setItem(PREVIEW_SOURCE_STORAGE_KEY,"lite");
    }
  },[previewSource,sourceIsVertical]);
  const activeVideoUrl = previewVideoStartUrl(playbackVideoUrl, previewTimeOrigin, progressDuration);
  const idleVideoUrl = nextPreloadEnabled && nextShotVideoUrl
    ? previewVideoStartUrl(
        nextShotVideoUrl,
        nextPreviewTimeOrigin,
        nextShot ? Math.max(0.01, nextShot.durMs / 1000) : 0
      )
    : "";
  const progressTime = clamp(playerTime, 0, progressDuration);
  const progressPercent = clamp((progressTime / progressDuration) * 100, 0, 100);
  const playbackAbsoluteMs = playbackShot.startMs + progressTime * 1000;
  const playbackSubtitleTrack = subtitleTrackForMode(playbackShot, subtitleMode);
  const playbackSubtitleLines = playbackSubtitleTrack?.lines || playbackShot.subtitles;
  const infoSubtitleTrack = subtitleTrackForMode(infoShot, subtitleMode);
  const infoSubtitleLines = infoSubtitleTrack?.lines || infoShot.subtitles;
  const subtitleLanguageSwitchEnabled = canSwitchSubtitleLanguage(playbackShot);
  const subtitleLanguageLabel = playbackSubtitleTrack?.language || "--";
  const activeSubtitleLines = (() => {
    let activeStartMs = -1;
    for (const line of playbackSubtitleLines) {
      if (line.startMs <= playbackAbsoluteMs && line.startMs >= activeStartMs) {
        activeStartMs = line.startMs;
      }
    }
    return activeStartMs >= 0
      ? playbackSubtitleLines.filter((line) => line.startMs === activeStartMs)
      : [];
  })();
  const progressStyle = { "--preview-progress": `${progressPercent}%` } as CSSProperties & Record<"--preview-progress", string>;
  const depthRangeStyle = {
    "--depth-range-low": `${depthRange.low}%`,
    "--depth-range-high": `${depthRange.high}%`
  } as CSSProperties & Record<"--depth-range-low" | "--depth-range-high", string>;
  const sourcePath = video?.srcPath || "";
  const sourceDir = sourcePath ? dirNameFromPath(sourcePath) : "-";
  const sourceFile = sourcePath ? fileNameFromPath(sourcePath) : "-";

  useEffect(() => {
    const controller = new AbortController();
    setDetectedMotion(null);
    setDetectedVisual(null);
    setDetectedMotionError(null);
    setDetectedMotionLoading(true);
    if (priorityIndexShotIdRef.current !== infoShot.shotId) {
      priorityIndexShotIdRef.current = infoShot.shotId;
      setPriorityIndexState("idle");
      setPriorityIndexMessage("");
    }
    void fetch(`/api/shot-motion-analysis/${infoShot.shotId}`, {
      cache: "no-store",
      signal: controller.signal
    })
      .then(async (response) => {
        const payload = await response.json() as {
          error?: string;
          visual?: { packet?: Record<string, unknown> | null; textEn?: string; embedStatus?: number } | null;
          analysis?: { motion_packet?: unknown } | null;
          motionIndex?: {
            generationId?: number;
            generationLabel?: string;
            preview?: boolean;
            scope?: "approved_show" | "sample";
            shotSearchTextEn?: string | null;
            motionPacket?: unknown;
          };
          hotFields?: { searchTextEn?: string | null };
        };
        // 404(모션 패킷 없음)는 visual 패널과 무관하므로 오류로 취급하지 않는다.
        // visual이 달린 미판정 샷은 모션이 아직 없어 404로 오는 경우가 대부분이다.
        if (!response.ok && response.status !== 404) throw new Error(payload.error || `HTTP ${response.status}`);
        const visual = payload.visual;
        if (visual) {
          setDetectedVisual({ packet: visual.packet ?? null, textEn: String(visual.textEn || ""), embedStatus: Number(visual.embedStatus || 0) });
        } else {
          setDetectedVisual(null);
        }
        const packet = motionRecord(payload.motionIndex?.motionPacket ?? payload.analysis?.motion_packet);
        if (!packet) return null;
        return {
          generationId: payload.motionIndex?.generationId ?? null,
          generationLabel: payload.motionIndex?.generationLabel || "legacy",
          preview: Boolean(payload.motionIndex?.preview),
          scope: payload.motionIndex?.scope || "legacy",
          shotSearchTextEn: payload.motionIndex?.shotSearchTextEn ?? payload.hotFields?.searchTextEn ?? null,
          motionPacket: packet
        } satisfies ShotDetectedMotion;
      })
      .then((result) => {
        if (controller.signal.aborted) return;
        setDetectedMotion(result);
        // 우선 처리 결과가 도착하면 폴링을 끝낸다.
        if (result) setPriorityIndexState("idle");
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setDetectedMotionError(error instanceof Error ? error.message : "검출 속성을 불러오지 못했습니다.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetectedMotionLoading(false);
      });
    return () => controller.abort();
  }, [infoShot.shotId, detectedReloadTick]);

  // 우선 처리 대기 중: 4초 간격으로 결과와 태스크 상태를 함께 본다.
  // 실패(3)면 즉시 에러로 전환하고, 15분이 지나면 포기한다.
  useEffect(() => {
    if (priorityIndexState !== "waiting") return;
    let cancelled = false;
    const poll = async () => {
      if (Date.now() - priorityIndexStartedAtRef.current > 15 * 60_000) {
        setPriorityIndexState("error");
        setPriorityIndexMessage("15분 안에 결과가 도착하지 않았습니다. 다시 시도해 주세요.");
        return;
      }
      try {
        const response = await fetch(`/api/shot-priority/${infoShot.shotId}?kind=motion`, { cache: "no-store" });
        const payload = await response.json() as { status?: number; error?: string | null };
        if (cancelled) return;
        if (Number(payload.status) === 3) {
          setPriorityIndexState("error");
          setPriorityIndexMessage(`우선 처리 실패: ${payload.error || "worker 오류"}`);
          return;
        }
        setDetectedReloadTick((tick) => tick + 1);
      } catch {
        if (!cancelled) setDetectedReloadTick((tick) => tick + 1);
      }
    };
    const timer = window.setInterval(() => void poll(), 4000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [priorityIndexState, infoShot.shotId]);

  async function requestPriorityIndex() {
    if (priorityIndexState === "busy" || priorityIndexState === "waiting") return;
    setPriorityIndexState("busy");
    try {
      const response = await fetch(`/api/shot-priority/${infoShot.shotId}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ kind: "motion" })
      });
      const payload = await response.json() as { error?: string; queued?: boolean; status?: number };
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      if (payload.queued === false && payload.status === 2) {
        // 이미 완료된 상태 — 바로 다시 불러온다.
        setDetectedReloadTick((tick) => tick + 1);
        return;
      }
      priorityIndexStartedAtRef.current = Date.now();
      setPriorityIndexState("waiting");
      setDetectedReloadTick((tick) => tick + 1);
    } catch (error) {
      setPriorityIndexState("error");
      setPriorityIndexMessage(error instanceof Error ? error.message : "priority request failed");
    }
  }
  const resolutionLabel = infoShot.width > 0 && infoShot.height > 0 ? `${infoShot.width}x${infoShot.height}` : "-";
  const infoEpisode = episodeForShot(episodes, infoShot);
  const episodeRelativeStartMs = Math.max(0, infoShot.startMs - (infoEpisode?.startMs || 0));
  const shotTimeLabel = `shot ${infoShot.shotId}, @${formatTime(episodeRelativeStartMs)} ~ ${formatPreviewSeconds(infoShot.durMs)} (${infoShot.frameCount} frames)`;
  const splitJob = splitJobs.find((job) => job.shotId === infoShot.shotId || job.shotIds.includes(infoShot.shotId));
  const splitState = splitPendingShotKeys.includes(infoShot.key) ? "enqueuing" : splitJob?.status ?? null;
  const deletePending = pendingDeleteShotKeys.includes(infoShot.key);
  const deleteVerb = infoShot.softDeleted ? "restore" : "delete";
  const deletePendingLabel = deleteVerb === "restore" ? "restoring" : "deleting";
  const motionReprocessPending = pendingMotionReprocessShotIds.includes(infoShot.shotId);
  const splitHelpState = shotSplitHelpState(infoShot, splitHelpOverride);
  const splitHelpDefaultIssueMask = splitHelpOverride
    ? splitHelpOverride.issueMask & SPLIT_ISSUE_MASK_ALL
    : shotSplitHelpIssueMask(infoShot);
  const splitHelpPending = pendingSplitHelpShotIds.includes(infoShot.shotId);
  const [splitHelpIssueMask, setSplitHelpIssueMask] = useState(splitHelpDefaultIssueMask);
  const rangeMarkBusy = rangeMarkPending !== null;
  const introEndPending = rangeMarkPending?.shotKey === infoShot.key && rangeMarkPending.mode === "intro_end";
  const outroStartPending = rangeMarkPending?.shotKey === infoShot.key && rangeMarkPending.mode === "outro_start";
  const isVerticalShow = showType === 3 || showType === 4;
  const episodeStartsForVideo = episodes
    .filter((episode) => episode.videoId === infoShot.videoId)
    .sort((left, right) => left.startMs - right.startMs || (left.id || 0) - (right.id || 0));
  const episodeStart = episodeStartsForVideo.find((episode) =>
    episode.startShotId === infoShot.shotId || episode.startMs === infoShot.startMs
  ) || null;
  const canUnsetEpisodeStart = Boolean(
    episodeStart &&
    episodeStartsForVideo.length > 1 &&
    episodeStartsForVideo[0]?.key !== episodeStart.key
  );
  const episodeStartPending = pendingEpisodeShotKey === infoShot.key;

  useEffect(() => {
    setSplitHelpIssueMask(splitHelpDefaultIssueMask);
  }, [infoShot.key, splitHelpDefaultIssueMask]);

  async function applySplitHelpIssueMask(nextIssueMask: number) {
    return applySplitHelpIssueMaskForShot(infoShot, nextIssueMask);
  }

  async function toggleSplitHelpIgnored(nextIgnored: boolean) {
    if (splitHelpPending) {
      return false;
    }
    return onSetSplitHelpIgnored(infoShot, nextIgnored);
  }

  async function copySelectedSubtitles() {
    const text = infoSubtitleLines.map((line) => line.displayText).join("\n");
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
    setSubtitlesCopied(true);
    window.setTimeout(() => setSubtitlesCopied(false), 1200);
  }

  function toggleSubtitleLanguage() {
    if (!subtitleLanguageSwitchEnabled) {
      return false;
    }
    setSubtitleMode((current) => current === "original" ? "familiar" : "original");
    return true;
  }

  function splitHelpIssueMaskForShot(targetShot: BrowseShot) {
    if (targetShot.shotId === infoShot.shotId) {
      return splitHelpIssueMask & SPLIT_ISSUE_MASK_ALL;
    }
    const override = splitHelpOverrides[targetShot.shotId];
    return override
      ? override.issueMask & SPLIT_ISSUE_MASK_ALL
      : shotSplitHelpIssueMask(targetShot);
  }

  function splitHelpDefaultIssueMaskForShot(targetShot: BrowseShot) {
    const override = splitHelpOverrides[targetShot.shotId];
    return override
      ? override.issueMask & SPLIT_ISSUE_MASK_ALL
      : shotSplitHelpIssueMask(targetShot);
  }

  function splitHelpStateForShot(targetShot: BrowseShot) {
    return shotSplitHelpState(targetShot, splitHelpOverrides[targetShot.shotId]);
  }

  function splitHelpIssueMaskForPairedNeighbor(targetShot: BrowseShot) {
    const mask = splitHelpIssueMaskForShot(targetShot);
    const state = splitHelpStateForShot(targetShot);
    if (state === "user_need_to_help") {
      return mask & SPLIT_ISSUE_MERGE_MASK;
    }
    return mask;
  }

  function shouldApplySplitHelpIssueMaskForShot(targetShot: BrowseShot, normalizedIssueMask: number) {
    const defaultIssueMask = splitHelpDefaultIssueMaskForShot(targetShot);
    const state = splitHelpStateForShot(targetShot);
    return normalizedIssueMask !== defaultIssueMask ||
      state === "user_need_to_help" ||
      (normalizedIssueMask === 0 && state !== "resolved");
  }

  async function applySplitHelpIssueMaskForShot(targetShot: BrowseShot, nextIssueMask: number) {
    if (pendingSplitHelpShotIds.includes(targetShot.shotId)) {
      return false;
    }
    const normalizedIssueMask = nextIssueMask & SPLIT_ISSUE_MASK_ALL;
    const shouldApply = shouldApplySplitHelpIssueMaskForShot(targetShot, normalizedIssueMask);
    if (targetShot.shotId === infoShot.shotId) {
      setSplitHelpIssueMask(normalizedIssueMask);
    }
    if (!shouldApply) {
      return true;
    }
    return onApplySplitHelpReview(targetShot, normalizedIssueMask);
  }

  function moveToInfoShot(targetShot: BrowseShot) {
    goodAdvanceCursorRef.current = targetShot.key;
    setPlaybackShotKey(targetShot.key);
    setInfoShotKey(targetShot.key);
    setNextPreloadEnabled(false);
    setPlayerTime(0);
    setPlayerDuration(Math.max(0.01, targetShot.durMs / 1000));
    publishLastBrowseShot(targetShot);
    onPlaybackShotChange(targetShot.key);
  }

  function nextInfoShotCircular(currentShotKey = infoShot.key) {
    const currentInfoIndex = playlist.findIndex((item) => item.key === currentShotKey);
    if (currentInfoIndex < 0 || playlist.length === 0) {
      return null;
    }
    return playlist[(currentInfoIndex + 1) % playlist.length] || null;
  }

  async function applyGoodAndAdvance() {
    if (keyboardReviewActionPendingRef.current) {
      return;
    }
    const currentShotKey = goodAdvanceCursorRef.current;
    const currentShot = playlist.find((item) => item.key === currentShotKey);
    if (!currentShot) {
      return;
    }
    const nextShot = nextInfoShotCircular(currentShotKey);
    keyboardReviewActionPendingRef.current = true;
    try {
      const applied = await applySplitHelpIssueMaskForShot(currentShot, 0);
      if (applied) {
        // 탭 이동 모드면 인접 샷 대신 다음 help shot으로 진행한다.
        if (onReviewTabJumpNext()) {
          return;
        }
        if (nextShot) {
          moveToInfoShot(nextShot);
        }
      }
    } finally {
      keyboardReviewActionPendingRef.current = false;
    }
  }

  async function toggleSplitHelpIgnoredAndAdvance() {
    if (keyboardReviewActionPendingRef.current || splitHelpPending) {
      return;
    }
    const currentShot = infoShot;
    const nextShot = nextInfoShotCircular(currentShot.key);
    keyboardReviewActionPendingRef.current = true;
    try {
      const applied = await onSetSplitHelpIgnored(currentShot, splitHelpState !== "ignored");
      if (applied) {
        // 탭 이동 모드면 인접 샷 대신 다음 help shot으로 진행한다.
        if (onReviewTabJumpNext()) {
          return;
        }
        if (nextShot) {
          moveToInfoShot(nextShot);
        }
      }
    } finally {
      keyboardReviewActionPendingRef.current = false;
    }
  }

  useEffect(() => {
    goodAdvanceCursorRef.current = infoShot.key;
  }, [infoShot.key]);

  function nextSplitHelpIssueMask(currentMask: number, issue: number, checked: boolean, allowClearingLastIssue = false) {
    const normalizedCurrent = currentMask & SPLIT_ISSUE_MASK_ALL;
    if (checked) {
      return (normalizedCurrent | issue) & SPLIT_ISSUE_MASK_ALL;
    }
    const withoutIssue = normalizedCurrent & ~issue;
    return withoutIssue === 0 && !allowClearingLastIssue ? normalizedCurrent : withoutIssue;
  }

  function adjacentSplitHelpShot(direction: -1 | 1) {
    const orderedShots = adjacencyShots
      .filter((item) => item.videoId === infoShot.videoId && !item.softDeleted)
      .sort((left, right) => left.shotNo - right.shotNo || left.startMs - right.startMs || left.shotId - right.shotId);
    const currentIndex = orderedShots.findIndex((item) => item.shotId === infoShot.shotId);
    if (currentIndex < 0) {
      return null;
    }
    return orderedShots[currentIndex + direction] || null;
  }

  async function applyPairedMergeHelpIssue(issue: number, checked: boolean) {
    const direction = issue === SPLIT_ISSUE_MERGE_RIGHT ? 1 : -1;
    const counterpartIssue = issue === SPLIT_ISSUE_MERGE_RIGHT
      ? SPLIT_ISSUE_MERGE_LEFT
      : SPLIT_ISSUE_MERGE_RIGHT;
    const neighborShot = adjacentSplitHelpShot(direction);
    if (neighborShot && pendingSplitHelpShotIds.includes(neighborShot.shotId)) {
      return false;
    }

    const currentMask = splitHelpIssueMask & SPLIT_ISSUE_MASK_ALL;
    const nextCurrentMask = nextSplitHelpIssueMask(currentMask, issue, checked, true);
    const operations: Array<Promise<boolean>> = [
      applySplitHelpIssueMaskForShot(infoShot, nextCurrentMask)
    ];

    if (neighborShot) {
      const neighborMask = splitHelpIssueMaskForPairedNeighbor(neighborShot);
      const nextNeighborMask = checked
        ? (neighborMask | counterpartIssue) & SPLIT_ISSUE_MASK_ALL
        : (neighborMask & ~counterpartIssue) & SPLIT_ISSUE_MASK_ALL;
      operations.push(applySplitHelpIssueMaskForShot(neighborShot, nextNeighborMask));
    }

    const results = await Promise.all(operations);
    return results.every(Boolean);
  }

  function toggleSplitHelpIssue(issue: number, checked: boolean) {
    if (issue === SPLIT_ISSUE_MERGE_LEFT || issue === SPLIT_ISSUE_MERGE_RIGHT) {
      void applyPairedMergeHelpIssue(issue, checked);
      return;
    }
    const currentMask = splitHelpIssueMask & SPLIT_ISSUE_MASK_ALL;
    const clickedSuggestedIssue =
      splitHelpState === "user_need_to_help" &&
      currentMask === issue &&
      !checked;
    const nextIssueMask = clickedSuggestedIssue ? currentMask : nextSplitHelpIssueMask(currentMask, issue, checked);
    void applySplitHelpIssueMask(nextIssueMask);
  }

  function videoForSlot(index: number) {
    return index === 0 ? videoARef.current : videoBRef.current;
  }

  function activeVideo() {
    return document.querySelector<HTMLVideoElement>(".previewVideo.active") || videoForSlot(activePlayerIndex);
  }

  function idleVideo() {
    return document.querySelector<HTMLVideoElement>(".previewVideo.standby") || videoForSlot(1 - activePlayerIndex);
  }

  function applyPlaybackRate(rate = playbackRate) {
    const videos = [videoARef.current, videoBRef.current];
    for (const item of videos) {
      if (item) {
        item.playbackRate = rate;
      }
    }
  }

  // 'original' 모드에서는 <video>의 currentTime이 원본 전체 기준 절대시간이므로
  // 화면에 쓰는 시간축(0 기준)으로 옮겨준다.
  function toPlayerTime(videoTime: number) {
    const value = Number.isFinite(videoTime) ? videoTime - previewTimeOriginRef.current : 0;
    return clamp(value, 0, previewDurationRef.current);
  }

  function toVideoTime(playerTimeValue: number) {
    const bounded = clamp(playerTimeValue, 0, previewDurationRef.current);
    return previewTimeOriginRef.current + bounded;
  }

  function resetVideoToStart(video: HTMLVideoElement | null) {
    if (!video) {
      return;
    }
    try {
      video.currentTime = previewTimeOriginRef.current;
    } catch {
      // Metadata may not be ready yet.
    }
  }

  function syncPlayerClock(video: HTMLVideoElement, force = false) {
    const nextTime = toPlayerTime(video.currentTime);
    const shotDuration = previewDurationRef.current;
    // 원본 재생 중에는 video.duration이 영화 전체 길이라 shot 길이를 써야 한다.
    const nextDuration = previewTimeOriginRef.current > 0
      ? shotDuration
      : Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : shotDuration;
    setPlayerTime((current) => {
      if (!force && Math.abs(current - nextTime) < 0.025 && nextTime > 0 && nextTime < nextDuration - 0.001) {
        return current;
      }
      return nextTime;
    });
    setPlayerDuration((current) => (Math.abs(current - nextDuration) < 0.005 ? current : nextDuration));
    // 원본 파일은 shot 끝에서 자동으로 멈추지 않으므로 직접 경계를 감시한다.
    if (previewTimeOriginRef.current > 0 && !video.paused && nextTime >= shotDuration - 0.001) {
      video.pause();
      handleVideoEnded(activePlayerIndex);
    }
  }

  function videoSlotSource(index: number) {
    return index === activePlayerIndex ? activeVideoUrl : idleVideoUrl;
  }

  useEffect(() => () => {
    clearProgressDragListeners();
    if (previewClockFrameRef.current !== null) {
      window.cancelAnimationFrame(previewClockFrameRef.current);
      previewClockFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      if (previewClockFrameRef.current !== null) {
        window.cancelAnimationFrame(previewClockFrameRef.current);
        previewClockFrameRef.current = null;
      }
      return;
    }

    let cancelled = false;
    const tick = () => {
      if (cancelled) {
        return;
      }
      const video = activeVideo();
      if (video && !video.paused && !video.ended) {
        syncPlayerClock(video);
      }
      previewClockFrameRef.current = window.requestAnimationFrame(tick);
    };

    previewClockFrameRef.current = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (previewClockFrameRef.current !== null) {
        window.cancelAnimationFrame(previewClockFrameRef.current);
        previewClockFrameRef.current = null;
      }
    };
  }, [activePlayerIndex, activeVideoUrl, isPlaying, playbackShot.durMs, playbackShot.key]);

  useEffect(() => {
    if (playRequestId === 0) {
      return;
    }
    setContinuousPlayback(false);
    setPlaybackShotKey(shot.key);
    setInfoShotKey(shot.key);
    setNextPreloadEnabled(false);
    manualPreviewSeekRef.current = false;
    setActivePlayerIndex(0);
    setAutoplayRequest((current) => current + 1);
  }, [playRequestId]);

  useEffect(() => {
    if (internalPlaybackShotKeyRef.current === shot.key) {
      internalPlaybackShotKeyRef.current = null;
      setInfoShotKey(shot.key);
      return;
    }
    internalPlaybackShotKeyRef.current = null;
    for (const [index, item] of [videoARef.current, videoBRef.current].entries()) {
      item?.pause();
      if (item && index !== activePlayerIndex) {
        try {
          item.currentTime = previewTimeOriginRef.current;
        } catch {
          // Metadata may not be ready yet.
        }
      }
    }
    setContinuousPlayback(false);
    setPlaybackShotKey(shot.key);
    setInfoShotKey(shot.key);
    manualPreviewSeekRef.current = false;
    setActivePlayerIndex((current) => 1 - current);
    setIsPlaying(false);
    setPlayerTime(0);
    setPlayerDuration(Math.max(0.01, shot.durMs / 1000));
    setNextPreloadEnabled(false);
  }, [shot.key]);

  useEffect(() => {
    setKeyframes([]);
  }, [playbackShot.key]);

  useEffect(() => {
    setPlayerTime(0);
    setPlayerDuration(Math.max(0.01, playbackShot.durMs / 1000));
    const videos = [videoARef.current, videoBRef.current].filter((item): item is HTMLVideoElement => item !== null);
    if (videos.length === 0) {
      return;
    }
    let cancelled = false;
    const resetPausedVideoToStart = (video: HTMLVideoElement, index: number) => {
      if (cancelled || !video.paused || video.ended || isScrubbingRef.current || manualPreviewSeekRef.current) {
        return;
      }
      try {
        video.currentTime = previewTimeOriginRef.current;
      } catch {
        // Safari can reject a seek until metadata is fully settled.
      }
      if (index === activePlayerIndex) {
        // 대기(preload) 비디오는 로드 이벤트가 늦게 와도 현재 플레이어 clock을 건드리면 안 된다.
        setPlayerTime(0);
        setPlayerDuration(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : Math.max(0.01, playbackShot.durMs / 1000));
      }
    };

    const cleanups: Array<() => void> = [];
    for (const video of videos) {
      const index = video === videoARef.current ? 0 : 1;
      const resetThisVideo = () => resetPausedVideoToStart(video, index);
      resetThisVideo();
      video.addEventListener("loadedmetadata", resetThisVideo);
      video.addEventListener("loadeddata", resetThisVideo);
      video.addEventListener("canplay", resetThisVideo);
      video.addEventListener("durationchange", resetThisVideo);
      const timers = [80, 240, 600].map((delay) => window.setTimeout(resetThisVideo, delay));
      cleanups.push(() => {
        video.removeEventListener("loadedmetadata", resetThisVideo);
        video.removeEventListener("loadeddata", resetThisVideo);
        video.removeEventListener("canplay", resetThisVideo);
        video.removeEventListener("durationchange", resetThisVideo);
        for (const timer of timers) {
          window.clearTimeout(timer);
        }
      });
    }
    return () => {
      cancelled = true;
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [activePlayerIndex, activeVideoUrl, playbackShot.durMs, playbackShot.key]);

  useEffect(() => {
    if (!depthMediaOwn) {
      return;
    }
    const canvas = depthCanvasRef.current;
    const video = activeVideo();
    if (!canvas || !video) {
      return;
    }
    const gl = canvas.getContext("webgl", { alpha: true, antialias: false, preserveDrawingBuffer: false });
    if (!gl) {
      return;
    }

    let frame = 0;
    let disposed = false;
    let program: WebGLProgram | null = null;
    let positionBuffer: WebGLBuffer | null = null;
    let texCoordBuffer: WebGLBuffer | null = null;
    let texture: WebGLTexture | null = null;

    try {
      program = createDepthProgram(gl);
      positionBuffer = gl.createBuffer();
      texCoordBuffer = gl.createBuffer();
      texture = gl.createTexture();
      if (!positionBuffer || !texCoordBuffer || !texture) {
        throw new Error("Failed to create depth mask WebGL buffers");
      }

      gl.useProgram(program);
      const positionLocation = gl.getAttribLocation(program, "a_position");
      const texCoordLocation = gl.getAttribLocation(program, "a_texCoord");
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(texCoordLocation);
      gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

      gl.uniform1i(gl.getUniformLocation(program, "u_video"), 0);
      const lowLocation = gl.getUniformLocation(program, "u_low");
      const highLocation = gl.getUniformLocation(program, "u_high");
      const softnessLocation = gl.getUniformLocation(program, "u_softness");

      const draw = () => {
        if (disposed) {
          return;
        }
        const rect = canvas.getBoundingClientRect();
        const pixelRatio = window.devicePixelRatio || 1;
        const width = Math.max(1, Math.round(rect.width * pixelRatio));
        const height = Math.max(1, Math.round(rect.height * pixelRatio));
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
          gl.viewport(0, 0, width, height);
        }
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          try {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
            gl.uniform1f(lowLocation, depthRange.low / 100);
            gl.uniform1f(highLocation, depthRange.high / 100);
            gl.uniform1f(softnessLocation, DEPTH_RANGE_SOFTNESS);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          } catch {
            // Media frames can be temporarily unavailable while switching sources.
          }
        }
        frame = window.requestAnimationFrame(draw);
      };
      draw();
    } catch {
      return;
    }

    return () => {
      disposed = true;
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      if (texture) {
        gl.deleteTexture(texture);
      }
      if (positionBuffer) {
        gl.deleteBuffer(positionBuffer);
      }
      if (texCoordBuffer) {
        gl.deleteBuffer(texCoordBuffer);
      }
      if (program) {
        gl.deleteProgram(program);
      }
    };
  }, [activePlayerIndex, activeVideoUrl, depthRange.high, depthRange.low, depthMediaOwn]);

  useEffect(() => {
    if (autoplayRequest === 0) {
      return;
    }
    const video = activeVideo();
    if (!video) {
      return;
    }

    const playFromStart = () => {
      applyPlaybackRate();
      try {
        video.currentTime = previewTimeOriginRef.current;
      } catch {
        // Some browsers reject seeking before metadata is available.
      }
      setPlayerTime(0);
      void video.play().catch(() => {
        setIsPlaying(false);
      });
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      playFromStart();
      return;
    }

    video.addEventListener("loadedmetadata", playFromStart, { once: true });
    return () => video.removeEventListener("loadedmetadata", playFromStart);
  // 속도 변경은 현재 위치를 유지해야 하므로 autoplay-from-start effect를 다시 트리거하지 않는다.
  }, [activePlayerIndex, autoplayRequest, playbackShot.key]);

  useEffect(() => {
    applyPlaybackRate();
  }, [playbackRate, playbackShot.key, nextPreloadEnabled]);

  useEffect(() => {
    setNextPreloadEnabled(false);
    if (!nextShot || !nextShotVideoUrl) {
      return;
    }
    const video = activeVideo();
    if (!video) {
      return;
    }

    const maybeEnableNextPreload = () => {
      if (videoBufferedToEnd(video)) {
        setNextPreloadEnabled(true);
      }
    };

    maybeEnableNextPreload();
    video.addEventListener("progress", maybeEnableNextPreload);
    video.addEventListener("canplaythrough", maybeEnableNextPreload);
    video.addEventListener("loadeddata", maybeEnableNextPreload);
    video.addEventListener("loadedmetadata", maybeEnableNextPreload);
    const timer = window.setInterval(maybeEnableNextPreload, 250);
    return () => {
      video.removeEventListener("progress", maybeEnableNextPreload);
      video.removeEventListener("canplaythrough", maybeEnableNextPreload);
      video.removeEventListener("loadeddata", maybeEnableNextPreload);
      video.removeEventListener("loadedmetadata", maybeEnableNextPreload);
      window.clearInterval(timer);
    };
  }, [activePlayerIndex, nextShot?.key, nextShotVideoUrl, playbackShot.key]);

  useEffect(() => {
    async function loadKeyframesForShot(targetShot: BrowseShot) {
      const version = `${targetShot.shotId}-${targetShot.startMs}-${targetShot.durMs}`;
      const cached = keyframeCacheRef.current.get(version);
      if (cached) {
        return cached;
      }
      const existing = keyframeLoadingRef.current.get(version);
      if (existing) {
        return existing;
      }
      const request = fetch(`/api/shot-keyframes/${targetShot.shotId}?v=${version}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((payload: { keyframes?: number[] } | null) => {
          const nextKeyframes = Array.isArray(payload?.keyframes)
            ? payload.keyframes.filter((value) => Number.isFinite(value) && value >= 0)
            : [];
          return nextKeyframes.length > 0 ? nextKeyframes : [0];
        })
        .catch(() => [0])
        .finally(() => {
          keyframeLoadingRef.current.delete(version);
        });
      keyframeLoadingRef.current.set(version, request);
      const loaded = await request;
      keyframeCacheRef.current.set(version, loaded);
      if (targetShot.key === playbackShot.key) {
        setKeyframes(loaded);
      }
      return loaded;
    }

    async function seekToKeyframe(direction: -1 | 1) {
      const video = activeVideo();
      if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA) {
        return;
      }
      const activeKeyframes = keyframes.length > 0 ? keyframes : await loadKeyframesForShot(playbackShot);
      const epsilon = 0.04;
      // 키프레임 목록은 shot 기준(0 원점)이라 원본 재생 중이면 shot 시간축으로 맞춘다.
      const current = toPlayerTime(video.currentTime);
      const target = direction > 0
        ? activeKeyframes.find((time) => time > current + epsilon)
        : [...activeKeyframes].reverse().find((time) => time < current - epsilon);
      if (target === undefined) {
        return;
      }
      video.currentTime = toVideoTime(target);
      video.playbackRate = playbackRate;
    }

    function toggleEpisodeStartState() {
      if (!isVerticalShow || episodeStartPending) {
        return false;
      }
      if (episodeStart) {
        if (!canUnsetEpisodeStart) {
          return false;
        }
        onSetEpisodeStart(infoShot, "unset");
        return true;
      }
      onSetEpisodeStart(infoShot, "set");
      return true;
    }

    function handleKeyDown(event: KeyboardEvent) {
      const isEpisodeToggle = !event.shiftKey && (event.code === "KeyE" || event.key.toLowerCase() === "e");
      const isSplitHelpGood = !event.shiftKey && (event.code === "KeyG" || event.key.toLowerCase() === "g");
      const isSplitHelpSplit = !event.shiftKey && (event.code === "KeyS" || event.key.toLowerCase() === "s");
      const isSplitHelpMergeLeft = !event.shiftKey && (event.code === "KeyA" || event.key.toLowerCase() === "a");
      const isSplitHelpMergeRight = !event.shiftKey && (event.code === "KeyD" || event.key.toLowerCase() === "d");
      const isSplitHelpIgnore = !event.shiftKey && (event.code === "KeyI" || event.key.toLowerCase() === "i");
      const isSubtitleLanguageToggle = !event.shiftKey && (event.code === "KeyL" || event.key.toLowerCase() === "l");
      const isPlayFromBeginningShortcut = event.key === "[" || (!event.shiftKey && event.code === "BracketLeft");
      const isLastFrameShortcut = event.key === "]" || (!event.shiftKey && event.code === "BracketRight");
      const keyboardPlaybackRate = playbackRateFromKeyboardEvent(event);
      if (!isEpisodeToggle && !isSplitHelpGood && !isSplitHelpSplit && !isSplitHelpMergeLeft && !isSplitHelpMergeRight && !isSplitHelpIgnore && !isSubtitleLanguageToggle && !isPlayFromBeginningShortcut && !isLastFrameShortcut && keyboardPlaybackRate === null) {
        return;
      }
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) {
        return;
      }
      if (!isPlayFromBeginningShortcut && !isLastFrameShortcut && isTextEditingTarget(event.target)) {
        return;
      }
      if (keyboardPlaybackRate !== null) {
        event.preventDefault();
        event.stopPropagation();
        setPreviewPlaybackRate(keyboardPlaybackRate, { playIfPaused: true });
        return;
      }
      if (isPlayFromBeginningShortcut) {
        event.preventDefault();
        event.stopPropagation();
        playActiveVideo(true);
        return;
      }
      if (isLastFrameShortcut) {
        event.preventDefault();
        event.stopPropagation();
        jumpToFrameEdge("last");
        return;
      }
      if (isSubtitleLanguageToggle) {
        if (!toggleSubtitleLanguage()) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isEpisodeToggle) {
        if (!toggleEpisodeStartState()) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isSplitHelpIgnore) {
        if (splitHelpPending || keyboardReviewActionPendingRef.current) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        void toggleSplitHelpIgnoredAndAdvance();
        return;
      }
      if (isSplitHelpGood || isSplitHelpSplit || isSplitHelpMergeLeft || isSplitHelpMergeRight) {
        if (splitHelpPending) {
          return;
        }
        const activeIssue = isSplitHelpSplit
          ? SPLIT_ISSUE_UNDER
          : isSplitHelpMergeLeft
            ? SPLIT_ISSUE_MERGE_LEFT
            : SPLIT_ISSUE_MERGE_RIGHT;
        const activeIssueSelected = (splitHelpIssueMask & activeIssue) === activeIssue;
        event.preventDefault();
        event.stopPropagation();
        if (isSplitHelpGood) {
          void applyGoodAndAdvance();
        } else {
          toggleSplitHelpIssue(activeIssue, !activeIssueSelected);
        }
        return;
      }
    }

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [
    activePlayerIndex,
    adjacencyShots,
    canUnsetEpisodeStart,
    continuousPlayback,
    episodeStart,
    episodeStartPending,
    infoShot,
    isVerticalShow,
    keyframes,
    onSetEpisodeStart,
    onApplySplitHelpReview,
    onSetSplitHelpIgnored,
    onReviewTabJumpNext,
    onPlaybackShotChange,
    playbackRate,
    playbackShot,
    playlist,
    splitHelpDefaultIssueMask,
    splitHelpIssueMask,
    splitHelpOverrides,
    splitHelpState,
    splitHelpPending,
    subtitleLanguageSwitchEnabled
  ]);

  function playActiveVideo(reset = false, rateOverride = playbackRate) {
    const video = activeVideo();
    if (!video) {
      setAutoplayRequest((current) => current + 1);
      return;
    }
    applyPlaybackRate(rateOverride);
    if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
      video.load();
    }
    const nearEnd = previewTimeOriginRef.current > 0
      ? toPlayerTime(video.currentTime) >= previewDurationRef.current - 0.05
      : Number.isFinite(video.duration) && video.duration > 0 && video.currentTime >= video.duration - 0.05;
    if (reset || video.ended || nearEnd) {
      try {
        video.currentTime = previewTimeOriginRef.current;
        manualPreviewSeekRef.current = false;
      } catch {
        // Metadata may not be ready yet.
      }
    }
    void video.play().catch(() => {
      setIsPlaying(false);
    });
  }

  function togglePlayback() {
    const video = activeVideo();
    if (!video) {
      setAutoplayRequest((current) => current + 1);
      return;
    }
    if (video.paused || video.ended) {
      playActiveVideo();
      return;
    }
    video.pause();
  }

  function jumpToPlaybackShot(targetShot: BrowseShot | null) {
    if (!targetShot || targetShot.key === playbackShot.key) {
      return;
    }
    const shouldResume = isPlaying || activeVideo()?.paused === false;
    internalPlaybackShotKeyRef.current = targetShot.key;
    manualPreviewSeekRef.current = false;
    // outgoing player는 마지막 프레임에 둔다. 여기서 0으로 되감으면 React가
    // standby를 active로 바꾸기 전 그 첫 프레임이 한 번 노출된다.
    activeVideo()?.pause();
    setPlaybackShotKey(targetShot.key);
    setInfoShotKey(targetShot.key);
    setActivePlayerIndex((current) => 1 - current);
    setNextPreloadEnabled(false);
    setPlayerTime(0);
    setPlayerDuration(Math.max(0.01, targetShot.durMs / 1000));
    setIsPlaying(false);
    publishLastBrowseShot(targetShot);
    onPlaybackShotChange(targetShot.key);
    if (shouldResume) {
      setAutoplayRequest((current) => current + 1);
    }
  }

  function handlePreviewZoneAction(clientX: number, width: number, left: number) {
    const now = window.performance.now();
    if (now - lastPreviewToggleTsRef.current < 180) {
      return;
    }
    lastPreviewToggleTsRef.current = now;
    const ratio = width > 0 ? (clientX - left) / width : 0.5;
    if (ratio < 0.25) {
      jumpToPlaybackShot(previousShot);
      return;
    }
    if (ratio > 0.75) {
      jumpToPlaybackShot(nextShot);
      return;
    }
    togglePlayback();
  }

  function handlePreviewPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    previewPointerStartRef.current = { x: event.clientX, y: event.clientY };
  }

  function handlePreviewPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    const start = previewPointerStartRef.current;
    previewPointerStartRef.current = null;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    handlePreviewZoneAction(event.clientX, rect.width, rect.left);
  }

  function handlePreviewMouseUp(event: React.MouseEvent<HTMLDivElement>) {
    if (event.button !== 0) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    handlePreviewZoneAction(event.clientX, rect.width, rect.left);
  }

  useEffect(() => {
    const zone = previewZoneRef.current;
    if (!zone) {
      return;
    }
    let touchStart: { x: number; y: number } | null = null;
    const handleMouseUp = (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = zone.getBoundingClientRect();
      handlePreviewZoneAction(event.clientX, rect.width, rect.left);
    };
    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    };
    const handleTouchEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      const start = touchStart;
      touchStart = null;
      if (!touch || !start || Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = zone.getBoundingClientRect();
      handlePreviewZoneAction(touch.clientX, rect.width, rect.left);
    };
    zone.addEventListener("mouseup", handleMouseUp);
    zone.addEventListener("touchstart", handleTouchStart, { passive: true });
    zone.addEventListener("touchend", handleTouchEnd, { passive: false });
    return () => {
      zone.removeEventListener("mouseup", handleMouseUp);
      zone.removeEventListener("touchstart", handleTouchStart);
      zone.removeEventListener("touchend", handleTouchEnd);
    };
  });

  function toggleContinuousPlaybackState() {
    const nextEnabled = !continuousPlayback;
    setContinuousPlayback(nextEnabled);
    if (nextEnabled && activeVideo()?.paused) {
      playActiveVideo();
    }
  }

  function toggleContinuousPlayback(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    toggleContinuousPlaybackState();
  }

  function advancePlaylist() {
    if (!continuousPlayback || !nextShot) {
      setContinuousPlayback(false);
      setIsPlaying(false);
      return;
    }
    const targetPlayerIndex = 1 - activePlayerIndex;
    const targetVideo = idleVideo();
    internalPlaybackShotKeyRef.current = nextShot.key;
    manualPreviewSeekRef.current = false;
    setPlaybackShotKey(nextShot.key);
    setInfoShotKey(nextShot.key);
    publishLastBrowseShot(nextShot);
    onPlaybackShotChange(nextShot.key);
    setNextPreloadEnabled(false);
    if (targetVideo && idleVideoUrl && targetVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      setActivePlayerIndex(targetPlayerIndex);
      applyPlaybackRate();
      try {
        targetVideo.currentTime = previewTimeOriginRef.current;
      } catch {
        // Metadata may not be ready yet.
      }
      void targetVideo.play().catch(() => {
        setAutoplayRequest((current) => current + 1);
      });
      return;
    }
    setAutoplayRequest((current) => current + 1);
  }

  function syncInfoToPlayback() {
    setInfoShotKey(playbackShot.key);
  }

  function handleVideoTimeUpdate(index: number, video: HTMLVideoElement) {
    if (index !== activePlayerIndex) {
      return;
    }
    syncPlayerClock(video);
  }

  function handleVideoDurationChange(index: number, video: HTMLVideoElement) {
    if (index !== activePlayerIndex) {
      return;
    }
    syncPlayerClock(video);
  }

  function handleVideoLoadedMetadata(index: number, video: HTMLVideoElement) {
    if (index !== activePlayerIndex) {
      return;
    }
    if (video.paused && !video.ended && !manualPreviewSeekRef.current) {
      try {
        video.currentTime = previewTimeOriginRef.current;
      } catch {
        // Metadata has only just arrived; Safari can still reject an immediate seek.
      }
    }
    syncPlayerClock(video);
  }

  function handleVideoPlaying(index: number, video: HTMLVideoElement) {
    if (index !== activePlayerIndex) {
      return;
    }
    applyPlaybackRate();
    syncPlayerClock(video);
    setIsPlaying(true);
    syncInfoToPlayback();
  }

  function handleVideoPause(index: number) {
    if (index === activePlayerIndex) {
      setIsPlaying(false);
    }
  }

  function handleVideoEnded(index: number) {
    if (index !== activePlayerIndex) {
      return;
    }
    if (continuousPlayback && nextShot) {
      advancePlaylist();
      return;
    }
    const video = videoForSlot(index);
    const duration = previewTimeOriginRef.current > 0
      ? previewDurationRef.current
      : video && Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : progressDuration;
    manualPreviewSeekRef.current = true;
    setIsPlaying(false);
    setContinuousPlayback(false);
    setPlayerDuration(duration);
    setPlayerTime(duration);
  }

  function seekActiveVideo(time: number) {
    const video = activeVideo();
    if (!video || !Number.isFinite(time)) {
      return;
    }
    const target = clamp(time, 0, progressDuration);
    try {
      video.currentTime = toVideoTime(target);
      manualPreviewSeekRef.current = true;
    } catch {
      // Metadata may not be ready yet.
    }
    setPlayerTime(target);
    setPlayerDuration(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : progressDuration);
  }

  function clearProgressDragListeners() {
    progressDragCleanupRef.current?.();
    progressDragCleanupRef.current = null;
    progressScrubRef.current = null;
  }

  function beginProgressScrub(clientX: number) {
    const progress = progressBarRef.current;
    if (!progress) {
      return;
    }
    const video = activeVideo();
    const rect = progress.getBoundingClientRect();
    const duration = video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : progressDuration;
    const currentTime = video && Number.isFinite(video.currentTime) ? video.currentTime : progressTime;
    scrubWasPlayingRef.current = Boolean(video && !video.paused && !video.ended);
    progressScrubRef.current = {
      startClientX: clientX,
      startTime: clamp(currentTime, 0, duration),
      duration,
      railWidth: Math.max(1, rect.width),
      hasMoved: false
    };
    isScrubbingRef.current = true;
    video?.pause();
  }

  function updateProgressScrub(clientX: number, force = false) {
    const scrub = progressScrubRef.current;
    if (!scrub) {
      return;
    }
    const deltaX = clientX - scrub.startClientX;
    if (Math.abs(deltaX) < 1 && !force) {
      return;
    }
    if (Math.abs(deltaX) >= 1) {
      scrub.hasMoved = true;
    }
    if (!scrub.hasMoved) {
      return;
    }
    const deltaTime = (deltaX / scrub.railWidth) * scrub.duration;
    seekActiveVideo(scrub.startTime + deltaTime);
  }

  function resumeActiveVideoAfterScrub() {
    const video = activeVideo();
    if (!video) {
      setAutoplayRequest((current) => current + 1);
      return;
    }
    applyPlaybackRate();
    if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
      video.load();
    }
    void video.play().catch(() => {
      setIsPlaying(false);
    });
  }

  function finishProgressScrub(clientX: number | null = null) {
    if (clientX !== null) {
      updateProgressScrub(clientX, true);
    }
    const shouldResume = scrubWasPlayingRef.current;
    clearProgressDragListeners();
    scrubWasPlayingRef.current = false;
    isScrubbingRef.current = false;
    if (shouldResume) {
      resumeActiveVideoAfterScrub();
    }
  }

  function startProgressMouseScrub(event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    clearProgressDragListeners();
    beginProgressScrub(event.clientX);
    const handleMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      updateProgressScrub(moveEvent.clientX);
    };
    const handleUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      finishProgressScrub(upEvent.clientX);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp, { capture: true, once: true });
    progressDragCleanupRef.current = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp, true);
    };
  }

  function startProgressTouchScrub(event: TouchEvent) {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    clearProgressDragListeners();
    beginProgressScrub(touch.clientX);
    const touchClientX = (touchEvent: TouchEvent) => {
      const item = touchEvent.touches[0] || touchEvent.changedTouches[0];
      return item ? item.clientX : null;
    };
    const handleMove = (moveEvent: TouchEvent) => {
      const clientX = touchClientX(moveEvent);
      if (clientX === null) {
        return;
      }
      moveEvent.preventDefault();
      updateProgressScrub(clientX);
    };
    const handleEnd = (endEvent: TouchEvent) => {
      const clientX = touchClientX(endEvent);
      endEvent.preventDefault();
      finishProgressScrub(clientX);
    };
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd, { capture: true, passive: false, once: true });
    window.addEventListener("touchcancel", handleEnd, { capture: true, passive: false, once: true });
    progressDragCleanupRef.current = () => {
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd, true);
      window.removeEventListener("touchcancel", handleEnd, true);
    };
  }

  useEffect(() => {
    const progress = progressBarRef.current;
    if (!progress) {
      return;
    }
    const stopProgressBubble = (event: Event) => event.stopPropagation();
    progress.addEventListener("mousedown", startProgressMouseScrub);
    progress.addEventListener("touchstart", startProgressTouchScrub, { passive: false });
    progress.addEventListener("mouseup", stopProgressBubble);
    progress.addEventListener("click", stopProgressBubble);
    progress.addEventListener("touchend", stopProgressBubble);
    progress.addEventListener("touchcancel", stopProgressBubble);
    progress.addEventListener("pointerdown", stopProgressBubble);
    progress.addEventListener("pointerup", stopProgressBubble);
    progress.addEventListener("pointercancel", stopProgressBubble);
    return () => {
      progress.removeEventListener("mousedown", startProgressMouseScrub);
      progress.removeEventListener("touchstart", startProgressTouchScrub);
      progress.removeEventListener("mouseup", stopProgressBubble);
      progress.removeEventListener("click", stopProgressBubble);
      progress.removeEventListener("touchend", stopProgressBubble);
      progress.removeEventListener("touchcancel", stopProgressBubble);
      progress.removeEventListener("pointerdown", stopProgressBubble);
      progress.removeEventListener("pointerup", stopProgressBubble);
      progress.removeEventListener("pointercancel", stopProgressBubble);
    };
  });

  function handleProgressKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const frameStep = infoShot.frameCount > 0 ? progressDuration / infoShot.frameCount : 1 / 30;
    if (event.key === "Home") {
      seekActiveVideo(0);
      return;
    }
    if (event.key === "End") {
      seekActiveVideo(progressDuration);
      return;
    }
    seekActiveVideo(progressTime + (event.key === "ArrowRight" ? frameStep : -frameStep));
  }

  function jumpToFrameEdge(edge: "first" | "last") {
    const video = activeVideo();
    const duration = video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : progressDuration;
    const frameDuration = infoShot.frameCount > 0 ? duration / infoShot.frameCount : 1 / 30;
    seekActiveVideo(edge === "first" ? 0 : Math.max(0, duration - frameDuration));
  }

  function seekToFrameEdge(edge: "first" | "last", event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    jumpToFrameEdge(edge);
  }

  function setPreviewPlaybackRate(next: number, options: { playIfPaused?: boolean } = {}) {
    setPlaybackRate(next);
    applyPlaybackRate(next);
    try {
      localStorage.setItem(PREVIEW_PLAYBACK_RATE_STORAGE_KEY, String(next));
    } catch {
      // Preview speed persistence is optional.
    }
    if (options.playIfPaused) {
      const video = activeVideo();
      if (!video || video.paused || video.ended) {
        playActiveVideo(false, next);
      }
    }
  }

  function togglePlaybackRate(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setPreviewPlaybackRate(playbackRate === 1 ? 2 : 1);
  }

  function depthRangeValueFromClientX(clientX: number) {
    const rail = depthRangeRailRef.current;
    if (!rail) {
      return 0;
    }
    const rect = rail.getBoundingClientRect();
    return clamp(((clientX - rect.left) / Math.max(1, rect.width)) * 100, 0, 100);
  }

  function updateDepthRange(handle: DepthRangeHandle, value: number) {
    const rounded = Math.round(value * 10) / 10;
    setDepthRange((current) => {
      if (handle === "low") {
        return { ...current, low: clamp(rounded, 0, current.high - DEPTH_RANGE_MIN_GAP) };
      }
      return { ...current, high: clamp(rounded, current.low + DEPTH_RANGE_MIN_GAP, 100) };
    });
  }

  function beginDepthRangeDrag(handle: DepthRangeHandle, clientX: number) {
    updateDepthRange(handle, depthRangeValueFromClientX(clientX));
    const handleMove = (event: PointerEvent) => {
      event.preventDefault();
      updateDepthRange(handle, depthRangeValueFromClientX(event.clientX));
    };
    const handleUp = (event: PointerEvent) => {
      event.preventDefault();
      updateDepthRange(handle, depthRangeValueFromClientX(event.clientX));
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  }

  function handleDepthRangeRailPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    const value = depthRangeValueFromClientX(event.clientX);
    const handle = Math.abs(value - depthRange.low) <= Math.abs(value - depthRange.high) ? "low" : "high";
    beginDepthRangeDrag(handle, event.clientX);
  }

  function handleDepthRangeHandlePointerDown(handle: DepthRangeHandle, event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    beginDepthRangeDrag(handle, event.clientX);
  }

  function handleDepthRangeKeyDown(handle: DepthRangeHandle, event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.key === "Home") {
      updateDepthRange(handle, handle === "low" ? 0 : depthRange.low + DEPTH_RANGE_MIN_GAP);
      return;
    }
    if (event.key === "End") {
      updateDepthRange(handle, handle === "low" ? depthRange.high - DEPTH_RANGE_MIN_GAP : 100);
      return;
    }
    const step = event.shiftKey ? 5 : 1;
    updateDepthRange(handle, depthRange[handle] + (event.key === "ArrowRight" ? step : -step));
  }

  function switchPreviewSource(source: PreviewSource, event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (source === previewSource) {
      return;
    }
    const shouldResume = isPlaying || activeVideo()?.paused === false;
    manualPreviewSeekRef.current = false;
    clearProgressDragListeners();
    for (const item of [videoARef.current, videoBRef.current]) {
      item?.pause();
      if (item) {
        try {
          item.currentTime = previewTimeOriginRef.current;
        } catch {
          // Metadata may not be ready yet.
        }
      }
    }
    setPreviewSource(source);
    localStorage.setItem(PREVIEW_SOURCE_STORAGE_KEY,source);
    setNextPreloadEnabled(false);
    setActivePlayerIndex((current) => 1 - current);
    setIsPlaying(false);
    setPlayerTime(0);
    setPlayerDuration(Math.max(0.01, playbackShot.durMs / 1000));
    if (shouldResume) {
      setAutoplayRequest((current) => current + 1);
    }
  }

  function previewExportTime() {
    const video = activeVideo();
    const current = video && Number.isFinite(video.currentTime) ? video.currentTime : progressTime;
    return clamp(current, 0, progressDuration);
  }

  function previewExportSource(): PreviewSource {
    if (activePreviewMedia.own) {
      return effectivePreviewSource;
    }
    return playbackShot.verticalVideoUrl ? "vertical-lite" : "lite";
  }

  function previewVideoDownloadUrl() {
    return `/api/shot-video-download/${playbackShot.shotId}?source=${previewExportSource()}`;
  }

  function previewFrameUrl() {
    return `/api/shot-frame/${playbackShot.shotId}?source=${previewExportSource()}&t=${previewExportTime().toFixed(3)}`;
  }

  function triggerDownload(url: string, filename: string) {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  // original 컷은 서버에서 ffmpeg로 자르는 동안 시간이 걸린다. 그 사이 다시 눌러
  // ffmpeg이 중복 실행되지 않도록 준비가 끝날 때까지 버튼을 잠근다.
  async function downloadPreviewVideo(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (videoDownloadBusy) {
      return;
    }
    const sourceSuffix = previewExportSource() === "vertical-lite" ? "liteV" : previewExportSource();
    const filename = `shot-${playbackShot.shotId}-${sourceSuffix}.mp4`;
    setVideoDownloadBusy(true);
    try {
      const response = await fetch(previewVideoDownloadUrl(), { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`video download failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "video download failed");
    } finally {
      setVideoDownloadBusy(false);
    }
  }

  // 프레임 저장은 탭과 무관하게 항상 원본 영상에서 뽑는다. frame_idx는 이 shot 안에서의 인덱스.
  function currentShotFrameTarget() {
    const seconds = previewExportTime();
    const fps = video && video.fpsNum > 0 && video.fpsDen > 0 ? video.fpsNum / video.fpsDen : 0;
    let frameIdx = fps > 0 ? Math.round(seconds * fps) : -1;
    if (playbackShot.frameCount > 0 && frameIdx > playbackShot.frameCount - 1) {
      frameIdx = playbackShot.frameCount - 1;
    }
    if (frameIdx < 0) {
      // fps를 모르면 인덱스를 못 구하니 시간 기반으로라도 원본에서 뽑는다.
      return {
        url: `/api/shot-frame/${playbackShot.shotId}?source=original&t=${seconds.toFixed(3)}`,
        filename: `shot-${playbackShot.shotId}-${Math.round(seconds * 1000)}ms.png`
      };
    }
    return {
      url: `/api/shot-frame/${playbackShot.shotId}?source=original&frame=${frameIdx}`,
      filename: `shot-${playbackShot.shotId}-${frameIdx}.png`
    };
  }

  function saveCurrentFramePng(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    const target = currentShotFrameTarget();
    triggerDownload(target.url, target.filename);
  }

  function downloadCurrentFrame(event?: React.MouseEvent<HTMLButtonElement>) {
    event?.stopPropagation();
    const target = currentShotFrameTarget();
    triggerDownload(target.url, target.filename);
  }

  async function copyCurrentFrame(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
      downloadCurrentFrame();
      return;
    }
    try {
      const response = await fetch(previewFrameUrl(), { cache: "no-store" });
      if (!response.ok) {
        throw new Error("frame download failed");
      }
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type || "image/png"]: blob })]);
    } catch {
      downloadCurrentFrame();
    }
  }

  return (
    <>
      <div
        className={`previewVideoWrap ${isPlaying ? "playing" : "paused"} ${depthMediaOwn ? "depthMaskActive" : ""} ${previewLandscapeInVertical ? "landscapeInVertical" : ""}`}
        onMouseUp={handlePreviewMouseUp}
        onPointerDown={handlePreviewPointerDown}
        onPointerUp={handlePreviewPointerUp}
        style={{ aspectRatio: `${videoWidth} / ${videoHeight}` }}
      >
        <div
          className="previewLeftControls"
          onMouseUp={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <button
            className="previewSpeedToggle"
            onClick={togglePlaybackRate}
            title="Toggle preview speed; keys 1, 2, 3 force speed"
            type="button"
          >
            {playbackRate}x
          </button>
          <button
            aria-label="Switch subtitle language"
            className="previewLanguageToggle"
            disabled={!subtitleLanguageSwitchEnabled}
            onClick={toggleSubtitleLanguage}
            title={subtitleLanguageSwitchEnabled ? "Switch subtitle language (L)" : "No alternate subtitle language"}
            type="button"
          >
            {subtitleLanguageLabel}
          </button>
        </div>
        <div
          className="previewSourceControls"
          onMouseUp={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <div className="previewSourceSwitch" aria-label="Preview source">
            <button
              className={previewSource === "original" ? "active" : ""}
              disabled={!originalSourceUrlForShot && previewSource !== "original"}
              onClick={(event) => switchPreviewSource("original", event)}
              title={originalSourceUrlForShot ? "Play the same range straight from the original source file" : "Original source file is not reachable"}
              type="button"
            >
              original
            </button>
            <button
              className={previewSource === "lite" ? "active" : ""}
              onClick={(event) => switchPreviewSource("lite", event)}
              type="button"
            >
              lite
            </button>
            {!sourceIsVertical ? <button
              className={`previewSourceVerticalLite ${previewSource === "vertical-lite" ? "active" : ""}`}
              onClick={(event) => switchPreviewSource("vertical-lite", event)}
              title={playbackShot.hasVert ? "Show 9:16 outpainted shot" : "No 9:16 yet — plays lite framed as the outpaint would (center at 25% height)"}
              type="button"
            >
              V-lite
            </button>
            : null}
            {/* 9:16 즉시 처리(⚡)는 탭 스트립이 아니라 V-lite 화면 안의 Outpaint 9:16
                버튼(previewOutpaintButton) 하나로만 노출한다 — 동작 버튼을 탭 자리에
                두면 탭으로 오인된다(2026-09-01 지적). */}
            <button
              className={previewSource === "doodle" ? "active" : ""}
              onClick={(event) => switchPreviewSource("doodle", event)}
              title={playbackShot.doodleVideoUrl ? "Show doodle ghost map" : "Doodle ghost map not ready"}
              type="button"
            >
              doodle
            </button>
            <button
              className={previewSource === "pose" ? "active" : ""}
              onClick={(event) => switchPreviewSource("pose", event)}
              title={playbackShot.poseVideoUrl ? "Show pose motion video" : "Pose not ready — falls back to V-lite, then lite"}
              type="button"
            >
              pose
            </button>
            <button
              className={previewSource === "depth" ? "active" : ""}
              disabled={!playbackShot.depthVideoUrl && previewSource !== "depth"}
              onClick={(event) => switchPreviewSource("depth", event)}
              title={playbackShot.depthVideoUrl ? "Show depth video" : "Depth video is not ready"}
              type="button"
            >
              depth
            </button>
            <button
              className={previewSource === "edge" ? "active" : ""}
              disabled={!playbackShot.edgeVideoUrl && previewSource !== "edge"}
              onClick={(event) => switchPreviewSource("edge", event)}
              title={playbackShot.edgeVideoUrl ? "Show edge video" : "Edge video is not ready"}
              type="button"
            >
              edge
            </button>
          </div>
          <div className="previewSourceQuickActions" aria-label="Preview exports">
            <button
              aria-label="Save current frame as PNG"
              className="previewSourceIconButton"
              onClick={saveCurrentFramePng}
              title="Save current frame as PNG — original tab extracts from the original file at full quality"
              type="button"
            >
              <ImageIcon size={14} />
            </button>
            <button
              aria-label="Download preview video"
              className="previewSourceIconButton"
              disabled={videoDownloadBusy}
              onClick={(event) => void downloadPreviewVideo(event)}
              title={videoDownloadBusy ? "Preparing the video cut…" : "Download video"}
              type="button"
            >
              {videoDownloadBusy ? <LoaderCircle className="spinIcon" size={14} /> : <Download size={14} />}
            </button>
            <button
              aria-label="Copy this frame to clipboard"
              className="previewSourceIconButton"
              onClick={(event) => void copyCurrentFrame(event)}
              title="Copy this frame to clipboard; downloads PNG if clipboard is blocked"
              type="button"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
        <div
          className="previewTopRightControls"
          onMouseUp={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <button
            className={`continuousModeButton ${continuousPlayback ? "active" : ""}`}
            onClick={toggleContinuousPlayback}
            title={continuousPlayback ? "Disable automatic next-shot playback" : "Enable automatic next-shot playback"}
            type="button"
          >
            <Repeat2 size={14} />
            <span>Auto</span>
          </button>
        </div>
        {[0, 1].map((index) => {
          const slotSource = videoSlotSource(index);
          return (
            <video
              className={`previewVideo ${index === activePlayerIndex ? "active" : "standby"}`}
              height={videoHeight}
              key={index}
              onDurationChange={(event) => handleVideoDurationChange(index, event.currentTarget)}
              onEmptied={() => {
                if (index === activePlayerIndex) {
                  setIsPlaying(false);
                  setPlayerTime(0);
                }
              }}
              onEnded={() => handleVideoEnded(index)}
              onLoadedMetadata={(event) => handleVideoLoadedMetadata(index, event.currentTarget)}
              onPause={() => handleVideoPause(index)}
              onPlaying={(event) => handleVideoPlaying(index, event.currentTarget)}
              onTimeUpdate={(event) => handleVideoTimeUpdate(index, event.currentTarget)}
              controls={false}
              crossOrigin="anonymous"
              playsInline
              preload={index === activePlayerIndex || slotSource ? "auto" : "none"}
              ref={index === 0 ? videoARef : videoBRef}
              src={slotSource || undefined}
              width={videoWidth}
              {...{ "webkit-playsinline": "true" }}
            />
          );
        })}
        {depthMediaOwn ? (
          <canvas
            aria-hidden="true"
            className="previewDepthCanvas"
            ref={depthCanvasRef}
          />
        ) : null}
        {effectivePreviewSource === "vertical-lite" && !activePreviewMedia.own && !playbackShot.hasVert && !sourceIsVertical ? (
          <button
            className="previewOutpaintButton"
            disabled={h2vPrioritizeBusy}
            onClick={() => void prioritizeCurrentShotVertical()}
            type="button"
          >
            {h2vPrioritizeBusy ? <LoaderCircle className="spinIcon" size={12} /> : <Zap size={12} />}
            <span>{h2vPrioritizeBusy ? "Queuing…" : "Outpaint 9:16"}</span>
          </button>
        ) : null}
        {effectivePreviewSource === "doodle" && !activePreviewMedia.own && (doodlePrimitivesMissing || !playbackShot?.doodleVideoUrl) ? (
          <div className="previewDoodleEmpty">
            {doodleRequestState === "idle" || doodleRequestState === "error" ? (
              <>
                {doodleRequestState === "error" ? <span className="previewDoodleError">{doodleRequestMessage}</span> : null}
                {doodlePrimitivesMissing && playbackShot?.doodleVideoUrl ? (
                  <span className="previewDoodleHint">벡터 사이드카 없음 — 일괄 재생성 대기 중</span>
                ) : null}
                <button
                  className="previewDoodleButton"
                  onClick={() => void makeDoodleForCurrentShot()}
                  type="button"
                >
                  Make Doodle
                </button>
                {!playbackShot.verticalVideoUrl && !sourceIsVertical ? (
                  <span className="previewDoodleHint">9:16 outpaint will be queued first via H2V</span>
                ) : null}
              </>
            ) : (
              <div className="previewDoodleWaiting">
                <LoaderCircle className="spinIcon" size={16} />
                <span>{doodleRequestMessage || "Preparing doodle…"}</span>
              </div>
            )}
          </div>
        ) : null}
        {/* 사이드카(프리미티브 JSON)만 없고 depth 영상은 있는 샷 — 영상은 그대로 보여주고
            선이 없는 이유만 알린다(2026-09-01: depth URL을 fetch와 분리하면서 상태 추가). */}
        {effectivePreviewSource === "doodle" && activePreviewMedia.own && doodlePrimitivesMissing ? (
          <div className="previewDoodleSidecarBadge">벡터 사이드카 없음 — 일괄 재생성 대기</div>
        ) : null}
        {effectivePreviewSource === "pose" && activePreviewMedia.own && playbackShot.videoUrl ? (
          <video
            aria-hidden="true"
            className={`previewPoseGhost ${playbackShot.verticalVideoUrl ? "" : "landscape"}`}
            crossOrigin="anonymous"
            playsInline
            preload="auto"
            ref={poseGhostVideoRef}
            src={playbackShot.verticalVideoUrl || playbackShot.videoUrl}
            {...{ "webkit-playsinline": "true" }}
          />
        ) : null}
        {effectivePreviewSource === "doodle" && doodleVectorData ? (
          <DoodleVectorOverlay
            canvasRef={doodleVectorCanvasRef}
            primitives={doodleVectorData.primitives}
            videoEl={activePlayerIndex === 0 ? videoARef.current : videoBRef.current}
          />
        ) : null}
        {activeSubtitleLines.length > 0 ? (
          <div className="previewSubtitleOverlay" aria-live="off">
            <div className="previewSubtitleBubble">
              {activeSubtitleLines.map((line) => (
                <span key={line.id}>{subtitlePlainText(line.displayText)}</span>
              ))}
            </div>
          </div>
        ) : null}
        {depthMediaOwn ? (
          <div
            className="previewDepthRangeControl"
            onClick={(event) => event.stopPropagation()}
            onMouseUp={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            onTouchEnd={(event) => event.stopPropagation()}
          >
            <div
              aria-label="Depth intensity range"
              className="previewDepthRangeRail"
              onPointerDown={handleDepthRangeRailPointerDown}
              ref={depthRangeRailRef}
              role="group"
              style={depthRangeStyle}
            >
              <button
                aria-label="Depth low cutoff"
                aria-valuemax={depthRange.high - DEPTH_RANGE_MIN_GAP}
                aria-valuemin={0}
                aria-valuenow={depthRange.low}
                className="previewDepthRangeHandle low"
                onKeyDown={(event) => handleDepthRangeKeyDown("low", event)}
                onPointerDown={(event) => handleDepthRangeHandlePointerDown("low", event)}
                type="button"
              />
              <button
                aria-label="Depth high cutoff"
                aria-valuemax={100}
                aria-valuemin={depthRange.low + DEPTH_RANGE_MIN_GAP}
                aria-valuenow={depthRange.high}
                className="previewDepthRangeHandle high"
                onKeyDown={(event) => handleDepthRangeKeyDown("high", event)}
                onPointerDown={(event) => handleDepthRangeHandlePointerDown("high", event)}
                type="button"
              />
            </div>
          </div>
        ) : null}
        <div
          aria-hidden="true"
          className="previewZoneLayer"
          ref={previewZoneRef}
        />
        <button
          aria-label="Jump to first frame"
          className="previewFrameEdgeButton first"
          onClick={(event) => seekToFrameEdge("first", event)}
          onMouseUp={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          title="Jump to first frame"
          type="button"
        >
          <SkipBack size={16} />
        </button>
        <button
          aria-label="Jump to last frame"
          className="previewFrameEdgeButton last"
          onClick={(event) => seekToFrameEdge("last", event)}
          onMouseUp={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          title="Jump to last frame"
          type="button"
        >
          <SkipForward size={16} />
        </button>
        <div
          aria-label="Preview progress"
          aria-valuemax={progressDuration}
          aria-valuemin={0}
          aria-valuenow={progressTime}
          className="previewProgressBar"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={handleProgressKeyDown}
          onMouseUp={(event) => event.stopPropagation()}
          onTouchEnd={(event) => event.stopPropagation()}
          role="slider"
          ref={progressBarRef}
          style={progressStyle}
          tabIndex={0}
        />
      </div>
      <div className="previewInfo">
        <div className="previewCompactMeta">
          <div className="previewFilePath">{sourceDir}</div>
          <div className="previewFileName">{sourceFile} ({resolutionLabel}, video {infoShot.videoId})</div>
          <div className="previewShotLine">
            shot{" "}
            <span
              className="previewShotIdCopy"
              onClick={() => void copyTextToClipboard(String(infoShot.shotId))}
              title="Copy shot id"
            >{infoShot.shotId}</span>{shotTimeLabel.slice(`shot ${infoShot.shotId}`.length)}
          </div>
        </div>
        <section className={`shotSplitHelpReview ${splitHelpState}`}>
          <div className="shotSplitHelpHeader">
            <strong>Split help</strong>
            <span>{shotSplitHelpStateLabel(splitHelpState)}</span>
          </div>
          <div className="shotSplitHelpControls">
            <label>
              <input
                checked={(splitHelpIssueMask & SPLIT_ISSUE_UNDER) !== 0}
                disabled={splitHelpPending}
                onChange={(event) => toggleSplitHelpIssue(SPLIT_ISSUE_UNDER, event.target.checked)}
                type="checkbox"
              />
              <span>split</span>
            </label>
            <label>
              <input
                checked={(splitHelpIssueMask & SPLIT_ISSUE_MERGE_LEFT) !== 0}
                disabled={splitHelpPending}
                onChange={(event) => toggleSplitHelpIssue(SPLIT_ISSUE_MERGE_LEFT, event.target.checked)}
                type="checkbox"
              />
              <span>merge left</span>
            </label>
            <label>
              <input
                checked={(splitHelpIssueMask & SPLIT_ISSUE_MERGE_RIGHT) !== 0}
                disabled={splitHelpPending}
                onChange={(event) => toggleSplitHelpIssue(SPLIT_ISSUE_MERGE_RIGHT, event.target.checked)}
                type="checkbox"
              />
              <span>merge right</span>
            </label>
            <label>
              <input
                checked={splitHelpIssueMask === 0 && splitHelpState === "resolved"}
                disabled={splitHelpPending}
                onChange={(event) => {
                  if (event.target.checked) {
                    void applySplitHelpIssueMask(0);
                  }
                }}
                type="checkbox"
              />
              <span>good</span>
            </label>
            <label>
              <input
                checked={splitHelpState === "ignored"}
                disabled={splitHelpPending}
                onChange={(event) => void toggleSplitHelpIgnored(event.target.checked)}
                type="checkbox"
              />
              <span>ignore</span>
            </label>
            {splitHelpPending ? <span className="shotSplitHelpSaving">saving</span> : null}
          </div>
        </section>
        {(infoShot.motionCaptionEn || infoShot.motionSearchTextEn || infoShot.motionKeywordsEn || infoShot.motionPacketPreview || infoShot.motionEmbeddingModel) ? (
          <section className="shotMotionSummary">
            <div className="shotMotionSummaryHeader">
              <h3>Motion</h3>
              <button
                className={`motionAnalysisState ${infoShot.motionState} motionAnalysisStateButton`}
                disabled={motionReprocessPending}
                onClick={() => onReprocessShotMotion(infoShot)}
                title="Reprocess this shot's full motion packet"
                type="button"
              >
                {motionReprocessPending ? "Queueing" : motionStateLabel(infoShot.motionState)}
              </button>
            </div>
            {/* 본문은 아래 Motion 섹션(v8 motion layer)이 담당한다. 여기 있던 legacy
                caption/keywords/packet preview는 같은 내용을 중복 노출해서 걷어냈다. */}
          </section>
        ) : null}
        {infoSubtitleLines.length > 0 ? (
          <div className="selectedSubtitles">
            <button
              aria-label="Copy subtitles"
              className="selectedSubtitlesCopy"
              onClick={() => void copySelectedSubtitles()}
              title={subtitlesCopied ? "Copied" : "Copy subtitles"}
              type="button"
            >
              {subtitlesCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <ol>
              {infoSubtitleLines.map((line) => (
                <li key={line.id}>{line.displayText}</li>
              ))}
            </ol>
          </div>
        ) : null}
        <div className="previewActions">
          <div className="previewButtonGroup" aria-label="Scene start">
            <button
              className="sceneStartButton"
              disabled={sceneStartPendingKey === infoShot.key || infoShot.sceneStart > 0}
              onClick={() => onSetSceneStart(infoShot, true)}
              type="button"
            >
              {infoShot.sceneStart > 0 ? `Scene ${infoShot.sceneStart}` : "set as scene start"}
            </button>
            <button
              className="sceneStartButton"
              disabled={sceneStartPendingKey === infoShot.key || infoShot.sceneStart <= 0}
              onClick={() => onSetSceneStart(infoShot, false)}
              type="button"
            >
              unset
            </button>
          </div>
          <button
            className="dangerActionButton"
            disabled={deletePending}
            onClick={() => onDeleteShot(infoShot)}
            type="button"
          >
            {deletePending ? deletePendingLabel : deleteVerb}
          </button>
          <button
            className="sceneStartButton"
            disabled={splitState !== null}
            onClick={() => onSplitShot(infoShot)}
            title="Mark this shot for split and queue the unified video rejudge"
            type="button"
          >
            {splitState === "running" ? "rejudging" : splitState ? "queued" : "mark split + rejudge"}
          </button>
        </div>
        {isVerticalShow ? (
          <div className="previewRangeActions">
            <div className="previewButtonGroup" aria-label="Episode start">
              <button
                className="sceneStartButton"
                disabled={episodeStartPending || episodeStart !== null}
                onClick={() => onSetEpisodeStart(infoShot, "set")}
                type="button"
              >
                {episodeStartPending ? "setting episode" : "set as episode start"}
              </button>
              <button
                className="sceneStartButton"
                disabled={episodeStartPending || !canUnsetEpisodeStart}
                onClick={() => onSetEpisodeStart(infoShot, "unset")}
                type="button"
              >
                {episodeStartPending ? "unsetting" : "unset"}
              </button>
            </div>
          </div>
        ) : (
          <div className="previewRangeActions">
            <button
              className="sceneStartButton"
              disabled={rangeMarkBusy}
              onClick={() => onMarkIntroEnd(infoShot)}
              type="button"
            >
              {introEndPending ? "marking intro" : "set as intro end"}
            </button>
            <button
              className="sceneStartButton"
              disabled={rangeMarkBusy}
              onClick={() => onMarkOutroStart(infoShot)}
              type="button"
            >
              {outroStartPending ? "marking outro" : "set as outro start"}
            </button>
          </div>
        )}
        {detectedVisual?.packet || detectedVisual?.textEn ? (
          <VisualDetectedAttributes visual={detectedVisual} />
        ) : null}
        {detectedMotionLoading ? (
          <section className="motionDetectedAttributes motionDetectedStatus">검출 속성을 불러오는 중…</section>
        ) : detectedMotionError ? (
          <section className="motionDetectedAttributes motionDetectedStatus error">검출 속성 로드 실패: {detectedMotionError}</section>
        ) : detectedMotion ? (
          <MotionDetectedAttributes detected={detectedMotion} />
        ) : (
          <section className="motionDetectedAttributes motionDetectedStatus">
            이 shot에 저장된 검출 속성이 없습니다.
            {priorityIndexState === "idle" ? (
              <button
                className="priorityIndexButton"
                onClick={() => void requestPriorityIndex()}
                type="button"
              >
                index now
              </button>
            ) : priorityIndexState === "error" ? (
              <>
                <span className="priorityIndexError">{priorityIndexMessage}</span>
                <button
                  className="priorityIndexButton"
                  onClick={() => void requestPriorityIndex()}
                  type="button"
                >
                  retry
                </button>
              </>
            ) : (
              <span className="priorityIndexWaiting">
                {priorityIndexState === "busy" ? "requesting…" : <><LoaderCircle className="spinIcon" size={12} /> indexing… 결과가 도착하면 이 자리에 표시됩니다</>}
              </span>
            )}
          </section>
        )}
      </div>
    </>
  );
}
